import React, {
  useState,
  useRef,
  useEffect,
  ReactElement,
  useCallback,
  useMemo
} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Import useParams
import {
  Box,
  Typography,
  Grid, // Keep existing Grid import
  Button,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Fab,
  Badge,
  Popover,
  Drawer,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControlLabel,
  Switch,
  Select,
  Skeleton
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Pause as PauseIcon,
  Download as DownloadIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  Share as ShareIcon,
  ShareOutlined as ShareOutlinedIcon,
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Mic as MicIcon,
  MicOutlined as MicOutlinedIcon,
  Add as AddIcon,
  AddOutlined as AddOutlinedIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  Edit as EditIcon,
  EditOutlined as EditOutlinedIcon,
  CameraAltOutlined as CameraIcon,
  AttachFile as AttachFileIcon,
  AudioFile as AudioFileIcon,
  AudioFileOutlined as AudioFileOutlinedIcon,
  Description as DescriptionIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  Summarize as SummarizeIcon,
  SummarizeOutlined as SummarizeOutlinedIcon,
  PictureAsPdf as PictureAsPdfIcon,
  PictureAsPdfOutlined as PictureAsPdfOutlinedIcon,
  TextSnippetOutlined as TextSnippetOutlinedIcon,
  AudiotrackOutlined as AudiotrackOutlinedIcon,
  CheckCircle as CheckCircleApprovalIcon,
  Cancel as RejectIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Bolt as BoltIcon,
  VisibilityOutlined as ViewOnlyIcon,
  Delete as DeleteIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  DriveFileMove as MoveIcon,
  DriveFileMoveOutlined as MoveOutlinedIcon
} from '@mui/icons-material';
import { 
  FileText, 
  Folders, 
  FilePdf, 
  FileAudio,
  Article,
  Waveform,
  ClipboardText,
  Users,
  NotePencil,
  WarningCircle,
  Lightning
} from '@phosphor-icons/react';
import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { RecordingFAB } from '@/components/RecordingFAB';
import { PresenceIndicator } from '@/components/PresenceIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  getTranscriptionSessionById,
  getSessionInsights,
  deleteSessionInsight
} from '@/services/transcriptionSessionService';
// import { performAiAnalysis } from '@/services/transcriptionSessionService';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { useToast } from '@/components/Toast';
import * as templateService from '@/services/templateService';
import {
  UnifiedTabs,
  UnifiedTabsContainer,
  type TabConfig
} from '@/components/UnifiedTabs/UnifiedTabs';
import {
  getAvailableDistributionGroups,
  shareSummaries
} from '@/services/shareService';
import { Question } from '@/types/questionTypes';
import {
  TranscriptionSession,
  TranscriptionSessionStatus,
  RecordingStatePayload,
  LiveSegmentEventPayload,
  TranscriptionLiveEvent,
  RecordingDividerPayload,
  RecordingStoppedPayload,
  SpeakerSegmentResult
} from '@/types/transcriptionSessionTypes';
import type { UpdateTranscriptionSessionClientInput } from '@/types/transcriptionSessionTypes';
import {
  postSpeakerSegment,
  updateTranscriptionSession,
  createTranscriptionSession,
  startRecording as apiStartRecording,
  stopRecording as apiStopRecording,
  getSegmentsDelta,
  deleteTranscriptionSession
} from '@/services/transcriptionSessionService';
import {
  getLiveAccessToken,
  postRecordingState,
  subscribeToLiveStream
} from '@/services/transcriptionLiveService';
import {
  useContentResponsive,
  useContentSpace
} from '@/contexts/ContentSpaceContext';
import {
  extractSessionIdFromUrl,
  createSessionAndNavigate
} from '@/services/sessionNavigationService';
import { SpeakerIdentityDialog } from '@/components/SpeakerManagement';
import { createSpeakerManagementService } from '@/services/speakerManagementService';
import { Client } from '@langchain/langgraph-sdk';
// Thread id is provided by preloaded sessionData.userThreadId
import { createPersonService } from '@/services/personService';
import { Person } from '@/types/peopleTypes';
import {
  SessionSpeakerMapping,
  SpeakerAssignment
} from '@/types/speakerManagementTypes';
import { SummariesTab } from '@/components/NewTranscriptPageComponents/V4/SummariesTab';
import {
  CriticalItemsPanel,
  type CriticalItem
} from '@/components/NewTranscriptPageComponents/V4/CriticalItemsPanel';
import {
  EndSessionChecklistDialog,
  type ChecklistState
} from '@/components/NewTranscriptPageComponents/V4/EndSessionChecklistDialog';
import { getSessionSummaries } from '@/services/transcriptionSessionService';
import type { GeneratedSummary } from '@/types/transcriptionSessionTypes';
import { ChatInterface } from '@/components/NewTranscriptPageComponents/V4/ChatInterface';
import { SessionChatInterface } from '@/components/NewTranscriptPageComponents/V4/SessionChatInterface';
import { MinimalTranscriptView } from '@/components/NewTranscriptPageComponents/V4/MinimalTranscriptView';
import { NotesInterface } from '@/components/NewTranscriptPageComponents/V4/NotesInterface';
import {
  getSessionJobs,
  subscribeToSessionJobs,
  TranscriptionSessionJob,
  SessionJobUpdate,
  getJobTypeName,
  getJobStatusName
} from '@/services/transcriptionSessionJobService';
import {
  uploadAudioDocument,
  generateAndDownloadFormattedTranscript,
  getSessionDocuments
} from '@/services/transcriptionDocumentService';
import { TranscriptionDocumentType } from '@/types/transcriptionDocumentTypes';
import {
  uploadAudioChunk,
  finalizeAudioChunks,
  cancelAudioChunks,
  generateBlockId
} from '@/services/audioChunkUploadService';
import { finalizeTranscriptionSession as apiFinalize } from '@/services/transcriptionSessionService';
import { TranscriptionDocumentTab } from '@/components/TranscriptionDocuments/TranscriptionDocumentTab';
import { getAllTemplates } from '@/services/templateService';
import { Template } from '@/types/templateTypes';
import {
  fetchCurrentThreadId,
  updateCurrentThreadId,
  getCachedThreadId,
  cacheThreadId,
  ThreadType
} from '@/services/threadService';
import { useMobileRecording } from '@/contexts/MobileRecordingContext';
import { createAttachmentUploadHandlers } from './NewTranscriptPageV4/utils/attachmentUpload';
import {
  formatTime,
  getSessionStatus
} from './NewTranscriptPageV4/utils/sessionUtils';
import { EditableSessionTitle } from '@/components/NewTranscriptPageComponents/V4/EditableSessionTitle';
import { SessionCollectionTagger } from '@/components/SessionCollectionTagger';
import { MobileCollectionDrawer } from '@/components/MobileCollectionDrawer';
import {
  getCollectionsByCompany,
  addSessionToCollection,
  removeSessionFromCollection
} from '@/services/sessionCollectionService';
import { SessionCollection } from '@/types/sessionCollectionTypes';
// AssemblyAITranscribe kept in codebase as reference, not used on this page

// LangGraph client helper
const createLangGraphClient = (apiUrl: string, token: string, userId: string) =>
  new Client({
    apiUrl,
    defaultHeaders: {
      Authorization: `Bearer ${token}`,
      'X-User-Id': userId
    }
  });

// Types (assuming these are unchanged from your original code)
interface TranscriptionState {
  isRecording: boolean;
  isPaused: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  connectionStatus: string;
  debugMessages: string[];
  audioLevel: number;
}

interface SpeakerSegment {
  id: string;
  speaker: number;
  text: string;
  timestamp: string;
  confidence: number;
  duration: number;
  isPauseSegment?: boolean;
}

// Special transcript row to indicate a new recording started
interface RecordingDividerSegment {
  id: string;
  kind: 'recording-divider';
  startTimeUtc: string;
}

type TranscriptRow = SpeakerSegment | RecordingDividerSegment;

interface TemplateQuestion {
  id: string;
  question: string;
  answer: string;
  confidence: number;
  isComplete: boolean;
  category: string;
  required?: boolean;
}

// SessionMetrics interface removed - handled by dedicated analytics

type ActiveTabType =
  | 'transcript'
  | 'summaries'
  | 'template'
  | 'documents'
  | 'speakers'
  | 'notes'
  | 'chat'
  | 'critical';

const DEEPGRAM_API_KEY = import.meta.env.VITE_APP_DEEPGRAM_API_KEY;
const TRANSCRIBE_MODE = (import.meta.env.VITE_TRANSCRIBE_MODE || 'batch')
  .toString()
  .toLowerCase();
const BATCH_MODE_ENABLED = TRANSCRIBE_MODE === 'batch';
const REALTIME_MODE_ENABLED = TRANSCRIBE_MODE === 'realtime';
const ENABLE_DEMO_ANALYSIS_TIMER = false;
const ENABLE_DEEPGRAM_PROGRESS = false; // Placeholder: wire to backend when available
// Feature flag: disable automatic speaker identity popup for now
const AUTO_OPEN_SPEAKER_IDENTITY_DIALOG = false;

// Upload handler functions for What's Next buttons - will be defined inside component
let handleCameraCapture: () => void;
let handleFileAttachment: () => void;

// Speaker colors and transcript content are now handled by EnhancedTranscriptViewContent component

// Slide transition for mobile chat modal
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const NewTranscriptPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Use mobile-style FAB for recording controls on narrower screens (including tablet portrait)
  const useMobileRecordingControls = useMediaQuery(
    theme.breakpoints.down('md')
  ); // < 900px
  const { shouldUseTabs, shouldStackVertically } = useContentResponsive();
  useContentSpace(); // Using for side effects

  // Component mounted (debug logging removed to prevent console spam)

  // Using Deepgram for realtime transcription (logging removed)

  // Use content-aware layout switching instead of just viewport breakpoints
  // This accounts for the left menu size to determine actual available space
  const shouldUseMobileLayout = shouldStackVertically; // Content width < 900px
  const shouldUseFabToggle =
    isMobile || useMobileRecordingControls || shouldUseMobileLayout;
  // Show FAB controls whenever either viewport is narrow or content stacks vertically, and user toggle allows it
  // This depends on showFabControls declared later; define as a function to avoid TDZ
  const canShowFabControls = () => shouldUseFabToggle && showFabControls;
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser, selectedUserRole } = useAppUser();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { setMobileRecordingMode, setPaused, setRecordingCallbacks } =
    useMobileRecording();

  // Authentication and user state initialized (debug logging removed)

  // Handle both session-based and template-based URL parameters
  const {
    sessionId: urlSessionId,
    templateId,
    templateName: urlTemplateName
  } = useParams<{
    sessionId?: string;
    templateId?: string;
    templateName?: string;
  }>();

  // Determine if we're using the new session-based URL or legacy template-based URL
  const isSessionBasedUrl = extractSessionIdFromUrl(location.pathname) !== null;
  const currentSessionId = isSessionBasedUrl ? urlSessionId ?? null : null;

  // Core transcription state
  const [state, setState] = useState<TranscriptionState>({
    isRecording: false,
    isPaused: false,
    transcript: '',
    interimTranscript: '',
    error: null,
    connectionStatus: 'Disconnected',
    debugMessages: [],
    audioLevel: 0
  });
  const [isSessionOwner, setIsSessionOwner] = useState<boolean>(true);
  const canControlRecording = Boolean(appUser?.id && isSessionOwner);
  const liveSubscriptionRef = useRef<(() => void) | null>(null);
  const lastLiveBroadcastRef = useRef<number>(0);
  const latestRecordingStateRef = useRef<RecordingStatePayload | null>(null);

  // Critical items (Key Items) state
  const [criticalItems, setCriticalItems] = useState<CriticalItem[]>([]);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistState, setChecklistState] = useState<ChecklistState>({
    selfHarm: undefined,
    medications: [],
    allergies: [],
    enemiesGang: undefined
  });
  const [criticalToastOpen, setCriticalToastOpen] = useState(false);
  const [criticalToastMessage, setCriticalToastMessage] = useState<
    string | null
  >(null);

  // Example: API/model callback could push detections here
  const addCriticalDetection = useCallback((item: Omit<CriticalItem, 'id'>) => {
    // Deduplicate similar snippets within a 20s window
    const nowId = `${item.category}-${item.snippet}-${Date.now()}`;
    setCriticalItems((prev) => {
      const exists = prev.some(
        (p) =>
          p.category === item.category &&
          p.snippet.trim() === item.snippet.trim() &&
          p.detectedAgoSeconds <= item.detectedAgoSeconds + 20
      );
      if (exists) return prev;
      // Show toast
      const label =
        item.category === 'self_harm'
          ? 'Possible self-harm risk mentioned'
          : item.category === 'medications'
          ? `Medications detected: ${item.snippet}`
          : item.category === 'allergies'
          ? `Allergy mentioned: ${item.snippet}`
          : item.category === 'enemies_gang'
          ? 'Enemy or gang affiliation mentioned'
          : 'Key item detected';
      setCriticalToastMessage(label);
      setCriticalToastOpen(true);
      return [{ id: nowId, status: 'new', ...item }, ...prev];
    });
  }, []);

  // Real session state for AI processing
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<TranscriptionSession | null>(
    null
  );
  // Prevent duplicate initialization for the same session id
  const initializedSessionIdsRef = useRef<Set<string>>(new Set());
  // Track if we've ever loaded session data to avoid showing skeleton on refreshes
  const hasLoadedSessionDataRef = useRef<boolean>(false);
  const effectiveSessionId = sessionId ?? currentSessionId ?? null;

  // Mark when we've loaded session data at least once, reset on session change
  useEffect(() => {
    if (sessionData) {
      hasLoadedSessionDataRef.current = true;
    }
  }, [sessionData]);

  // Reset hasLoadedSessionDataRef when navigating to a different session
  useEffect(() => {
    hasLoadedSessionDataRef.current = false;
  }, [effectiveSessionId]);

  useEffect(() => {
    if (!appUser?.id) {
      setIsSessionOwner(false);
      return;
    }
    if (!sessionData?.userId) {
      setIsSessionOwner(!effectiveSessionId);
      return;
    }
    setIsSessionOwner(sessionData.userId === appUser.id);
  }, [appUser?.id, sessionData?.userId, effectiveSessionId]);

  // Session collections state
  const [sessionCollections, setSessionCollections] = useState<
    SessionCollection[]
  >([]);
  const [collectionTaggerAnchorEl, setCollectionTaggerAnchorEl] =
    useState<null | HTMLElement>(null);
  const [allAvailableCollections, setAllAvailableCollections] = useState<
    SessionCollection[]
  >([]);
  const [isLoadingAllCollections, setIsLoadingAllCollections] = useState(false);
  const [mobileCollectionDrawerOpen, setMobileCollectionDrawerOpen] =
    useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [moveToCollectionDialogOpen, setMoveToCollectionDialogOpen] =
    useState(false);
  const [availableCollections, setAvailableCollections] = useState<
    SessionCollection[]
  >([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');

  // Sync session collections from sessionData
  useEffect(() => {
    if (sessionData?.collections) {
      const mappedCollections = sessionData.collections.map((c) => ({
        id: c.id,
        title: c.name,
        description: c.description || null,
        companyId: sessionData.companyId,
        createdByUserId: sessionData.userId,
        referenceNumber: c.referenceNumber || null,
        // Placeholders for SessionCollection type requirements
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sessionCount: 1,
        documentCount: 0,
        conversationCount: 0,
        sessions: [],
        conversations: [],
        documents: [],
        aiSummary: null,
        aiSummaryGeneratedAt: null
      })) as unknown as SessionCollection[];
      setSessionCollections(mappedCollections);
    }
  }, [sessionData]);

  // Reload collections callback for SessionCollectionTagger
  const reloadSessionCollections = useCallback(async () => {
    if (!effectiveSessionId || !appUser?.id) return;

    try {
      // Refresh the entire session to get updated collections
      const refreshed = await getTranscriptionSessionById(
        effectiveSessionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      if (refreshed) {
        setSessionData(refreshed);
      }
    } catch (error) {
      console.error('Error reloading session collections:', error);
    }
  }, [
    effectiveSessionId,
    appUser?.id,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Load all available collections for the company (for checklist dialog)
  const loadAllCollections = useCallback(async () => {
    if (!selectedUserRole || !appUser) return;

    setIsLoadingAllCollections(true);
    try {
      const collections = await getCollectionsByCompany(
        selectedUserRole.companyId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        1,
        100 // Get up to 100 collections
      );
      setAllAvailableCollections(collections);
    } catch (error) {
      console.error('Error loading all collections:', error);
    } finally {
      setIsLoadingAllCollections(false);
    }
  }, [
    selectedUserRole,
    appUser,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Load collections when checklist dialog opens
  useEffect(() => {
    if (checklistOpen && allAvailableCollections.length === 0) {
      loadAllCollections();
    }
  }, [checklistOpen, allAvailableCollections.length, loadAllCollections]);

  // Real-time AI now handled by ChatInterface component

  // Enhanced UI state
  const [speakers, setSpeakers] = useState<TranscriptRow[]>([]);
  const [templateQuestions, setTemplateQuestions] = useState<
    TemplateQuestion[]
  >([]);

  const hydrateTranscriptFromSession = useCallback(
    (snapshot: TranscriptionSession | null) => {
      if (!snapshot) return;

      const hasSegments = snapshot.speakerSegments?.length;
      const hasRecordings = snapshot.recordings?.length;

      if (!hasSegments && !hasRecordings) {
        setSpeakers([]);
        return;
      }

      const timeline: Array<{
        t: number;
        kind: 'segment' | 'divider';
        row: TranscriptRow;
      }> = [];

      if (hasSegments) {
        snapshot.speakerSegments!.forEach((seg) => {
          const isPause = (seg as any).isPauseSegment || false;
          timeline.push({
            t: new Date(seg.timestamp).getTime(),
            kind: 'segment',
            row: {
              id: seg.id,
              speaker: seg.speakerIndex,
              text: seg.textContent,
              timestamp: new Date(seg.timestamp).toISOString(),
              confidence: seg.confidenceScore || 1.0,
              duration: seg.durationSeconds || 0,
              isPauseSegment: isPause
            }
          });
        });
      }

      if (hasRecordings) {
        snapshot.recordings!.forEach((rec) => {
          timeline.push({
            t: new Date(rec.recordingStartTimeUtc).getTime(),
            kind: 'divider',
            row: {
              id: `rec-divider-${rec.id}`,
              kind: 'recording-divider',
              startTimeUtc: rec.recordingStartTimeUtc
            }
          });
        });
      }

      if (!timeline.length) {
        setSpeakers([]);
        return;
      }

      timeline.sort((a, b) =>
        a.t !== b.t
          ? a.t - b.t
          : a.kind === 'divider' && b.kind === 'segment'
          ? -1
          : 1
      );
      setSpeakers(timeline.map((item) => item.row));
    },
    []
  );
  // Removed autoInsightsMessage - no longer auto-generating insights
  // const [autoInsightsMessage, setAutoInsightsMessage] = useState<string | null>(null);

  // Analysis completion states
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState<string[]>([]);
  const [showJustInTimeUI, setShowJustInTimeUI] = useState(true); // Show by default
  const [showNextActionsUI, setShowNextActionsUI] = useState(true); // Show by default for success
  const [dismissedCallout, setDismissedCallout] = useState(false);
  const [analysisEnded, setAnalysisEnded] = useState(false);
  const [sortUnansweredFirst, setSortUnansweredFirst] = useState(false);
  const [showTemplateChangeWarning, setShowTemplateChangeWarning] =
    useState(false);
  const [typingQuestionId, setTypingQuestionId] = useState<string | null>(null);

  // Session timing
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);

  // Check if changing template would cause data loss
  const hasProgressToLose = useCallback(() => {
    // Check if there's actual transcript data (speakers array contains transcript rows)
    const hasTranscriptData = speakers.length > 0;

    // Check if any questions have been answered
    const hasAnsweredQuestions = templateQuestions.some(
      (q) => q.isComplete || (q.answer && q.answer.trim().length > 0)
    );

    // Only return true if there's actual data that would be lost
    return hasTranscriptData || hasAnsweredQuestions;
  }, [speakers, templateQuestions]);

  const updateUnansweredState = useCallback(
    (questions: TemplateQuestion[], showDialog = false) => {
      if (!questions || questions.length === 0) {
        setUnansweredQuestions([]);
        setAllQuestionsAnswered(true);
        return;
      }

      const unanswered = questions.filter((question) => {
        const answerText = (question.answer || '').trim();
        return !question.isComplete || answerText.length === 0;
      });

      setUnansweredQuestions(unanswered.map((question) => question.question));
      setAllQuestionsAnswered(unanswered.length === 0);

      if (unanswered.length === 0) {
        // nothing to show
      } else if (showDialog) {
        // expand inline callout instead of dialog
        setDismissedCallout(false);
        setShowJustInTimeUI(true);
      }
    },
    []
  );

  const applyTemplateQuestions = useCallback(
    (questions: TemplateQuestion[], options?: { showDialog?: boolean }) => {
      setTemplateQuestions(questions);
      updateUnansweredState(questions, options?.showDialog ?? false);
    },
    [updateUnansweredState]
  );

  const refreshTranscriptFromServer = useCallback(async () => {
    if (!sessionId || !appUser?.id) return;
    try {
      const refreshed = await getTranscriptionSessionById(
        sessionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      if (refreshed) {
        setSessionData(refreshed);
        hydrateTranscriptFromSession(refreshed);
        setSessionSummaries([]);
      }
    } catch (error) {
      console.error('[UploadBatch] Failed to refresh transcript', error);
    }
  }, [
    sessionId,
    appUser?.id,
    getTranscriptionSessionById,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    hydrateTranscriptFromSession
  ]);

  useEffect(() => {
    // Check local storage for dismissal state on mount or session change
    if (effectiveSessionId) {
      const dismissedKey = `intakeai_callout_dismissed_${effectiveSessionId}`;
      const isDismissed = localStorage.getItem(dismissedKey) === 'true';
      setDismissedCallout(isDismissed);
    } else {
      setDismissedCallout(false);
    }
  }, [effectiveSessionId]);

  useEffect(() => {
    // Auto-expand inline callouts instead of opening dialogs
    // Only if not already dismissed (checked via state which is init from localStorage)
    if (analysisComplete && !dismissedCallout) {
      if (!allQuestionsAnswered && unansweredQuestions.length > 0) {
        setShowJustInTimeUI(true);
        // Auto-enable sort to show unanswered questions first
        setSortUnansweredFirst(true);
      } else if (allQuestionsAnswered) {
        setShowNextActionsUI(true);
      }
    }
  }, [
    analysisComplete,
    allQuestionsAnswered,
    unansweredQuestions,
    dismissedCallout
  ]);

  // Fallback auto-open when analysis did not report success, but we have question data
  useEffect(() => {
    // Fallback: expand inline instead of opening dialogs
    if (!analysisComplete && analysisEnded && !dismissedCallout) {
      if (unansweredQuestions.length > 0) {
        setShowJustInTimeUI(true);
      } else if (templateQuestions.length > 0) {
        setShowNextActionsUI(true);
      }
    }
  }, [
    analysisComplete,
    analysisEnded,
    unansweredQuestions,
    templateQuestions.length,
    dismissedCallout
  ]);

  // Template loading state
  const [templateName, setTemplateName] = useState<string>('');
  const [isLoadingQuestions, setIsLoadingQuestions] = useState<boolean>(true);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');

  // Session metrics removed - now handled by dedicated analytics

  // View Summary Popover State
  const [summaryPopoverAnchor, setSummaryPopoverAnchor] =
    useState<HTMLButtonElement | null>(null);
  const summaryPopoverOpen = Boolean(summaryPopoverAnchor);
  const [selectedSummaryTab, setSelectedSummaryTab] = useState(0);

  // Speaker Management State
  const [speakerMapping, setSpeakerMapping] =
    useState<SessionSpeakerMapping | null>(null);
  const [availablePeople, setAvailablePeople] = useState<Person[]>([]);
  const [showSpeakerDialog, setShowSpeakerDialog] = useState(false);
  const [currentSegments, setCurrentSegments] = useState<
    Array<{
      speakerIndex: number;
      text: string;
      timestamp: string;
    }>
  >([]);
  const [previousAssignments, setPreviousAssignments] = useState<
    SpeakerAssignment[]
  >([]);

  // Status state (read-only)
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);

  // Approval toggle state
  const [requiresApproval, setRequiresApproval] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);

  const refreshTemplateQuestionsFromSession = useCallback(
    async (options?: {
      showDialog?: boolean;
    }): Promise<{ questionsLoaded: boolean; questionCount: number }> => {
      if (!sessionId || !appUser?.id)
        return { questionsLoaded: false, questionCount: 0 };
      setIsLoadingQuestions(true);
      try {
        const snapshot = await getTranscriptionSessionById(
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        if (snapshot?.templateQuestions) {
          const questions = snapshot.templateQuestions.map((tq: any) => ({
            id: tq.id,
            question: tq.questionText,
            answer: tq.answerText || '',
            confidence: tq.confidenceScore || 0,
            isComplete: tq.isComplete,
            category: tq.category,
            required: tq.isRequired
          }));
          applyTemplateQuestions(questions, {
            showDialog: options?.showDialog ?? false
          });

          if (snapshot) {
            setSessionData(snapshot);
          }

          return { questionsLoaded: true, questionCount: questions.length };
        }

        if (snapshot) {
          setSessionData(snapshot);
        }
        return { questionsLoaded: false, questionCount: 0 };
      } catch (error) {
        console.error(
          '[UploadBatch] Failed to refresh template questions',
          error
        );
        return { questionsLoaded: false, questionCount: 0 };
      } finally {
        setIsLoadingQuestions(false);
      }
    },
    [
      sessionId,
      appUser?.id,
      getTranscriptionSessionById,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      applyTemplateQuestions,
      shouldUseMobileLayout
    ]
  );
  const [sessionSummaries, setSessionSummaries] = useState<GeneratedSummary[]>(
    []
  );
  const [isSummariesLoading, setIsSummariesLoading] = useState(false);
  const [isSummariesGenerating, setIsSummariesGenerating] = useState(false);

  const loadSessionSummaries = useCallback(async () => {
    if (!sessionId || !appUser?.id) return;
    setIsSummariesLoading(true);
    try {
      const items = await getSessionSummaries(
        sessionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setSessionSummaries(items);
    } catch (e) {
      // ignore if not yet generated
    } finally {
      setIsSummariesLoading(false);
    }
  }, [sessionId, appUser?.id, getAccessTokenSilently, getAccessTokenWithPopup]);
  const [documentUploadTrigger, setDocumentUploadTrigger] = useState(0);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  // Transcript auto-scroll state
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewSegments, setHasNewSegments] = useState(false);
  const previousSegmentCountRef = useRef(0);

  // Transcript scroll helpers
  const scrollToBottom = useCallback((smooth = true) => {
    if (transcriptScrollRef.current) {
      transcriptScrollRef.current.scrollTo({
        top: transcriptScrollRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
      setHasNewSegments(false);
    }
  }, []);

  const checkIfNearBottom = useCallback(() => {
    if (!transcriptScrollRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } =
      transcriptScrollRef.current;
    const threshold = 100; // pixels from bottom
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  const handleTranscriptScroll = useCallback(() => {
    const nearBottom = checkIfNearBottom();
    setIsNearBottom(nearBottom);
    if (nearBottom) {
      setHasNewSegments(false);
    }
  }, [checkIfNearBottom]);

  // Job tracking state
  const [sessionJobs, setSessionJobs] = useState<TranscriptionSessionJob[]>([]);
  const [sessionJobsInitialized, setSessionJobsInitialized] = useState(false);
  const [jobSubscription, setJobSubscription] = useState<(() => void) | null>(
    null
  );
  const transcribeJobHandledRef = useRef<string | null>(null);
  const analyzeJobHandledRef = useRef<string | null>(null);
  const summarizeJobHandledRef = useRef<string | null>(null);
  const isSessionProcessing = useMemo(() => {
    const statusValue: any = sessionData?.status;
    return (
      statusValue === TranscriptionSessionStatus.Processing ||
      (typeof statusValue === 'string' &&
        String(statusValue).toLowerCase() === 'processing')
    );
  }, [sessionData?.status]);
  const normalizeJobStatus = (status: number | string): string => {
    const name = getJobStatusName(status);
    return typeof name === 'string'
      ? name.toLowerCase()
      : String(name).toLowerCase();
  };
  const isJobRunning = (status: number | string) =>
    normalizeJobStatus(status) === 'running';
  const isJobSucceeded = (status: number | string) => {
    const normalized = normalizeJobStatus(status);
    return normalized === 'succeeded' || normalized === 'complete';
  };
  const isJobFailed = (status: number | string) =>
    normalizeJobStatus(status) === 'failed';
  const isJobCancelled = (status: number | string) =>
    normalizeJobStatus(status) === 'cancelled';
  const [currentProcessingStage, setCurrentProcessingStage] =
    useState<string>('');

  // Recording interface ref for auto-scroll
  const recordingInterfaceRef = useRef<HTMLDivElement>(null);

  // Note: Review findings now handled by chat-based approach

  // UPDATED: Active tab state initialization - Template tab is now first priority
  const [activeTab, setActiveTab] = useState<ActiveTabType>('summaries');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
  const [tabOverflowAnchorEl, setTabOverflowAnchorEl] =
    useState<null | HTMLElement>(null);
  const [availableDistributionGroups, setAvailableDistributionGroups] =
    useState<
      Array<{
        id: number;
        name: string;
        description: string;
        recipientCount: number;
        associatedSummaries: string[];
      }>
    >([]);
  const [isLoadingDistributionGroups, setIsLoadingDistributionGroups] =
    useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState<boolean>(false);
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>([]);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const recordingIdRef = useRef<string | null>(null);
  const pendingSegmentsRef = useRef<
    Array<{
      textContent: string;
      timestamp: string;
      speakerIndex: number;
      confidenceScore: number;
      durationSeconds: number;
    }>
  >([]);

  useEffect(() => {
    setSessionJobsInitialized(false);
  }, [sessionId]);
  const startTimeUtcRef = useRef<string | null>(null);
  const [isStartingRecording, setIsStartingRecording] =
    useState<boolean>(false);
  // Mobile/medium FAB visibility (user-toggleable)
  const [showFabControls, setShowFabControls] = useState<boolean>(true);
  const [fabVisibilityOverride, setFabVisibilityOverride] = useState<
    'show' | 'hide' | null
  >(null);

  // Track accumulated recording time (excluding pauses)
  const accumulatedTimeRef = useRef<number>(0);
  const lastResumeTimeRef = useRef<number | null>(null);
  const recordingFileNameRef = useRef<string | null>(null);

  // Chunk upload tracking for streaming audio to blob storage
  const uploadedBlocksRef = useRef<Array<{ index: number; blockId: string }>>(
    []
  );
  const chunkIndexRef = useRef<number>(0);
  const pendingUploadsRef = useRef<Set<string>>(new Set());
  const uploadErrorRef = useRef<string | null>(null);
  const lastSegmentIdRef = useRef<string | null>(null);
  const catchUpInFlightRef = useRef<boolean>(false);

  // Define segment catch-up callbacks before useEffects that reference them
  const appendSegmentsFromDelta = useCallback(
    (segments: SpeakerSegmentResult[]) => {
      if (!segments?.length) return;
      const existingIds = new Set(
        speakers.filter((s: any) => s?.id).map((s: any) => s.id)
      );
      const transcriptAdditions: string[] = [];
      setSpeakers((prev) => {
        const appended: SpeakerSegment[] = [];
        for (const seg of segments) {
          const segId = seg.id ?? `delta-${Date.now()}-${Math.random()}`;
          if (existingIds.has(segId)) continue;
          const text = seg.textContent ?? '';
          appended.push({
            id: segId,
            speaker: seg.speakerIndex ?? 0,
            text,
            timestamp: seg.timestamp,
            confidence: seg.confidenceScore ?? 1,
            duration: seg.durationSeconds ?? 0,
            isPauseSegment: (seg as any).isPauseSegment
          } as SpeakerSegment);
          existingIds.add(segId);
          if (!(seg as any).isPauseSegment && text.trim()) {
            transcriptAdditions.push(
              `Speaker ${seg.speakerIndex ?? 0}: ${text}`
            );
          }
        }
        if (!appended.length) {
          return prev;
        }
        return [...prev, ...appended];
      });
      if (transcriptAdditions.length) {
        setState((prev) => ({
          ...prev,
          transcript: prev.transcript
            ? `${prev.transcript}\n${transcriptAdditions.join('\n')}`
            : transcriptAdditions.join('\n')
        }));
      }
      const last = segments[segments.length - 1];
      if (last?.id) {
        lastSegmentIdRef.current = last.id;
      }
    },
    [speakers, setState, setSpeakers]
  );

  const catchUpSegments = useCallback(
    async (reason: string) => {
      if (
        isSessionOwner ||
        !effectiveSessionId ||
        !appUser?.id ||
        catchUpInFlightRef.current
      ) {
        return;
      }
      catchUpInFlightRef.current = true;
      try {
        const result = await getSegmentsDelta(
          effectiveSessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          {
            sinceSegmentId: lastSegmentIdRef.current ?? undefined
          }
        );
        if (result?.segments?.length) {
          appendSegmentsFromDelta(result.segments);
          if (result.nextCursor) {
            lastSegmentIdRef.current = result.nextCursor;
          }
        }
      } catch (error) {
        console.error(
          '[LiveTranscription] Failed to catch up segments',
          reason,
          error
        );
      } finally {
        catchUpInFlightRef.current = false;
      }
    },
    [
      isSessionOwner,
      effectiveSessionId,
      appUser?.id,
      appendSegmentsFromDelta,
      getAccessTokenSilently,
      getAccessTokenWithPopup
    ]
  );

  useEffect(() => {
    setFabVisibilityOverride(null);
    setRecordingControlsOverride(null);
  }, [sessionId]);

  useEffect(() => {
    if (!speakers.length) {
      lastSegmentIdRef.current = null;
      return;
    }
    for (let i = speakers.length - 1; i >= 0; i--) {
      const row = speakers[i] as any;
      if (
        row?.id &&
        (typeof row.speaker !== 'undefined' || row?.isPauseSegment === true)
      ) {
        lastSegmentIdRef.current = row.id;
        return;
      }
    }
  }, [speakers]);

  useEffect(() => {
    if (isSessionOwner || !effectiveSessionId || !appUser?.id) return;
    catchUpSegments('initial');
  }, [isSessionOwner, effectiveSessionId, appUser?.id, catchUpSegments]);

  useEffect(() => {
    if (isSessionOwner || !effectiveSessionId || !appUser?.id) return;
    const interval = window.setInterval(
      () => catchUpSegments('interval'),
      7000
    );
    return () => window.clearInterval(interval);
  }, [isSessionOwner, effectiveSessionId, appUser?.id, catchUpSegments]);

  // Auto-scroll to bottom when transcript tab is opened (only during recording)
  useEffect(() => {
    if (activeTab === 'transcript' && transcriptScrollRef.current) {
      // Only auto-scroll to bottom if currently recording
      // After processing is complete, user should see the top of the transcript
      if (state.isRecording) {
        setTimeout(() => scrollToBottom(false), 100);
      } else {
        // Scroll to top for processed files
        setTimeout(() => {
          if (transcriptScrollRef.current) {
            transcriptScrollRef.current.scrollTop = 0;
          }
        }, 100);
      }
    }
  }, [activeTab, scrollToBottom, state.isRecording]);

  // Auto-scroll when new segments arrive (if user is near bottom)
  useEffect(() => {
    const currentCount = speakers.length;
    const previousCount = previousSegmentCountRef.current;

    if (currentCount > previousCount) {
      // New segments have arrived
      if (isNearBottom) {
        // User is at bottom, auto-scroll
        scrollToBottom(true);
      } else {
        // User has scrolled up, show indicator
        setHasNewSegments(true);
      }
    }

    previousSegmentCountRef.current = currentCount;
  }, [speakers.length, isNearBottom, scrollToBottom]);

  // Timer effect to update recording duration and force visualizer updates
  useEffect(() => {
    if (!isSessionOwner) return;
    let interval: NodeJS.Timeout | null = null;

    if (state.isRecording && !state.isPaused) {
      if (lastResumeTimeRef.current === null) {
        lastResumeTimeRef.current = Date.now();
      }

      interval = setInterval(() => {
        if (lastResumeTimeRef.current !== null) {
          const currentSegmentDuration = Date.now() - lastResumeTimeRef.current;
          setRecordingDuration(
            accumulatedTimeRef.current + currentSegmentDuration
          );
        }
      }, 100);
    } else if (state.isRecording && state.isPaused) {
      if (lastResumeTimeRef.current !== null) {
        accumulatedTimeRef.current += Date.now() - lastResumeTimeRef.current;
        lastResumeTimeRef.current = null;
      }
      setRecordingDuration(accumulatedTimeRef.current);
    } else if (!state.isRecording) {
      setRecordingDuration(0);
      accumulatedTimeRef.current = 0;
      lastResumeTimeRef.current = null;
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [state.isRecording, state.isPaused, isSessionOwner]);

  // Define broadcastRecordingState before useEffects that use it
  const broadcastRecordingState = useCallback(
    async (override?: Partial<RecordingStatePayload>) => {
      if (
        REALTIME_MODE_ENABLED ||
        !isSessionOwner ||
        !effectiveSessionId ||
        !appUser?.id
      ) {
        return;
      }

      const accumulated =
        override?.accumulatedMilliseconds ?? recordingDuration;

      const payload: RecordingStatePayload = {
        recordingId: recordingIdRef.current ?? undefined,
        isRecording: override?.isRecording ?? state.isRecording,
        isPaused: override?.isPaused ?? state.isPaused,
        accumulatedMilliseconds: accumulated,
        durationMilliseconds:
          override?.durationMilliseconds ?? accumulated ?? undefined,
        audioLevel: override?.audioLevel ?? state.audioLevel,
        interimTranscript:
          override?.interimTranscript ?? state.interimTranscript,
        recordingStartedAtUtc:
          override?.recordingStartedAtUtc ??
          startTimeUtcRef.current ??
          undefined,
        recordingEndedAtUtc: override?.recordingEndedAtUtc,
        updatedAtUtc: new Date().toISOString()
      };

      latestRecordingStateRef.current = payload;

      await postRecordingState(
        effectiveSessionId,
        payload,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
    },
    [
      isSessionOwner,
      effectiveSessionId,
      appUser?.id,
      state.isRecording,
      state.isPaused,
      state.audioLevel,
      state.interimTranscript,
      recordingDuration,
      getAccessTokenSilently,
      getAccessTokenWithPopup
    ]
  );

  useEffect(() => {
    if (!isSessionOwner || !state.isRecording) return;
    const now = Date.now();
    if (now - lastLiveBroadcastRef.current < 1000) return;
    lastLiveBroadcastRef.current = now;
    broadcastRecordingState({ accumulatedMilliseconds: recordingDuration });
  }, [
    recordingDuration,
    isSessionOwner,
    state.isRecording,
    broadcastRecordingState
  ]);

  useEffect(() => {
    if (!isSessionOwner || !state.isRecording) return;
    const previous = latestRecordingStateRef.current?.interimTranscript ?? '';
    const next = state.interimTranscript ?? '';
    if (previous === next) return;
    broadcastRecordingState({ interimTranscript: next });
  }, [
    state.interimTranscript,
    state.isRecording,
    isSessionOwner,
    broadcastRecordingState
  ]);

  // Removed useEffect for auto insights message cleanup
  // useEffect(() => {
  //   if (autoInsightsMessage) {
  //     const timer = setTimeout(() => {
  //       setAutoInsightsMessage(null);
  //     }, 2000); // Clear after 2 seconds
  //     return () => clearTimeout(timer);
  //   }
  // }, [autoInsightsMessage]);

  // Run the Document Processing Flow (Embedding -> Questions -> Summary) removed

  // File upload handler for camera and file attachments
  const handleFileUploadInternal = useCallback(
    async (files: File[]) => {
      if (!appUser?.id || !sessionId) {
        console.error('Cannot upload file: missing user or session', {
          hasUser: !!appUser?.id,
          hasSession: !!sessionId
        });
        return;
      }

      try {
        setIsUploadingDocument(true);

        // Import the upload function dynamically
        const { processSessionDocuments, uploadGenericDocument } = await import(
          '@/services/transcriptionDocumentService'
        );

        for (const file of files) {
          const result = await uploadGenericDocument(
            sessionId,
            file,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup,
            {
              skipAnalysis: true
            }
          );

          if (!result.success) {
            console.error('Upload failed:', result.message);
            toast.showError('Failed to upload file');
            return;
          }
        }

        await processSessionDocuments(
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        setDocumentUploadTrigger((prev) => prev + 1);
        setActiveTab('documents');
        setUploadProgress(100);
        toast.showSuccess('File uploaded successfully!', {
          duration: 3000
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.showError('Failed to upload file');
      } finally {
        setIsUploadingDocument(false);
      }
    },
    [appUser?.id, sessionId, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  // Define upload handlers inside component to access state
  let handleCameraCapture: () => void;
  let handleFileAttachment: () => void;

  ({ handleCameraCapture, handleFileAttachment } =
    createAttachmentUploadHandlers(handleFileUploadInternal));
  // View Summary Popover Handlers
  const handleViewSummaryClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setSummaryPopoverAnchor(event.currentTarget);
  };

  const handleSummaryPopoverClose = () => {
    setSummaryPopoverAnchor(null);
  };

  // Refs for Deepgram functionality
  const deepgramLiveRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const pausedRef = useRef<boolean>(false);
  const pauseStartRef = useRef<Date | null>(null);
  const accumulatedPauseDurationRef = useRef<number>(0); // Track total pause duration in milliseconds
  const audioDataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  // Removed: AssemblyAI ref; Deepgram is the only live provider here
  // No batching needed – periodic analysis on full transcript
  const lastProcessingTimeRef = useRef<number>(0);

  const PCM_SAMPLE_RATE = 16000;

  // Speaker Management Services
  const speakerService = React.useMemo(
    () =>
      createSpeakerManagementService(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      ),
    []
  );

  const personService = React.useMemo(
    () => createPersonService(getAccessTokenSilently, getAccessTokenWithPopup),
    []
  );

  // Handle session initialization and URL routing
  useEffect(() => {
    const handleSessionInitialization = async () => {
      if (isSessionBasedUrl && currentSessionId) {
        // Session-based URL: set the session ID from URL
        setSessionId(currentSessionId);

        // Load existing session data (speakers, insights, template info, etc.)
        if (
          appUser?.id &&
          !initializedSessionIdsRef.current.has(currentSessionId)
        ) {
          try {
            const session = await getTranscriptionSessionById(
              currentSessionId,
              appUser.id,
              getAccessTokenSilently,
              getAccessTokenWithPopup
            );
            if (session) {
              // Reset transient state if we're switching to a new session
              if (sessionId && sessionId !== session.id) {
                setSpeakers([]);
                setState((prev) => ({
                  ...prev,
                  transcript: '',
                  interimTranscript: '',
                  isRecording: false,
                  isPaused: false
                }));
                setUnansweredQuestions([]);
                setAllQuestionsAnswered(false);
                setAnalysisComplete(false);
                setAnalysisEnded(false);
                setDismissedCallout(false);
              }

              setSessionData(session);
              initializedSessionIdsRef.current.add(currentSessionId);

              // Initialize gating last-seen cursor from server if available
              const initialServerCursor =
                (session as any)?.gatingLastProcessedSegmentId ||
                (session as any)?.GatingLastProcessedSegmentId ||
                null;
              if (initialServerCursor) {
                gatingLastSeenSegmentIdRef.current =
                  String(initialServerCursor);
              }

              // Restore all session state

              // Restore speaker segments (transcript) and interleave recording dividers by time
              const combinedRows: TranscriptRow[] = [];
              const timeline: Array<{
                t: number;
                kind: 'segment' | 'divider';
                row: any;
              }> = [];
              if (session.speakerSegments?.length) {
                for (const seg of session.speakerSegments) {
                  const isPause = (seg as any).isPauseSegment || false;
                  timeline.push({
                    t: new Date(seg.timestamp).getTime(),
                    kind: 'segment',
                    row: {
                      id: seg.id,
                      speaker: seg.speakerIndex,
                      text: seg.textContent,
                      timestamp: seg.timestamp,
                      confidence: seg.confidenceScore || 1.0,
                      duration: seg.durationSeconds || 0,
                      isPauseSegment: isPause
                    }
                  });
                }
              }
              if (session.recordings?.length) {
                for (const rec of session.recordings) {
                  timeline.push({
                    t: new Date(rec.recordingStartTimeUtc).getTime(),
                    kind: 'divider',
                    row: {
                      id: `rec-divider-${rec.id}`,
                      kind: 'recording-divider',
                      startTimeUtc: rec.recordingStartTimeUtc
                    }
                  });
                }
              }
              if (timeline.length) {
                timeline.sort((a, b) =>
                  a.t !== b.t
                    ? a.t - b.t
                    : a.kind === 'divider' && b.kind === 'segment'
                    ? -1
                    : 1
                );
                for (const item of timeline) combinedRows.push(item.row);
                setSpeakers(combinedRows);
              }

              // Restore AI insights (action items, etc.)
              if (session.aiInsights && session.aiInsights.length > 0) {
                const insights = session.aiInsights.map((insight: any) => ({
                  id: insight.id,
                  type: insight.insightType,
                  title: insight.title,
                  description: insight.description,
                  priority: insight.priority,
                  timestamp: insight.timestamp,
                  relatedSpeaker: insight.relatedSpeakerIndex
                }));
                // AI insights restored
              }

              // Set session title in header
              if (session.title) {
                setTemplateName(session.title);
              }

              // Restore session metrics if available
              if (session.metrics) {
                // Session metrics restored separately if needed
              }

              // Update transcript state if there's content
              if (session.fullTranscriptText) {
                setState((prev) => ({
                  ...prev,
                  transcript: session.fullTranscriptText
                }));
              }

              // Restore session timing
              if (session.sessionStartTime) {
                setSessionStartTime(new Date(session.sessionStartTime));
              }

              // Note: Template questions will be loaded separately by loadTemplateQuestions useEffect
            }
          } catch (error) {
            console.error('Failed to load session data:', error);
          }
        }
      } else if (!isSessionBasedUrl && templateId && appUser?.id) {
        // Legacy template-based URL: create session and redirect
        const sessionId = await createSessionAndNavigate({
          templateId: templateId ? Number(templateId) : undefined,
          templateName: urlTemplateName
            ? decodeURIComponent(urlTemplateName)
            : undefined,
          userId: appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          navigate
        });

        if (!sessionId) {
          console.error('Failed to create session from legacy URL');
          // Stay on current page with error state
        }
      }
    };

    handleSessionInitialization();
  }, [
    isSessionBasedUrl,
    currentSessionId,
    templateId,
    urlTemplateName,
    appUser?.id
  ]);

  // Helper function to map AI insights to critical items format
  const mapInsightsToCriticalItems = useCallback(
    (insights: any[]): CriticalItem[] => {
      return insights.map((insight: any) => {
        // Map insight type to category
        let category: CriticalItem['category'] = 'other';
        const insightType = insight.type?.toLowerCase() || '';

        if (insightType === 'concern' || insightType === 'sentiment') {
          // Check description for self-harm, medications, allergies, or gang-related content
          const description = (insight.description || '').toLowerCase();
          const title = (insight.title || '').toLowerCase();
          const combined = `${title} ${description}`;

          if (
            combined.includes('self-harm') ||
            combined.includes('suicide') ||
            combined.includes('harm')
          ) {
            category = 'self_harm';
          } else if (
            combined.includes('medication') ||
            combined.includes('drug') ||
            combined.includes('prescription')
          ) {
            category = 'medications';
          } else if (
            combined.includes('allergy') ||
            combined.includes('allergic')
          ) {
            category = 'allergies';
          } else if (
            combined.includes('gang') ||
            combined.includes('enemy') ||
            combined.includes('enemies')
          ) {
            category = 'enemies_gang';
          }
        } else if (insightType === 'actionitem' || insightType === 'keypoint') {
          category = 'other';
        }

        // Map priority to confidence
        const priority = insight.priority?.toLowerCase() || 'medium';
        let confidence: 'High' | 'Medium' | 'Low' = 'Medium';
        if (priority === 'high') confidence = 'High';
        else if (priority === 'low') confidence = 'Low';

        // Calculate time ago
        const timestamp = new Date(insight.timestamp);
        const now = new Date();
        const diffMs = now.getTime() - timestamp.getTime();
        const diffSeconds = Math.floor(diffMs / 1000);

        return {
          id: insight.id,
          category,
          snippet: insight.description || insight.title || '',
          detectedAgoSeconds: diffSeconds,
          confidence,
          status: 'new' as const
        };
      });
    },
    []
  );

  // Fetch and map AI insights to critical items
  useEffect(() => {
    const fetchInsights = async () => {
      if (!sessionId || !appUser?.id) {
        return;
      }

      try {
        const insights = await getSessionInsights(
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const mappedItems = mapInsightsToCriticalItems(insights);
        setCriticalItems(mappedItems);
      } catch (error) {
        console.error('Error fetching AI insights:', error);
      }
    };

    fetchInsights();
  }, [
    sessionId,
    appUser?.id,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    mapInsightsToCriticalItems
  ]);

  // Delete critical item callback
  const deleteCriticalItem = useCallback(
    async (id: string) => {
      if (!sessionId || !appUser?.id) return;

      // Optimistically remove from UI
      setCriticalItems((prev) => prev.filter((i) => i.id !== id));

      try {
        // Call backend API to delete the insight
        const success = await deleteSessionInsight(
          sessionId,
          id,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        if (!success) {
          console.error('Failed to delete insight');
          // Reload insights to restore the deleted item on failure
          const insights = await getSessionInsights(
            sessionId,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
          const mappedItems = mapInsightsToCriticalItems(insights);
          setCriticalItems(mappedItems);
        }
      } catch (error) {
        console.error('Error deleting critical item:', error);
        // Reload insights to restore the deleted item on error
        const insights = await getSessionInsights(
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        const mappedItems = mapInsightsToCriticalItems(insights);
        setCriticalItems(mappedItems);
      }
    },
    [
      sessionId,
      appUser?.id,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      mapInsightsToCriticalItems
    ]
  );

  // Helper function to acquire Auth0 access token for LangGraph calls
  const getAuthToken = async (): Promise<string> => {
    const authParams = {
      authorizationParams: {
        audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      }
    };
    try {
      return await getAccessTokenSilently(authParams);
    } catch (error: any) {
      if (
        error.error === 'consent_required' ||
        error.error === 'login_required' ||
        error.error === 'interaction_required'
      ) {
        const token = await getAccessTokenWithPopup(authParams);
        if (!token) {
          throw new Error(
            'Interactive token acquisition failed to return a token.'
          );
        }
        return token;
      }
      throw error;
    }
  };

  // Run only the Summaries step via LangGraph (creates/updates Summarize job and finalizes status)
  const performSummariesOnly = useCallback(async () => {
    if (!sessionId || !appUser?.id) return { success: false } as const;
    try {
      const token = await getAuthToken();
      const LANGGRAPH_BASE_URL =
        import.meta.env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';
      const client = new Client({
        apiUrl: LANGGRAPH_BASE_URL,
        defaultHeaders: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': appUser.id,
          'X-Session-Id': sessionId
        }
      });
      const createdThread = await client.threads.create();
      const threadId = createdThread.thread_id; // ephemeral; do not persist or pass in metadata
      let ok = false;
      const stream = client.runs.stream(threadId, 'combined_summary_flow', {
        input: {
          session_id: sessionId,
          user_id: appUser.id,
          metadata: {
            timestamp: new Date().toISOString()
          }
        },
        config: {
          configurable: {
            authorization: `Bearer ${token}`,
            session_id: sessionId,
            user_id: appUser.id
          }
        },
        metadata: {
          session_id: sessionId,
          transcription_session_id: sessionId,
          user_id: appUser.id
        },
        streamMode: ['messages', 'updates']
      });
      for await (const sse of stream as any) {
        const evt = sse?.event as string | undefined;
        const data = sse?.data;
        if (evt === 'updates' && data && typeof data === 'object') {
          if (
            data.workflow_stage === 'summaries_complete' ||
            data.final_analysis_complete === true
          ) {
            ok = true;
          }
        }
      }
      return { success: ok } as const;
    } catch (e) {
      console.error('[SummariesOnly] Error', e);
      return { success: false } as const;
    }
  }, [sessionId, appUser?.id, getAuthToken]);

  // Removed periodic analysis - now only analyzing at end of conversation
  // useEffect(() => {
  //   let interval: NodeJS.Timeout | null = null;

  //   if (state.isRecording && sessionId) {
  //     interval = setInterval(() => {
  //       performPeriodicAnalysis().catch(console.error);
  //     }, 15000); // 15 seconds
  //   }

  //   return () => {
  //     if (interval) clearInterval(interval);
  //   };
  // }, [state.isRecording, sessionId, performPeriodicAnalysis]);

  // Trigger final analysis on stop (only if recording was actually started)
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [hasStartedRecording, setHasStartedRecording] = useState(false);
  const [isAnalyzingTranscript, setIsAnalyzingTranscript] = useState(false);
  const [pipelineStage, setPipelineStage] = useState<
    'idle' | 'uploading' | 'transcribing' | 'analyzing' | 'complete' | 'error'
  >('idle');
  const [analyzeSubstage, setAnalyzeSubstage] = useState<
    'idle' | 'quality' | 'questions' | 'summaries' | 'complete' | 'error'
  >('idle');
  const [transcribingProgress, setTranscribingProgress] = useState<
    number | null
  >(null);
  const [transcribingStatusMessage, setTranscribingStatusMessage] = useState<
    string | null
  >(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [analyzingProgress, setAnalyzingProgress] = useState<number | null>(
    null
  );

  // ---------------------------------------------------------------------
  // LangGraph: Periodic gated question filling (5–10s, concurrency-guarded)
  // ---------------------------------------------------------------------
  const gatedQfThreadIdRef = useRef<string | null>(null);
  const gatingInFlightRef = useRef<boolean>(false);
  const gatingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const gatingLastSeenSegmentIdRef = useRef<string | null>(null);
  const gatingPendingRef = useRef<boolean>(false);
  const isAnalyzingRef = useRef<boolean>(false);
  const isRecordingRef = useRef<boolean>(false);
  const isPausedRef = useRef<boolean>(false);
  const runGatedQfTickRef = useRef<(() => Promise<void>) | null>(null);
  // Keep refs in sync so callbacks read the latest state without re-binding
  useEffect(() => {
    isAnalyzingRef.current = isAnalyzingTranscript;
  }, [isAnalyzingTranscript]);
  useEffect(() => {
    isRecordingRef.current = state.isRecording;
    isPausedRef.current = state.isPaused;
  }, [state.isRecording, state.isPaused]);

  // Fire AssemblyAI batch transcription every 30 seconds while recording
  // useEffect(() => {
  //   if (!sessionId) return;
  //   if (!state.isRecording) return;

  //   const interval = setInterval(() => {
  //     // This will no-op if recording has stopped or is paused
  //     triggerAssemblyBatchTranscribe('interval').catch(() => {
  //       // Swallow errors for background batch calls
  //     });
  //   }, 30000); // 30 seconds

  //   return () => {
  //     clearInterval(interval);
  //   };
  // }, [sessionId, state.isRecording, triggerAssemblyBatchTranscribe]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isSessionOwner) return;
      // Ignore if user is typing in an input field
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (isTyping) return;

      // Spacebar - Play/Pause
      if (event.code === 'Space' && state.isRecording) {
        event.preventDefault();
        if (state.isPaused) {
          resumeRecording();
        } else {
          pauseRecording();
        }
      }

      // Esc - End & Generate Summary
      if (
        event.code === 'Escape' &&
        (state.isRecording || hasStartedRecording)
      ) {
        event.preventDefault();
        stopConversation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [state.isRecording, state.isPaused, hasStartedRecording, isSessionOwner]);

  const stopGatingTimer = useCallback(() => {
    if (gatingTimerRef.current) {
      clearTimeout(gatingTimerRef.current);
      gatingTimerRef.current = null;
    }
  }, []);

  const scheduleNextGate = useCallback(
    (delayMs: number) => {
      // Only schedule when no gate is currently running and we should run
      // Use refs to always read current state values (avoid stale closures)
      const shouldRunNow =
        !!effectiveSessionId &&
        ((isRecordingRef.current && !isPausedRef.current) ||
          isAnalyzingRef.current);
      if (!shouldRunNow) {
        return;
      }
      stopGatingTimer();
      gatingTimerRef.current = setTimeout(() => {
        // Guard again at execution time
        const shouldRunAtFire =
          !!effectiveSessionId &&
          ((isRecordingRef.current && !isPausedRef.current) ||
            isAnalyzingRef.current);
        if (!shouldRunAtFire) {
          return;
        }
        if (!gatingInFlightRef.current && runGatedQfTickRef.current) {
          void runGatedQfTickRef.current();
        } else if (gatingInFlightRef.current) {
          gatingPendingRef.current = true;
        }
      }, Math.max(5000, delayMs));
    },
    [effectiveSessionId]
  );
  // Frontend no longer constructs gating payload; the agent computes unanswered and new segments server-side

  const runGatedQfTick = useCallback(async () => {
    try {
      const sid = effectiveSessionId;
      if (!sid || !appUser?.id) {
        scheduleNextGate(5000);
        return;
      }
      // Avoid overlapping calls and avoid running while heavy Analyze job is active
      if (gatingInFlightRef.current || isAnalyzingRef.current) {
        scheduleNextGate(5000);
        return;
      }

      gatingInFlightRef.current = true;
      (window as any).__gatingInFlight = true;
      // Queue the next tick immediately; if it fires while this is in-flight, we'll run right after finish
      scheduleNextGate(5000);
      const token = await getAuthToken();
      const LANGGRAPH_BASE_URL =
        (import.meta.env as any).VITE_LANGGRAPH_BASE_URL ||
        'http://localhost:2024';

      const client = createLangGraphClient(
        LANGGRAPH_BASE_URL,
        token,
        appUser.id
      );

      // Reuse persisted thread mapping for GatedQuestionFilling if available, else create and persist
      if (!gatedQfThreadIdRef.current) {
        const cached = getCachedThreadId(sid, ThreadType.GatedQuestionFilling);
        if (cached) {
          gatedQfThreadIdRef.current = cached;
        }
      }
      if (!gatedQfThreadIdRef.current) {
        try {
          const res = await fetchCurrentThreadId(
            sid,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup,
            ThreadType.GatedQuestionFilling
          );
          if (res?.threadId) {
            gatedQfThreadIdRef.current = res.threadId;
            cacheThreadId(sid, res.threadId, ThreadType.GatedQuestionFilling);
          }
        } catch {}
      }
      if (!gatedQfThreadIdRef.current) {
        const created = await client.threads.create();
        gatedQfThreadIdRef.current = created.thread_id;
        cacheThreadId(
          sid,
          gatedQfThreadIdRef.current,
          ThreadType.GatedQuestionFilling
        );
        updateCurrentThreadId(
          sid,
          appUser.id,
          gatedQfThreadIdRef.current,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          ThreadType.GatedQuestionFilling
        ).catch(() => {});
      }
      const threadId = gatedQfThreadIdRef.current!;
      const jitter = 5000; // 5s cadence

      const stream = client.runs.stream(
        threadId,
        'gated_question_filling_flow',
        {
          input: {
            session_id: sid,
            user_id: appUser.id,
            backend_auth_token: token
          },
          config: {
            configurable: {
              authorization: `Bearer ${token}`,
              session_id: sid,
              user_id: appUser.id,
              thread_id: threadId
            }
          },
          metadata: {
            session_id: sid,
            thread_id: threadId,
            conversation_id: threadId,
            transcription_session_id: sid,
            user_id: appUser.id
          },
          streamMode: ['messages', 'updates'],
          streamSubgraphs: true
        }
      );

      let streamEventCount = 0;
      let lastWorkflowStage = null;
      let didRefreshQuestions = false;
      for await (const sse of stream as any) {
        streamEventCount++;
        const evt = sse?.event as string | undefined;
        const data = sse?.data;
        if (!evt) continue;

        if (evt === 'updates' && data && typeof data === 'object') {
          if (data.workflow_stage) {
            lastWorkflowStage = data.workflow_stage;
          }
          // Trigger refresh when gated fill produced outputs
          const hasGatedFillOutput = !!(
            (data as any).gated_fill &&
            Array.isArray((data as any).gated_fill.messages) &&
            (data as any).gated_fill.messages.length > 0
          );
          const isFillTerminalStage =
            data.workflow_stage === 'gated_fill_complete' ||
            data.workflow_stage === 'gated_fill_failed' ||
            data.workflow_stage === 'fill_skipped';

          if (
            (hasGatedFillOutput || isFillTerminalStage) &&
            !didRefreshQuestions
          ) {
            if (sid && appUser?.id) {
              try {
                const refreshed = await getTranscriptionSessionById(
                  sid,
                  appUser.id,
                  getAccessTokenSilently,
                  getAccessTokenWithPopup,
                  { bypassCache: true }
                );
                if (refreshed) {
                  didRefreshQuestions = true;
                  try {
                    const uiQuestions = (refreshed.templateQuestions || []).map(
                      (tq: any) => ({
                        id: tq.id,
                        question: tq.questionText,
                        answer: tq.answerText || '',
                        confidence: tq.confidenceScore ?? 0,
                        isComplete: !!tq.isComplete,
                        category: tq.category,
                        required: tq.isRequired
                      })
                    );
                    applyTemplateQuestions(uiQuestions);
                  } catch {}
                  setSessionData((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      title: refreshed.title ?? prev.title,
                      templateQuestions:
                        refreshed.templateQuestions ?? prev.templateQuestions,
                      aiInsights: refreshed.aiInsights ?? prev.aiInsights
                    };
                  });

                  // Update critical items with new insights in real-time
                  if (refreshed.aiInsights && refreshed.aiInsights.length > 0) {
                    try {
                      const mappedItems = mapInsightsToCriticalItems(
                        refreshed.aiInsights
                      );
                      setCriticalItems(mappedItems);
                    } catch (e) {
                      console.error(
                        'Failed to map insights to critical items:',
                        e
                      );
                    }
                  }
                }
                const serverCursor =
                  (refreshed as any)?.gatingLastProcessedSegmentId ||
                  (refreshed as any)?.GatingLastProcessedSegmentId ||
                  null;
                if (serverCursor) {
                  gatingLastSeenSegmentIdRef.current = String(serverCursor);
                }
              } catch (e) {
                console.error('Failed to refresh session after fill', e);
              }
            }
          }
        }
      }
      // stream closed

      // Fallback: if stream closed without mid-stream refresh but indicates completion, refresh once
      if (!didRefreshQuestions && lastWorkflowStage === 'gated_fill_complete') {
        try {
          if (sid && appUser?.id) {
            const refreshed = await getTranscriptionSessionById(
              sid,
              appUser.id,
              getAccessTokenSilently,
              getAccessTokenWithPopup,
              { bypassCache: true }
            );
            if (refreshed) {
              const uiQuestions = (refreshed.templateQuestions || []).map(
                (tq: any) => ({
                  id: tq.id,
                  question: tq.questionText,
                  answer: tq.answerText || '',
                  confidence: tq.confidenceScore ?? 0,
                  isComplete: !!tq.isComplete,
                  category: tq.category,
                  required: tq.isRequired
                })
              );
              applyTemplateQuestions(uiQuestions);
              setSessionData((prev) =>
                prev
                  ? {
                      ...prev,
                      title: refreshed.title ?? prev.title,
                      templateQuestions:
                        refreshed.templateQuestions ?? prev.templateQuestions,
                      aiInsights: refreshed.aiInsights ?? prev.aiInsights
                    }
                  : prev
              );
            }
          }
        } catch (e) {
          console.error('GatedQF fallback refresh failed', e);
        }
      }

      // Advance cursor only after a successful tick finishes
      gatingInFlightRef.current = false;
      (window as any).__gatingInFlight = false;
      // If a tick was queued while in-flight, run it immediately (or after 1s)
      if (gatingPendingRef.current) {
        gatingPendingRef.current = false;
        setTimeout(() => {
          if (runGatedQfTickRef.current) {
            void runGatedQfTickRef.current();
          }
        }, 1000);
      }
    } catch (e) {
      console.error('GatedQF tick error', e);
      gatingInFlightRef.current = false;
      (window as any).__gatingInFlight = false;
      // If a tick was queued while in-flight, run it immediately (or after 1s)
      if (gatingPendingRef.current) {
        gatingPendingRef.current = false;
        setTimeout(() => {
          if (runGatedQfTickRef.current) {
            void runGatedQfTickRef.current();
          }
        }, 1000);
      }
    }
  }, [
    effectiveSessionId,
    appUser?.id,
    scheduleNextGate,
    mapInsightsToCriticalItems,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Keep runGatedQfTick ref in sync for cross-callback references
  useEffect(() => {
    runGatedQfTickRef.current = runGatedQfTick;
  }, [runGatedQfTick]);

  // Start/stop the gating loop when recording is active (and not paused) or session is in analyzing/processing stages
  useEffect(() => {
    const shouldRun =
      !!effectiveSessionId &&
      ((state.isRecording && !state.isPaused) || isAnalyzingRef.current);
    if (shouldRun) {
      scheduleNextGate(5000);
    } else {
      stopGatingTimer();
    }
    return () => {
      stopGatingTimer();
    };
  }, [effectiveSessionId, state.isRecording, state.isPaused]);

  // Trigger final analysis on stop (only if recording was actually started)

  // processJobUpdate removed

  // Recording controls visibility - hide by default, only show when starting new recording or actively recording
  const [showRecordingControls, setShowRecordingControls] = useState(false);
  const [recordingControlsOverride, setRecordingControlsOverride] = useState<
    'show' | 'hide' | null
  >(null);

  // Check if session has existing content (audio or documents)
  const hasExistingContent = useMemo(() => {
    if (!sessionData) return false;
    return (
      sessionData.status === TranscriptionSessionStatus.Approved ||
      (sessionData.speakerSegments?.length ?? 0) > 0 ||
      (sessionData.recordings?.length ?? 0) > 0 ||
      (sessionData.companyDocuments?.length ?? 0) > 0
    );
  }, [sessionData]);

  // Determine if this is an audio-based session (has recordings or segments)
  const isAudioSession = useMemo(() => {
    if (!sessionData) return true; // Default to true for new sessions until content added
    const hasDocs = (sessionData.companyDocuments?.length ?? 0) > 0;
    const hasAudio =
      (sessionData.recordings?.length ?? 0) > 0 ||
      (sessionData.speakerSegments?.length ?? 0) > 0;

    // Check if any document is actually an audio/video file
    const hasAudioDocument = sessionData.companyDocuments?.some(
      (doc) =>
        doc.documentName?.toLowerCase().endsWith('.webm') ||
        doc.documentName?.toLowerCase().endsWith('.mp3') ||
        doc.documentName?.toLowerCase().endsWith('.wav') ||
        doc.documentName?.toLowerCase().endsWith('.m4a') ||
        doc.documentName?.toLowerCase().endsWith('.mp4')
    );

    if (hasAudio || hasAudioDocument) return true;

    // If we have documents but no audio, it's a document session
    if (hasDocs && !hasAudio) return false;

    // Otherwise default to audio (or empty)
    return true;
  }, [sessionData]);

  // Extract file type extension from session documents
  const fileTypeExtension = useMemo(() => {
    if (!sessionData) return null;

    // Check companyDocuments first (most reliable source)
    const documents = sessionData.companyDocuments;
    if (documents && documents.length > 0) {
      // Try to get extension from the first document
      const firstDoc = documents[0];

      // First try fileFormat (might already be extension without dot)
      if (firstDoc.fileFormat) {
        const ext = firstDoc.fileFormat.trim().toUpperCase();
        return ext.startsWith('.') ? ext : `.${ext}`;
      }

      // Fallback to extracting from documentName
      if (firstDoc.documentName) {
        const match = firstDoc.documentName.match(/\.([a-z0-9]+)$/i);
        if (match && match[1]) {
          return `.${match[1].toUpperCase()}`;
        }
      }
    }

    // If no documents, check if we have recordings (default to audio format)
    if (
      (sessionData.recordings?.length ?? 0) > 0 ||
      (sessionData.speakerSegments?.length ?? 0) > 0
    ) {
      // Default to .WEBM for recordings (most common format for browser recordings)
      return '.WEBM';
    }

    return null;
  }, [sessionData]);

  // Determine if recording controls should be shown based on session state
  useEffect(() => {
    if (sessionData) {
      // Keep controls visible while actively recording or when there are unanswered questions
      const hasUnansweredQuestions =
        analysisComplete &&
        !allQuestionsAnswered &&
        unansweredQuestions.length > 0;

      // Only show recording controls if we are recording OR (no content yet AND no unanswered questions)
      // If content exists, we hide controls to enforce 1-file per session
      const autoShowRecordingControls =
        state.isRecording || (!hasExistingContent && !hasUnansweredQuestions);

      // Default FAB visibility mirrors desktop behavior: hide for existing sessions
      // BUT: keep FAB visible while actively recording or if user has manually shown it
      if (useMobileRecordingControls || shouldUseMobileLayout) {
        const autoShowFab =
          state.isRecording ||
          isStartingRecording ||
          hasStartedRecording ||
          !hasExistingContent;
        const overrideFabShow =
          fabVisibilityOverride === null
            ? false
            : fabVisibilityOverride === 'show';
        const nextFabVisibility = autoShowFab ? true : overrideFabShow;

        if (showFabControls !== nextFabVisibility) {
          setShowFabControls(nextFabVisibility);
        }
      }

      // Respect override: 'hide' always hides, 'show' always shows, null uses auto logic
      const nextShowRecordingControls =
        recordingControlsOverride === 'hide'
          ? false
          : recordingControlsOverride === 'show'
          ? true
          : autoShowRecordingControls;

      if (showRecordingControls !== nextShowRecordingControls) {
        setShowRecordingControls(nextShowRecordingControls);
      }
    } else {
      // If no session data yet, hide controls by default
      // Controls will show when user starts recording (handled by isStartingRecording/hasStartedRecording)
      if (showRecordingControls) {
        setShowRecordingControls(false);
      }
      if (useMobileRecordingControls || shouldUseMobileLayout) {
        // Only show FAB if actively starting/recording
        const shouldShowFab = isStartingRecording || hasStartedRecording;
        if (showFabControls !== shouldShowFab) {
          setShowFabControls(shouldShowFab);
        }
      }
    }
  }, [
    sessionData,
    hasStartedRecording,
    state.isRecording,
    isStartingRecording,
    analysisComplete,
    allQuestionsAnswered,
    unansweredQuestions,
    useMobileRecordingControls,
    shouldUseMobileLayout,
    fabVisibilityOverride,
    showFabControls,
    recordingControlsOverride,
    showRecordingControls,
    hasExistingContent
  ]);

  // Removed: automatic final analysis on recording stop
  // Final analysis is now triggered after successful finalize in stopConversation

  // Optional demo analysis timer disabled by default
  useEffect(() => {
    if (!ENABLE_DEMO_ANALYSIS_TIMER) return;
    if (isAnalyzingTranscript && !analysisComplete) {
      setDismissedCallout(false);
      const timer = setTimeout(() => {
        setIsAnalyzingTranscript(false);
        setAnalysisComplete(true);
        const hasUnanswered = Math.random() > 0.5;
        setAllQuestionsAnswered(!hasUnanswered);
        setUnansweredQuestions(
          hasUnanswered
            ? [
                'What was the primary reason for the arrest?',
                'Were Miranda rights properly administered?',
                'What personal property was collected during booking?'
              ]
            : []
        );
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [isAnalyzingTranscript, analysisComplete]);

  const unansweredQuestionDetails = templateQuestions.filter((question) => {
    const answerText = (question.answer || '').trim();
    return !question.isComplete || answerText.length === 0;
  });
  // Fetch Template Questions
  useEffect(() => {
    const loadTemplateQuestions = async () => {
      // For session-based URLs, use session template questions (with AI answers) instead of original template
      if (isSessionBasedUrl) {
        if (!sessionId || !sessionData) {
          setIsLoadingQuestions(sessionData === null); // Keep loading if sessionData is still loading
          return;
        }

        // Use session template questions if available (these may have AI-filled answers)
        if (
          sessionData.templateQuestions &&
          sessionData.templateQuestions.length > 0
        ) {
          console.log(
            'Loading template questions from session data:',
            sessionData.templateQuestions
          );

          // Fetch the original template to get the proper ordering
          const sessionTemplateId = sessionData.templateId;
          if (sessionTemplateId && appUser?.id) {
            try {
              const fetchedTemplate = await templateService.getTemplateById(
                sessionTemplateId,
                appUser.id,
                getAccessTokenSilently,
                getAccessTokenWithPopup
              );

              if (fetchedTemplate && fetchedTemplate.questions) {
                // Sort original questions by order
                const sortedApiQuestions = (
                  fetchedTemplate.questions || []
                ).sort(
                  (a: Question, b: Question) => (a.order ?? 0) - (b.order ?? 0)
                );

                // Create a map of session answers by question text for merging
                const sessionAnswersMap = new Map();
                sessionData.templateQuestions.forEach((sessionQ) => {
                  sessionAnswersMap.set(
                    sessionQ.normalizedQuestionText,
                    sessionQ
                  );
                });

                // Merge original questions (with order) with session answers
                const orderedQuestionsWithAnswers: TemplateQuestion[] =
                  sortedApiQuestions.map((q: Question) => {
                    // Find corresponding session answer by normalized text matching
                    const sessionAnswer =
                      sessionAnswersMap.get(q.normalizedTitle) ||
                      sessionData.templateQuestions.find(
                        (sq) =>
                          sq.questionText.toLowerCase().trim() ===
                          q.title.toLowerCase().trim()
                      );

                    return {
                      id: sessionAnswer?.id || String(q.id),
                      question: q.title,
                      answer: sessionAnswer?.answerText || '',
                      confidence: sessionAnswer?.confidenceScore || 0,
                      isComplete: sessionAnswer?.isComplete || false,
                      category: sessionAnswer?.category || 'General',
                      required: q.isRequired
                    };
                  });

                applyTemplateQuestions(orderedQuestionsWithAnswers);
                setTemplateName(
                  fetchedTemplate.name ||
                    `Session Template ${sessionTemplateId}`
                );
              } else {
                // Fallback to session questions without ordering if template fetch fails
                const sessionTemplateQuestions =
                  sessionData.templateQuestions.map((tq: any) => ({
                    id: tq.id,
                    question: tq.questionText,
                    answer: tq.answerText || '',
                    confidence: tq.confidenceScore || 0,
                    isComplete: tq.isComplete,
                    category: tq.category,
                    required: tq.isRequired
                  }));
                applyTemplateQuestions(sessionTemplateQuestions);
              }
            } catch (error) {
              console.error('Failed to fetch template for ordering:', error);
              // Fallback to session questions without ordering
              const sessionTemplateQuestions =
                sessionData.templateQuestions.map((tq: any) => ({
                  id: tq.id,
                  question: tq.questionText,
                  answer: tq.answerText || '',
                  confidence: tq.confidenceScore || 0,
                  isComplete: tq.isComplete,
                  category: tq.category,
                  required: tq.isRequired
                }));
              applyTemplateQuestions(sessionTemplateQuestions);
            }
          } else {
            // No template ID available, use session questions as-is
            const sessionTemplateQuestions = sessionData.templateQuestions.map(
              (tq: any) => ({
                id: tq.id,
                question: tq.questionText,
                answer: tq.answerText || '',
                confidence: tq.confidenceScore || 0,
                isComplete: tq.isComplete,
                category: tq.category,
                required: tq.isRequired
              })
            );
            applyTemplateQuestions(sessionTemplateQuestions);
          }
          // Template error handling removed

          // Template name will be loaded separately from the original template
        } else {
          // Fallback: no session template questions yet, but there's a template ID
          const sessionTemplateId = sessionData.templateId;
          if (sessionTemplateId && appUser?.id) {
            console.log(
              'Loading empty template questions from template:',
              sessionTemplateId
            );
            try {
              const fetchedTemplate = await templateService.getTemplateById(
                sessionTemplateId,
                appUser.id,
                getAccessTokenSilently,
                getAccessTokenWithPopup
              );

              if (fetchedTemplate && fetchedTemplate.questions) {
                setTemplateName(
                  fetchedTemplate.name ||
                    `Session Template ${sessionTemplateId}`
                );
                const sortedApiQuestions = (
                  fetchedTemplate.questions || []
                ).sort(
                  (a: Question, b: Question) => (a.order ?? 0) - (b.order ?? 0)
                );

                const uiQuestions: TemplateQuestion[] = sortedApiQuestions.map(
                  (q: Question) => ({
                    id: String(q.id),
                    question: q.title,
                    answer: '',
                    confidence: 0,
                    isComplete: false,
                    category: 'General',
                    required: q.isRequired
                  })
                );
                applyTemplateQuestions(uiQuestions);
                // Template error handling removed
              } else {
                // setTemplateError('Template not found or has no questions.');
                applyTemplateQuestions([]);
              }
            } catch (err: any) {
              console.error(
                'Failed to load template questions from session:',
                err
              );
              // Template error handling removed
              console.error(
                'Template error:',
                err.message || 'Failed to load template questions.'
              );
              applyTemplateQuestions([]);
            }
          } else {
            // Template error handling removed - no template associated with this session
            applyTemplateQuestions([]);
          }
        }
        setIsLoadingQuestions(false);
        return;
      }

      // For legacy template-based URLs
      if (!templateId || !appUser?.id) {
        // Template error handling removed
        console.error(
          !appUser?.id ? 'User not authenticated.' : 'Template ID is missing.'
        );
        setIsLoadingQuestions(false);
        return;
      }
      setIsLoadingQuestions(true);
      // Template error handling removed
      try {
        const fetchedTemplate = await templateService.getTemplateById(
          Number(templateId),
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        if (fetchedTemplate && fetchedTemplate.questions) {
          setTemplateName(
            fetchedTemplate.name || `Session (Template ${templateId})`
          );
          const sortedApiQuestions = (fetchedTemplate.questions || []).sort(
            (a: Question, b: Question) => (a.order ?? 0) - (b.order ?? 0)
          );

          const uiQuestions: TemplateQuestion[] = sortedApiQuestions.map(
            (q: Question) => ({
              id: String(q.id),
              question: q.title,
              answer: '',
              confidence: 0,
              isComplete: false,
              category: 'General',
              required: q.isRequired
            })
          );
          applyTemplateQuestions(uiQuestions);
        } else {
          // setTemplateError('Template not found or has no questions.');
          // setShowTemplateErrorToast(true);
          applyTemplateQuestions([]);
        }
      } catch (err: any) {
        console.error('Failed to load template questions:', err);
        // setTemplateError(err.message || 'Failed to load template questions.');
        // setShowTemplateErrorToast(true);
        applyTemplateQuestions([]);
      } finally {
        setIsLoadingQuestions(false);
      }
    };

    if (appUser) {
      loadTemplateQuestions();
    } else {
      setIsLoadingQuestions(false);
    }
  }, [
    templateId,
    appUser,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    isSessionBasedUrl,
    sessionId,
    sessionData?.templateId, // Only react to template ID changes, not all sessionData changes
    applyTemplateQuestions
  ]);

  const updateTemplateAnswers = (_text: string) => {
    // Template answers are now handled by the Semantic Kernel agent
    // This function is kept for backward compatibility but doesn't do anything
    // All updates come from the agent processing results
  };

  const pickFirst = <T,>(
    ...values: Array<T | null | undefined>
  ): T | undefined => {
    for (const value of values) {
      if (value !== undefined && value !== null) return value;
    }
    return undefined;
  };

  const handleLiveEvent = useCallback(
    (event: TranscriptionLiveEvent) => {
      switch (event.event) {
        case 'segment-added': {
          const payload = event.payload as LiveSegmentEventPayload | undefined;
          if (!payload) return;
          const resolveTimestamp = (value?: string) => {
            if (!value) return new Date().toISOString();
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime())
              ? new Date().toISOString()
              : parsed.toISOString();
          };
          const payloadId =
            pickFirst<string>(
              payload.id,
              (payload as any).Id,
              (payload as any).speakerSegmentId,
              (payload as any).SpeakerSegmentId
            ) || `live-segment-${Date.now()}`;
          const payloadTimestamp = pickFirst<string>(
            payload.timestamp,
            (payload as any).timestamp,
            (payload as any).Timestamp
          );
          const speakerIndex =
            pickFirst<number>(
              payload.speakerIndex,
              (payload as any).speaker,
              (payload as any).SpeakerIndex
            ) ?? 0;
          const textContent =
            pickFirst<string>(
              payload.textContent,
              (payload as any).text,
              (payload as any).TextContent,
              (payload as any).transcript
            ) || '';
          setSpeakers((prev) => {
            const alreadyExists = prev.some(
              (row: any) => row?.id === payloadId
            );
            if (alreadyExists) {
              return prev;
            }
            const newSegment: SpeakerSegment = {
              id: payloadId,
              speaker: speakerIndex,
              text: textContent,
              timestamp: resolveTimestamp(payloadTimestamp),
              confidence: payload.confidenceScore,
              duration: payload.durationSeconds,
              isPauseSegment: payload.isPauseSegment
            };
            return [...prev, newSegment];
          });
          if (!payload.isPauseSegment && textContent) {
            setState((prev) => {
              const addition = `Speaker ${speakerIndex}: ${textContent}`;
              const transcript = prev.transcript
                ? `${prev.transcript}\n${addition}`
                : addition;
              return {
                ...prev,
                transcript
              };
            });
          }
          lastSegmentIdRef.current = payloadId;
          break;
        }
        case 'recording-state': {
          const payload = event.payload as RecordingStatePayload | undefined;
          if (!payload) return;
          const isRecording =
            pickFirst<boolean>(
              payload.isRecording,
              (payload as any).isRecording,
              (payload as any).IsRecording
            ) ?? false;
          const isPaused =
            pickFirst<boolean>(
              payload.isPaused,
              (payload as any).isPaused,
              (payload as any).IsPaused
            ) ?? false;
          const interim =
            pickFirst<string>(
              payload.interimTranscript,
              (payload as any).interimTranscript,
              (payload as any).InterimTranscript
            ) ?? null;
          const audioLevel =
            pickFirst<number>(
              payload.audioLevel,
              (payload as any).audioLevel,
              (payload as any).AudioLevel
            ) ?? state.audioLevel;
          const accumulatedMs = pickFirst<number>(
            payload.accumulatedMilliseconds,
            (payload as any).accumulatedMilliseconds,
            (payload as any).AccumulatedMilliseconds
          );
          setState((prev) => ({
            ...prev,
            isRecording,
            isPaused,
            interimTranscript: interim ?? prev.interimTranscript,
            audioLevel
          }));
          if (typeof accumulatedMs === 'number') {
            setRecordingDuration(accumulatedMs);
          }
          setSessionData((prev) => {
            if (!prev) return prev;
            const targetStatus = isRecording
              ? TranscriptionSessionStatus.Recording
              : prev.status === TranscriptionSessionStatus.Recording
              ? TranscriptionSessionStatus.Processing
              : prev.status;
            if (targetStatus === prev.status) {
              return prev;
            }
            return { ...prev, status: targetStatus };
          });
          break;
        }
        case 'recording-divider': {
          const payload = event.payload as RecordingDividerPayload | undefined;
          if (!payload) return;
          const recordingId =
            pickFirst<string>(
              payload.recordingId,
              (payload as any).recordingId,
              (payload as any).RecordingId
            ) || `divider-${Date.now()}`;
          const startValue = pickFirst<string>(
            payload.startedAtUtc,
            (payload as any).startedAtUtc,
            (payload as any).startTimeUtc,
            (payload as any).StartTimeUtc
          );
          setSpeakers((prev) => [
            ...prev,
            {
              id: `rec-divider-${recordingId}`,
              kind: 'recording-divider',
              startTimeUtc: startValue || new Date().toISOString()
            } as RecordingDividerSegment
          ]);
          break;
        }
        case 'recording-stopped': {
          setState((prev) => ({
            ...prev,
            isRecording: false,
            isPaused: false,
            audioLevel: 0
          }));
          setSessionData((prev) =>
            prev && prev.status === TranscriptionSessionStatus.Recording
              ? { ...prev, status: TranscriptionSessionStatus.Processing }
              : prev
          );
          break;
        }
        default:
          break;
      }
    },
    [setSpeakers, setState, setSessionData, setRecordingDuration]
  );

  // Setup audio monitoring (unchanged)
  const setupAudioMonitoring = (stream: MediaStream) => {
    try {
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)({ sampleRate: PCM_SAMPLE_RATE });
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);
      const dataArray = new Uint8Array(
        analyser.frequencyBinCount
      ) as Uint8Array<ArrayBuffer>;
      audioDataRef.current = dataArray;
      scriptProcessor.onaudioprocess = (e) => {
        if (!analyserRef.current || !audioDataRef.current) return;
        analyserRef.current.getByteFrequencyData(audioDataRef.current);
        let sum = 0;
        for (let i = 0; i < audioDataRef.current.length; i++) {
          sum += audioDataRef.current[i];
        }
        const average = sum / audioDataRef.current.length;
        const level = Math.min(100, Math.round(average * 2));
        if (
          !pausedRef.current &&
          deepgramLiveRef.current &&
          deepgramLiveRef.current.send
        ) {
          const inputBuffer = e.inputBuffer;
          const leftChannel = inputBuffer.getChannelData(0);
          const pcmData = new Int16Array(leftChannel.length);
          for (let i = 0; i < leftChannel.length; i++) {
            const s = Math.max(-1, Math.min(1, leftChannel[i]));
            pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }
          deepgramLiveRef.current.send(pcmData.buffer);
        }
        if (Math.abs(level - state.audioLevel) > 3) {
          setState((prev) => ({ ...prev, audioLevel: level }));
        }
      };
    } catch (error) {
      console.error('Error setting up audio monitoring:', error);
    }
  };
  // Speaker Management Effects
  // Load available people when component mounts
  useEffect(() => {
    const loadPeople = async () => {
      if (!selectedUserRole?.companyId) {
        console.warn('No company selected, skipping people load');
        return;
      }

      try {
        const people = await personService.getAllPeople({
          companyId: selectedUserRole.companyId,
          pageNumber: 1,
          pageSize: 100,
          sortBy: 'displayName',
          sortAscending: true
        });
        setAvailablePeople(people);
      } catch (error) {
        console.error('Failed to load people:', error);
      }
    };

    loadPeople();
  }, [personService, selectedUserRole?.companyId]);

  // Load speaker mapping when session changes
  useEffect(() => {
    const loadSpeakerMapping = async () => {
      if (!sessionId) return;

      try {
        const mapping = await speakerService.getSessionSpeakers(sessionId);
        setSpeakerMapping(mapping);
        setPreviousAssignments(mapping.speakerAssignments);
      } catch (error) {
        console.error('Failed to load speaker mapping:', error);
      }
    };

    loadSpeakerMapping();
  }, [sessionId, speakerService]);

  // Handle speaker identity confirmation when connection is reestablished
  useEffect(() => {
    // Simulate scenario where we need to confirm speaker identities
    // This would typically be triggered by a new WebSocket connection
    // For now, we'll check if we have recent segments without confirmed speakers

    if (
      speakers.length > 0 &&
      speakerMapping &&
      state.connectionStatus === 'Connected'
    ) {
      const recentSegments = speakers
        .filter((s: any) => !(s as any).kind)
        .slice(-3) as SpeakerSegment[]; // Last 3 speaker segments
      const uniqueSpeakers = Array.from(
        new Set(recentSegments.map((s) => s.speaker))
      );

      // Check if any speakers don't have confirmed assignments
      const unconfirmedSpeakers = uniqueSpeakers.filter((speakerIndex) => {
        const assignment = speakerMapping.speakerAssignments.find(
          (a) => a.speakerIndex === speakerIndex
        );
        return !assignment || !assignment.personId;
      });

      if (
        AUTO_OPEN_SPEAKER_IDENTITY_DIALOG &&
        unconfirmedSpeakers.length > 0 &&
        !showSpeakerDialog
      ) {
        setCurrentSegments(
          recentSegments.map((s) => ({
            speakerIndex: s.speaker,
            text: s.text,
            timestamp: s.timestamp
          }))
        );

        // Only show dialog if we have previous assignments to suggest
        if (previousAssignments.length > 0) {
          setShowSpeakerDialog(true);
        }
      }
    }
  }, [
    speakers,
    speakerMapping,
    state.connectionStatus,
    showSpeakerDialog,
    previousAssignments
  ]);

  // Load requiresApproval flag from session data
  useEffect(() => {
    if (sessionData) {
      setRequiresApproval(sessionData.requiresApproval || false);
    }
  }, [sessionData]);

  // Job subscription effect - subscribe to job updates when session is processing or jobs are active
  useEffect(() => {
    if (!sessionId || !appUser?.id) return;
    let cancelled = false;
    let cleanup: (() => void) | null = null;

    const setupJobSubscription = async () => {
      try {
        // 1. Get token and subscribe FIRST to avoid missing events during fetch
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
          }
        });
        if (cancelled) return;

        cleanup = subscribeToSessionJobs(
          sessionId,
          appUser.id,
          token,
          (update: SessionJobUpdate) => {
            setSessionJobs((prevJobs) => {
              const updatedJobs = [...prevJobs];
              const jobIndex = updatedJobs.findIndex(
                (job) => job.id === update.job.id
              );

              if (update.event === 'jobAdded' && jobIndex === -1) {
                updatedJobs.push(update.job);
              } else if (update.event === 'jobUpdated' && jobIndex >= 0) {
                updatedJobs[jobIndex] = update.job;
              }

              // Re-evaluate running job status on every update
              const runningJob = updatedJobs
                .filter(
                  (job) =>
                    (job.status as any) === 1 ||
                    String(job.status).toLowerCase() === 'running'
                )
                .sort((a, b) => {
                  const prio = (t: any) => {
                    const n = getJobTypeName(t);
                    if (n === 'Upload') return 0;
                    if (n === 'Transcribe') return 1;
                    if (n === 'Process') return 2;
                    if (n === 'Analyze') return 3;
                    if (n === 'Summarize') return 4;
                    return -1;
                  };
                  return prio(a.jobType) - prio(b.jobType);
                })
                .pop();

              if (runningJob) {
                const runningName = getJobTypeName(runningJob.jobType);
                setCurrentProcessingStage(runningName);
                setDismissedCallout(false);

                if (runningName === 'Upload') {
                  setPipelineStage('uploading');
                  if ((runningJob as any)?.progressPercent != null) {
                    setUploadProgress(
                      (runningJob as any).progressPercent as any
                    );
                  }
                } else if (runningName === 'Transcribe') {
                  setPipelineStage('transcribing');
                  if ((runningJob as any)?.progressPercent != null) {
                    setTranscribingProgress(
                      (runningJob as any).progressPercent as any
                    );
                  }
                } else if (
                  runningName === 'Analyze' ||
                  runningName === 'Process'
                ) {
                  setPipelineStage('analyzing');
                  if ((runningJob as any)?.progressPercent != null) {
                    setAnalyzingProgress(
                      (runningJob as any).progressPercent as any
                    );
                  }
                }
              } else {
                // No running jobs - set to complete to avoid showing processing callout
                setCurrentProcessingStage('');
                setPipelineStage('complete');
              }

              // Handle specific job completion events
              const updateJobTypeName = getJobTypeName(update.job.jobType);

              if (updateJobTypeName === 'Transcribe') {
                if (isJobSucceeded(update.job.status)) {
                  refreshTranscriptFromServer();
                } else if (
                  isJobFailed(update.job.status) ||
                  isJobCancelled(update.job.status)
                ) {
                  setPipelineStage('error');
                  setCurrentProcessingStage('');
                }
              }

              if (updateJobTypeName === 'Analyze') {
                if (isJobRunning(update.job.status)) {
                  setIsAnalyzingTranscript(true);
                  setPipelineStage('analyzing');
                  setAnalyzeSubstage('questions');
                  setDismissedCallout(false);
                }
                if (isJobSucceeded(update.job.status)) {
                  refreshTemplateQuestionsFromSession({ showDialog: true })
                    .then(
                      (result) =>
                        new Promise((resolve) =>
                          setTimeout(() => resolve(result), 100)
                        )
                    )
                    .then(() => {
                      setAnalysisComplete(true);
                      setAnalysisEnded(true);
                      setIsAnalyzingTranscript(false);
                      setDismissedCallout(false);
                      setSortUnansweredFirst(true);
                    })
                    .catch(() => {
                      setAnalysisComplete(true);
                      setAnalysisEnded(true);
                      setIsAnalyzingTranscript(false);
                    });
                }
                if (
                  isJobFailed(update.job.status) ||
                  isJobCancelled(update.job.status)
                ) {
                  setIsAnalyzingTranscript(false);
                  setAnalyzeSubstage('error');
                  setPipelineStage('complete');
                }
              }

              if (updateJobTypeName === 'Summarize') {
                if (isJobRunning(update.job.status)) {
                  setIsSummariesLoading(true);
                  setIsSummariesGenerating(true);
                  // Also update callout to show "Summarizing..." if it's still active
                  setAnalyzeSubstage('summaries');
                  setPipelineStage('analyzing');
                }
                if (isJobSucceeded(update.job.status)) {
                  loadSessionSummaries()
                    .then(() => {
                      setIsSummariesGenerating(false);
                      setIsSummariesLoading(false);
                      setAnalyzeSubstage('complete');
                      setPipelineStage('complete');
                      return getTranscriptionSessionById(
                        sessionId,
                        appUser.id,
                        getAccessTokenSilently,
                        getAccessTokenWithPopup
                      );
                    })
                    .then((refreshed) => {
                      if (refreshed) setSessionData(refreshed);
                    })
                    .catch(() => {
                      setIsSummariesGenerating(false);
                      setIsSummariesLoading(false);
                    });
                }
                if (
                  isJobFailed(update.job.status) ||
                  isJobCancelled(update.job.status)
                ) {
                  setIsSummariesGenerating(false);
                  setIsSummariesLoading(false);
                  setAnalyzeSubstage('error');
                }
              }

              // Check if ALL jobs are complete
              const allCompleted = updatedJobs.every((job) => {
                const s = String(job.status).toLowerCase();
                return (
                  (job.status as any) === 2 ||
                  (job.status as any) === 3 ||
                  (job.status as any) === 4 ||
                  s === 'succeeded' ||
                  s === 'failed' ||
                  s === 'cancelled'
                );
              });

              if (allCompleted && updatedJobs.length > 0) {
                setCurrentProcessingStage('');
                setPipelineStage('complete');
                setAnalyzeSubstage('complete');

                // Final refresh
                getTranscriptionSessionById(
                  sessionId,
                  appUser.id,
                  getAccessTokenSilently,
                  getAccessTokenWithPopup
                )
                  .then((refreshedSession) => {
                    if (refreshedSession) {
                      setSessionData(refreshedSession);
                      hydrateTranscriptFromSession(refreshedSession);
                    }
                  })
                  .catch(() => {});
              }

              return updatedJobs;
            });
          },
          (error: Error) => {
            console.error('❌ Job subscription error:', {
              error: error.message,
              sessionId,
              userId: appUser.id
            });
          }
        );
        setJobSubscription(() => cleanup);

        // 2. Fetch initial state (after subscribing to catch anything new)
        const jobs = await getSessionJobs(
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        if (cancelled) return;

        // 3. Merge initial state with any events received during fetch
        let mergedJobs: TranscriptionSessionJob[] = [];
        setSessionJobs((prevJobs) => {
          const merged = [...jobs];
          const dbJobIds = new Set(jobs.map((j) => j.id));
          prevJobs.forEach((pJob) => {
            if (!dbJobIds.has(pJob.id)) {
              merged.push(pJob);
            }
          });
          mergedJobs = merged; // Capture for processing
          return merged;
        });

        setSessionJobsInitialized(true);

        // 4. Process initial state
        const runningJob = mergedJobs
          .filter(
            (job) =>
              (job.status as any) === 1 ||
              String(job.status).toLowerCase() === 'running'
          )
          .sort((a, b) => {
            const prio = (t: any) => {
              const n = getJobTypeName(t);
              if (n === 'Upload') return 0;
              if (n === 'Transcribe') return 1;
              if (n === 'Process') return 2;
              if (n === 'Analyze') return 3;
              if (n === 'Summarize') return 4;
              return -1;
            };
            return prio(a.jobType) - prio(b.jobType);
          })
          .pop();

        if (runningJob) {
          const runningName = getJobTypeName(runningJob.jobType);
          setCurrentProcessingStage(runningName);
          setDismissedCallout(false);

          if (runningName === 'Upload') {
            setPipelineStage('uploading');
            if ((runningJob as any)?.progressPercent != null) {
              setUploadProgress((runningJob as any).progressPercent as any);
            }
          } else if (runningName === 'Transcribe') {
            setPipelineStage('transcribing');
            if ((runningJob as any)?.progressPercent != null) {
              setTranscribingProgress(
                (runningJob as any).progressPercent as any
              );
            }
          } else if (runningName === 'Analyze' || runningName === 'Process') {
            setPipelineStage('analyzing');
            if ((runningJob as any)?.progressPercent != null) {
              setAnalyzingProgress((runningJob as any).progressPercent as any);
            }
          }
        } else {
          // No running jobs - set to complete to avoid showing processing callout
          setCurrentProcessingStage('');
          setPipelineStage('complete');
        }
      } catch (error) {
        console.error('Failed to set up job subscription:', error);
      }
    };

    setupJobSubscription();

    return () => {
      cancelled = true;
      if (cleanup) {
        cleanup();
        setJobSubscription(null);
      }
    };
  }, [
    sessionId,
    appUser?.id,
    isSessionProcessing,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    hydrateTranscriptFromSession
  ]);

  // Effect to clear upload query params if we are just viewing an uploaded session
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('source') || params.has('upload')) {
      params.delete('source');
      params.delete('upload');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ''
        },
        { replace: true }
      );
    }
  }, [location.search, location.pathname, navigate]);

  // Handle document processing flow for non-audio uploads - logic removed as backend handles it

  useEffect(() => {
    if (!sessionId || !appUser?.id) return;
    if (sessionJobs.length === 0) return;

    const transcribeJob = sessionJobs.find(
      (job) => getJobTypeName(job.jobType) === 'Transcribe'
    );
    if (
      transcribeJob &&
      isJobSucceeded(transcribeJob.status) &&
      transcribeJobHandledRef.current !== transcribeJob.id
    ) {
      transcribeJobHandledRef.current = transcribeJob.id;
      refreshTranscriptFromServer();
    }
  }, [sessionJobs, sessionId, appUser?.id, refreshTranscriptFromServer]);

  useEffect(() => {
    if (!sessionId || !appUser?.id) return;
    if (sessionJobs.length === 0) return;

    const analyzeJob = sessionJobs.find(
      (job) => getJobTypeName(job.jobType) === 'Analyze'
    );
    if (!analyzeJob) return;

    if (isJobRunning(analyzeJob.status)) {
      setIsAnalyzingTranscript(true);
      setPipelineStage('analyzing');
      setAnalyzeSubstage('questions');
      setDismissedCallout(false);
    }

    if (
      isJobSucceeded(analyzeJob.status) &&
      analyzeJobHandledRef.current !== analyzeJob.id
    ) {
      analyzeJobHandledRef.current = analyzeJob.id;
      console.log(
        '[UploadBatch] Question filling job succeeded, refreshing questions...'
      );
      // Refresh questions THEN mark analysis complete (popup shows via useEffect)
      refreshTemplateQuestionsFromSession({ showDialog: true })
        .then((result) => {
          console.log('[UploadBatch] Questions refreshed:', result);
          // Wait for next tick to ensure React has processed the state updates
          return new Promise((resolve) =>
            setTimeout(() => resolve(result), 100)
          );
        })
        .then((result) => {
          setAnalysisComplete(true);
          setAnalysisEnded(true);
          setIsAnalyzingTranscript(false);
          setDismissedCallout(false);
          setSortUnansweredFirst(true);
          console.log(
            '[UploadBatch] Analysis marked complete, questions:',
            result
          );
        })
        .catch((error) => {
          console.error('[UploadBatch] Failed to refresh questions:', error);
          // Still mark complete even on error
          setAnalysisComplete(true);
          setAnalysisEnded(true);
          setIsAnalyzingTranscript(false);
        });
    }

    if (
      (isJobFailed(analyzeJob.status) || isJobCancelled(analyzeJob.status)) &&
      analyzeJobHandledRef.current !== `${analyzeJob.id}:failed`
    ) {
      analyzeJobHandledRef.current = `${analyzeJob.id}:failed`;
      setIsAnalyzingTranscript(false);
      setAnalyzeSubstage('error');
      setPipelineStage('complete');
    }
  }, [
    sessionJobs,
    sessionId,
    appUser?.id,
    refreshTemplateQuestionsFromSession
  ]);

  useEffect(() => {
    if (!sessionId || !appUser?.id) return;
    if (sessionJobs.length === 0) return;

    const summaryJob = sessionJobs.find(
      (job) => getJobTypeName(job.jobType) === 'Summarize'
    );
    if (!summaryJob) return;

    if (isJobRunning(summaryJob.status)) {
      setIsSummariesLoading(true);
      setIsSummariesGenerating(true);
      // Don't touch analysisComplete or pipeline - summaries run in background
    }

    if (
      isJobSucceeded(summaryJob.status) &&
      summarizeJobHandledRef.current !== summaryJob.id
    ) {
      summarizeJobHandledRef.current = summaryJob.id;
      console.log(
        '[UploadBatch] Summary job succeeded, reloading summaries...'
      );

      // Just reload summaries - don't touch analysisComplete (already set by question fill)
      setIsSummariesLoading(true);
      loadSessionSummaries()
        .then(() => {
          console.log('[UploadBatch] Summaries reloaded');
          setIsSummariesGenerating(false);
          setIsSummariesLoading(false);
          return getTranscriptionSessionById(
            sessionId,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        })
        .then((refreshed) => {
          if (refreshed) {
            setSessionData(refreshed);
          }
        })
        .catch((error) => {
          console.error('[UploadBatch] Failed to reload summaries', error);
          setIsSummariesGenerating(false);
          setIsSummariesLoading(false);
        });
    }

    if (
      (isJobFailed(summaryJob.status) || isJobCancelled(summaryJob.status)) &&
      summarizeJobHandledRef.current !== `${summaryJob.id}:failed`
    ) {
      summarizeJobHandledRef.current = `${summaryJob.id}:failed`;
      setIsSummariesGenerating(false);
      setIsSummariesLoading(false);
    }
  }, [
    sessionJobs,
    sessionId,
    appUser?.id,
    loadSessionSummaries,
    getTranscriptionSessionById,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    applyTemplateQuestions,
    shouldUseMobileLayout
  ]);

  useEffect(() => {
    if (!effectiveSessionId || !appUser?.id || isSessionOwner) {
      if (liveSubscriptionRef.current) {
        liveSubscriptionRef.current();
        liveSubscriptionRef.current = null;
      }
      return;
    }

    let cancelled = false;
    let cleanup: (() => void) | null = null;

    (async () => {
      try {
        const token = await getLiveAccessToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        if (cancelled) return;
        cleanup = subscribeToLiveStream(
          effectiveSessionId,
          appUser.id,
          token,
          handleLiveEvent,
          (error) => {
            console.error('Transcription live stream error:', error?.message);
            catchUpSegments('sse-error');
          },
          () => catchUpSegments('sse-open')
        );
        liveSubscriptionRef.current = cleanup;
      } catch (error) {
        console.error('Failed to subscribe to live stream:', error);
      }
    })();

    return () => {
      cancelled = true;
      if (cleanup) {
        cleanup();
      }
      if (liveSubscriptionRef.current === cleanup) {
        liveSubscriptionRef.current = null;
      }
    };
  }, [
    effectiveSessionId,
    appUser?.id,
    isSessionOwner,
    handleLiveEvent,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    catchUpSegments
  ]);

  // Approval toggle handler
  const handleToggleRequiresApproval = async () => {
    if (!sessionId || !appUser?.id) return;

    const newRequiresApproval = !requiresApproval;

    // Optimistically update the UI immediately
    setRequiresApproval(newRequiresApproval);

    // Also update the sessionData to reflect the change immediately
    if (sessionData) {
      setSessionData({
        ...sessionData,
        requiresApproval: newRequiresApproval
      });
    }

    try {
      // Update the session entity in the database (async, no waiting)
      await updateTranscriptionSession(
        sessionId,
        {
          id: sessionId,
          requiresApproval: newRequiresApproval
        },
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
    } catch (error) {
      console.error('Failed to toggle approval requirement:', error);

      // Revert the optimistic update on error
      setRequiresApproval(!newRequiresApproval);
      if (sessionData) {
        setSessionData({
          ...sessionData,
          requiresApproval: !newRequiresApproval
        });
      }
    }
  };

  // Rename handler
  const handleOpenRenameDialog = () => {
    if (!sessionData) return;
    setMenuAnchorEl(null);
    setRenameValue(sessionData.title || 'Untitled Session');
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    if (!sessionId || !appUser || !renameValue.trim()) return;

    const trimmedTitle = renameValue.trim();

    // Optimistically update the UI
    if (sessionData) {
      setSessionData({
        ...sessionData,
        title: trimmedTitle
      });
    }

    try {
      await updateTranscriptionSession(
        sessionId,
        {
          id: sessionId,
          title: trimmedTitle
        },
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setRenameDialogOpen(false);
    } catch (error) {
      console.error('Failed to rename session:', error);
      // Revert on error
      if (sessionData) {
        setSessionData({
          ...sessionData,
          title: sessionData.title
        });
      }
    }
  };

  // Delete handler
  const handleOpenDeleteDialog = () => {
    setMenuAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSession = async () => {
    if (!sessionId || !appUser) return;

    setIsDeletingSession(true);
    try {
      const success = await deleteTranscriptionSession(
        sessionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      if (success) {
        // Navigate away from the deleted session
        navigate('/records');
      } else {
        console.error('Failed to delete session');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    } finally {
      setIsDeletingSession(false);
      setDeleteDialogOpen(false);
    }
  };

  // Move to Folder handlers
  const handleOpenMoveToCollectionDialog = async () => {
    if (!sessionId || !appUser || !selectedUserRole) return;

    setMenuAnchorEl(null);

    try {
      // Fetch available collections
      const collections = await getCollectionsByCompany(
        selectedUserRole.companyId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setAvailableCollections(collections);

      // Set current collection if session is in one
      if (sessionCollections.length > 0) {
        setSelectedCollectionId(sessionCollections[0].id);
      } else {
        setSelectedCollectionId('');
      }

      setMoveToCollectionDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  };

  const handleMoveToCollection = async () => {
    if (!sessionId || !appUser) return;

    try {
      const currentCollectionId =
        sessionCollections.length > 0 ? sessionCollections[0].id : null;

      // If the selected collection is the same as the current one, do nothing
      if (currentCollectionId === selectedCollectionId) {
        setMoveToCollectionDialogOpen(false);
        return;
      }

      // Remove from current collection if exists and different from selected
      if (currentCollectionId && currentCollectionId !== selectedCollectionId) {
        await removeSessionFromCollection(
          currentCollectionId,
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      }

      // Add to new collection if one is selected and different from current
      if (
        selectedCollectionId &&
        selectedCollectionId !== currentCollectionId
      ) {
        await addSessionToCollection(
          selectedCollectionId,
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      }

      // Refresh collections for this session
      await reloadSessionCollections();

      setMoveToCollectionDialogOpen(false);
    } catch (error) {
      console.error('Failed to move session:', error);
    }
  };

  // Download handlers
  // Note: Audio and Summaries download removed as they are not yet implemented
  // Start conversation with real session creation
  const startConversation = async () => {
    if (!isSessionOwner) {
      console.warn('View-only mode: cannot start recording');
      return;
    }
    const batchMode =
      (import.meta.env.VITE_TRANSCRIBE_MODE || 'batch')
        .toString()
        .toLowerCase() === 'batch';
    try {
      const isNewSession = !sessionId || speakers.length === 0;

      // Enter preparing state, but do not show "Recording" yet
      setIsStartingRecording(true);
      // Clear stale recording ID and pending segments to prevent using previous recording's ID
      recordingIdRef.current = null;
      pendingSegmentsRef.current = [];

      setState((prev) => ({
        ...prev,
        isPaused: false,
        error: null,
        // Only clear transcript for truly new sessions
        transcript: isNewSession ? '' : prev.transcript,
        interimTranscript: '',
        connectionStatus: 'Connecting...',
        // Only clear debug messages for new sessions
        debugMessages: isNewSession ? [] : prev.debugMessages,
        audioLevel: 0
      }));

      // We'll set sessionStartTime after backend recording is created
      pausedRef.current = false;
      // We'll set hasStartedRecording after mic + backend recording are ready
      setHasAnalyzed(false); // Reset analysis flag for new recording

      // Reset analysis completion states when starting new recording
      setAnalysisComplete(false);
      setIsAnalyzingTranscript(false);
      setAnalysisEnded(false);
      setDismissedCallout(true); // Dismiss any existing callouts
      // Clear persisted dismissal state when starting new recording
      if (sessionId) {
        localStorage.removeItem(`intakeai_callout_dismissed_${sessionId}`);
      }
      setPipelineStage('idle');
      setAnalyzeSubstage('idle');

      lastProcessingTimeRef.current = Date.now();

      // Only clear speakers and insights for truly new sessions
      if (isNewSession) {
        setSpeakers([]);
        // Insights no longer stored in state
      } else {
        // For existing sessions, reload data from backend to ensure consistency
        try {
          console.log(
            'Reloading existing session data for session:',
            sessionId
          );
          const sessionData = await getTranscriptionSessionById(
            sessionId!,
            appUser!.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );

          if (sessionData?.speakerSegments || sessionData?.recordings) {
            const combinedRows: TranscriptRow[] = [];
            const timeline: Array<{
              t: number;
              kind: 'segment' | 'divider';
              row: any;
            }> = [];
            if (sessionData.speakerSegments?.length) {
              for (const seg of sessionData.speakerSegments) {
                const isPause = (seg as any).isPauseSegment || false;
                timeline.push({
                  t: new Date(seg.timestamp).getTime(),
                  kind: 'segment',
                  row: {
                    id: seg.id,
                    speaker: seg.speakerIndex,
                    text: seg.textContent,
                    timestamp: new Date(seg.timestamp).toISOString(),
                    confidence: seg.confidenceScore || 1.0,
                    duration: seg.durationSeconds || 0,
                    isPauseSegment: isPause
                  }
                });
              }
            }
            if (sessionData.recordings?.length) {
              for (const rec of sessionData.recordings) {
                timeline.push({
                  t: new Date(rec.recordingStartTimeUtc).getTime(),
                  kind: 'divider',
                  row: {
                    id: `rec-divider-${rec.id}`,
                    kind: 'recording-divider',
                    startTimeUtc: rec.recordingStartTimeUtc
                  }
                });
              }
            }
            if (timeline.length) {
              timeline.sort((a, b) =>
                a.t !== b.t
                  ? a.t - b.t
                  : a.kind === 'divider' && b.kind === 'segment'
                  ? -1
                  : 1
              );
              for (const item of timeline) combinedRows.push(item.row);
              setSpeakers(combinedRows);
            } else {
              setSpeakers([]);
            }
          }

          // Load AI insights from backend
          if (sessionData?.aiInsights) {
            // Insights no longer stored in state - handled by chat interface
            console.log(
              'AI insights available:',
              sessionData.aiInsights.length
            );
          }

          // Load template questions/answers from backend (maintaining proper order)
          if (
            sessionData?.templateQuestions &&
            sessionData.templateId &&
            appUser?.id
          ) {
            try {
              // Fetch the original template to get the proper ordering
              const fetchedTemplate = await templateService.getTemplateById(
                sessionData.templateId,
                appUser.id,
                getAccessTokenSilently,
                getAccessTokenWithPopup
              );

              if (fetchedTemplate && fetchedTemplate.questions) {
                // Sort original questions by order
                const sortedApiQuestions = (
                  fetchedTemplate.questions || []
                ).sort(
                  (a: Question, b: Question) => (a.order ?? 0) - (b.order ?? 0)
                );

                // Create a map of session answers by question text for merging
                const sessionAnswersMap = new Map();
                sessionData.templateQuestions.forEach((sessionQ: any) => {
                  sessionAnswersMap.set(
                    sessionQ.normalizedQuestionText,
                    sessionQ
                  );
                });

                // Merge original questions (with order) with session answers
                const orderedQuestionsWithAnswers = sortedApiQuestions.map(
                  (q: Question) => {
                    // Find corresponding session answer by normalized text matching
                    const sessionAnswer =
                      sessionAnswersMap.get(q.normalizedTitle) ||
                      sessionData.templateQuestions.find(
                        (sq: any) =>
                          sq.questionText.toLowerCase().trim() ===
                          q.title.toLowerCase().trim()
                      );

                    return {
                      id: sessionAnswer?.id || String(q.id),
                      question: q.title,
                      answer: sessionAnswer?.answerText || '',

                      confidence: sessionAnswer?.confidenceScore || 0,
                      isComplete: sessionAnswer?.isComplete || false,
                      category: sessionAnswer?.category || 'General',
                      required: q.isRequired
                    };
                  }
                );

                applyTemplateQuestions(orderedQuestionsWithAnswers);
              } else {
                // Fallback to session questions without ordering if template fetch fails
                const frontendQuestions = sessionData.templateQuestions.map(
                  (tq: any) => ({
                    id: tq.id,
                    question: tq.questionText,
                    answer: tq.answerText || '',
                    aiThoughtProcess: tq.isComplete
                      ? 'Previously answered'
                      : 'Waiting for input...',
                    confidence: tq.confidenceScore || 0,
                    isComplete: tq.isComplete,
                    category: tq.category,
                    required: tq.isRequired
                  })
                );
                applyTemplateQuestions(frontendQuestions);
              }
            } catch (error) {
              console.error(
                'Failed to fetch template for ordering in startConversation:',
                error
              );
              // Fallback to session questions without ordering
              const frontendQuestions = sessionData.templateQuestions.map(
                (tq: any) => ({
                  id: tq.id,
                  question: tq.questionText,
                  answer: tq.answerText || '',
                  confidence: tq.confidenceScore || 0,
                  isComplete: tq.isComplete,
                  category: tq.category,
                  required: tq.isRequired
                })
              );
              applyTemplateQuestions(frontendQuestions);
            }
          }
          console.log('Session data reloaded successfully');
        } catch (error) {
          console.warn('Failed to reload session data:', error);
          // Continue anyway - user can still record new data
        }
      }

      // Use existing session (should already be created via URL handling)
      let currentSessionId = sessionId;

      // Only create a session if we don't have one (fallback for edge cases)
      if (!currentSessionId && appUser?.id) {
        console.warn(
          'No session ID available during start - this should have been handled by URL routing'
        );
        try {
          const session = await createTranscriptionSession(
            {
              title: templateName || 'Live Transcription Session',
              templateId: templateId ? Number(templateId) : undefined,
              sessionStartTime: new Date().toISOString()
            },
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
          const newSessionId = session?.id;
          if (newSessionId) {
            setSessionId(newSessionId);
            currentSessionId = newSessionId;
            console.log('Created fallback session:', newSessionId);

            // For new sessions, update URL to session-based format
            if (isSessionBasedUrl === false) {
              navigate(`/transcript/${newSessionId}`, { replace: true });
            }
          } else {
            console.error('Failed to create fallback session - no ID returned');
            setState((prev) => ({
              ...prev,
              isRecording: false,
              error: 'Failed to create session',
              connectionStatus: 'Error'
            }));
            return;
          }
        } catch (error) {
          console.error('Failed to create fallback session:', error);
          setState((prev) => ({
            ...prev,
            isRecording: false,
            error: 'Failed to create session',
            connectionStatus: 'Error'
          }));
          return;
        }
      } else if (currentSessionId) {
        console.log('Continuing with existing session:', currentSessionId);
      }

      if (!currentSessionId) {
        console.error('No session ID available, cannot start recording');
        setState((prev) => ({
          ...prev,
          isRecording: false,
          error: 'No session ID available',
          connectionStatus: 'Error'
        }));
        return;
      }

      // Always start Deepgram live for streaming UX, even in batch mode
      console.log('Starting Deepgram live with session ID:', currentSessionId);
      const deepgram = createClient(DEEPGRAM_API_KEY);
      // Acquire microphone. If this fails, we won't flip to recording
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      streamRef.current = stream;

      // Setup MediaRecorder for audio file saving
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = []; // Reset chunks

      // Reset chunk upload tracking
      uploadedBlocksRef.current = [];
      chunkIndexRef.current = 0;
      pendingUploadsRef.current.clear();
      uploadErrorRef.current = null;

      mediaRecorder.ondataavailable = (event) => {
        // Skip if no data.
        // Note: We do NOT check pausedRef.current here because MediaRecorder may emit
        // a final chunk when pausing or stopping, and we must capture it.
        if (event.data.size === 0) {
          return;
        }

        // Keep collecting chunks as fallback
        audioChunksRef.current.push(event.data);

        // Stream chunk to blob storage immediately
        const currentIndex = chunkIndexRef.current++;
        const blockId = generateBlockId(currentIndex);

        // Track pending upload
        pendingUploadsRef.current.add(blockId);

        // Upload chunk in background (non-blocking)
        uploadAudioChunk(
          currentSessionId!,
          recordingIdRef.current!,
          blockId,
          event.data,
          recordingFileNameRef.current ||
            `recording-${recordingIdRef.current!}.webm`,
          appUser!.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        )
          .then((result) => {
            pendingUploadsRef.current.delete(blockId);

            if (result.success) {
              uploadedBlocksRef.current.push({ index: currentIndex, blockId });
              console.log(`✓ Chunk ${currentIndex} uploaded (${blockId})`);
            } else {
              console.error(
                `✗ Chunk ${currentIndex} upload failed:`,
                result.error
              );
              uploadErrorRef.current =
                result.error || 'Unknown chunk upload error';
            }
          })
          .catch((error) => {
            pendingUploadsRef.current.delete(blockId);
            console.error(`✗ Chunk ${currentIndex} exception:`, error);
            uploadErrorRef.current = String(error);
          });
      };

      mediaRecorder.onstop = () => {
        // MediaRecorder stopped
      };

      // Start recording for file saving
      mediaRecorder.start(10000); // Collect chunks every 10 seconds (reduces HTTP overhead)

      setupAudioMonitoring(stream); // Pass the stream here
      if (deepgram) {
        const liveTranscription = deepgram.listen.live({
          language: 'en-US',
          smart_format: true,
          punctuate: true,
          diarize: true,
          interim_results: true,
          vad_events: true,
          model: 'nova-3',
          encoding: 'linear16',
          sample_rate: PCM_SAMPLE_RATE,
          endpointing: false,
          channels: 1
          // numerals is not currently exposed in the JS live SDK; handled in batch
        });
        deepgramLiveRef.current = liveTranscription;
        liveTranscription.on(LiveTranscriptionEvents.Open, () =>
          setState((prev) => ({ ...prev, connectionStatus: 'Connected' }))
        );
        liveTranscription.on(
          LiveTranscriptionEvents.Transcript,
          (data: any) => {
            const alternative = data.channel.alternatives[0];
            const transcriptText = alternative.transcript;
            if (!transcriptText || transcriptText.trim() === '') return;
            const isFinal = data.is_final;
            if (alternative.words && alternative.words.length > 0) {
              const words = alternative.words;

              // Debug speaker detection
              // Optional debug: Deepgram word-level speaker data (removed for production)

              const groupedBySpeaker: { [speaker: number]: string[] } = {};
              words.forEach((word: any) => {
                const speaker = word.speaker ?? 0;
                if (!groupedBySpeaker[speaker]) groupedBySpeaker[speaker] = [];
                groupedBySpeaker[speaker].push(word.word);
              });
              if (isFinal) {
                // Compute per-speaker start/end from Deepgram words for accurate timing
                const bySpeaker: Record<
                  string,
                  { words: string[]; start?: number; end?: number }
                > = {};
                for (const w of words) {
                  const sp = String(w.speaker ?? 0);
                  if (!bySpeaker[sp]) bySpeaker[sp] = { words: [] };
                  bySpeaker[sp].words.push(w.word);
                  if (typeof w.start === 'number') {
                    if (
                      bySpeaker[sp].start === undefined ||
                      w.start < bySpeaker[sp].start
                    ) {
                      bySpeaker[sp].start = w.start;
                    }
                  }
                  if (typeof w.end === 'number') {
                    if (
                      bySpeaker[sp].end === undefined ||
                      w.end > bySpeaker[sp].end
                    ) {
                      bySpeaker[sp].end = w.end;
                    }
                  }
                }

                Object.entries(bySpeaker).forEach(([speaker, info]) => {
                  const text = info.words.join(' ');
                  // Base timestamp: use recording start + offset seconds + accumulated pause duration
                  let baseDate = new Date();
                  if (recordingIdRef.current && startTimeUtcRef?.current) {
                    const base = new Date(startTimeUtcRef.current);
                    const offset = (info.start ?? 0) * 1000;
                    const pauseOffset = accumulatedPauseDurationRef.current;
                    baseDate = new Date(base.getTime() + offset + pauseOffset);
                  }
                  const startIso = baseDate.toISOString();
                  const duration = Math.max(
                    0,
                    (info.end ?? info.start ?? 0) - (info.start ?? 0)
                  );

                  const newSegment: SpeakerSegment = {
                    id: Date.now().toString() + speaker,
                    speaker: parseInt(speaker),
                    text,
                    timestamp: startIso,
                    confidence: alternative.confidence ?? 1.0,
                    duration
                  };
                  setSpeakers((prev) => {
                    const updated = [...prev, newSegment];
                    const prevHasSegment = prev.some(
                      (seg) =>
                        (seg as any)?.id === gatingLastSeenSegmentIdRef.current
                    );
                    if (gatingInFlightRef.current || !prevHasSegment) {
                      gatingLastSeenSegmentIdRef.current = null;
                    }
                    return updated;
                  });
                  updateTemplateAnswers(text);

                  if (currentSessionId && appUser?.id) {
                    const segmentData = {
                      textContent: text,
                      timestamp: startIso,
                      speakerIndex: parseInt(speaker),
                      confidenceScore: newSegment.confidence,
                      durationSeconds: newSegment.duration
                    };

                    if (recordingIdRef.current) {
                      postSpeakerSegment(
                        currentSessionId,
                        appUser.id,
                        getAccessTokenSilently,
                        {
                          ...segmentData,
                          transcriptionRecordingId: recordingIdRef.current
                        }
                      ).catch((error) => {
                        console.error(
                          'Failed to save segment to backend:',
                          error
                        );
                      });
                    } else {
                      // Queue segment if recording ID is not yet available
                      console.log('Queueing segment, recording ID not ready');
                      pendingSegmentsRef.current.push(segmentData);
                    }
                  }
                });
                setState((prev) => ({
                  ...prev,
                  transcript:
                    prev.transcript +
                    '\n' +
                    Object.entries(groupedBySpeaker)
                      .map(
                        ([speaker, words]) =>
                          `Speaker ${speaker}: ${words.join(' ')}`
                      )
                      .join('\n'),
                  interimTranscript: ''
                }));
              } else {
                const interimText = Object.entries(groupedBySpeaker)
                  .map(
                    ([speaker, words]) =>
                      `Speaker ${speaker}: ${words.join(' ')}`
                  )
                  .join('\n');
                setState((prev) => ({
                  ...prev,
                  interimTranscript: interimText
                }));
              }
            }
          }
        );
        // Handle Close and Error events
        liveTranscription.on(LiveTranscriptionEvents.Close, () => {
          setState((prev) => ({
            ...prev,
            connectionStatus: 'Disconnected by server'
          }));
        });
        liveTranscription.on(LiveTranscriptionEvents.Error, (error: any) => {
          console.error('Deepgram error:', error);
          setState((prev) => ({
            ...prev,
            error: `Deepgram error: ${error.message || JSON.stringify(error)}`,
            connectionStatus: 'Error'
          }));
        });
      }

      // Ensure a server-side recording is created and capture its id BEFORE marking as recording
      if (sessionId && appUser?.id) {
        const rec = await apiStartRecording(
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        if (rec?.id) {
          recordingIdRef.current = rec.id;
          startTimeUtcRef.current = rec.startTimeUtc;
          // Reset accumulated pause duration for this NEW recording
          // This ensures each recording within a session has its own pause accumulation
          // and pauses from previous recordings don't affect this one
          accumulatedPauseDurationRef.current = 0;

          // Generate unique filename for this recording
          const fileName = `session-${(sessionId ?? '').slice(0, 8)}-rec-${(
            rec.id || '0'
          ).slice(0, 8)}-${Date.now()}.webm`;
          recordingFileNameRef.current = fileName;

          console.log('Started server recording', rec, 'Filename:', fileName);

          // Flush any pending segments
          if (pendingSegmentsRef.current.length > 0) {
            console.log(
              `Flushing ${pendingSegmentsRef.current.length} pending segments`
            );
            for (const segment of pendingSegmentsRef.current) {
              postSpeakerSegment(
                sessionId,
                appUser.id,
                getAccessTokenSilently,
                {
                  ...segment,
                  transcriptionRecordingId: rec.id
                }
              ).catch((error) => {
                console.error('Failed to save queued segment:', error);
              });
            }
            pendingSegmentsRef.current = [];
          }

          // Insert a divider row into transcript to indicate new recording
          setSpeakers((prev) => {
            // Avoid accidentally dropping existing dividers; simply append
            return [
              ...prev,
              {
                id: `rec-divider-${rec.id}`,
                kind: 'recording-divider',
                startTimeUtc: rec.startTimeUtc
              } as any
            ];
          });

          // Now that mic is active and backend recording exists, mark as recording
          setState((prev) => ({ ...prev, isRecording: true }));
          setHasStartedRecording(true);
          setIsStartingRecording(false);
          // Start timer only after server-side recording is created
          setSessionStartTime(new Date());
          setSessionData((prev) =>
            prev
              ? { ...prev, status: TranscriptionSessionStatus.Recording }
              : prev
          );
          await broadcastRecordingState({
            isRecording: true,
            isPaused: false,
            accumulatedMilliseconds: 0,
            recordingStartedAtUtc: rec.startTimeUtc,
            interimTranscript: ''
          });
        } else {
          console.warn(
            'Failed to start server recording; fallback will create synthetic on segment post'
          );
          recordingIdRef.current = null;
          startTimeUtcRef.current = null;
          // Do not show Recording if backend recording is not created
          setIsStartingRecording(false);
          setState((prev) => ({
            ...prev,
            isRecording: false,
            error: 'Could not create recording. Please try again.'
          }));
          return; // Exit start flow
        }
      }
    } catch (error: any) {
      console.error('Failed to start transcription:', error);
      setState((prev) => ({
        ...prev,
        isRecording: false,
        error: `Failed to start transcription: ${error.message || error}`,
        connectionStatus: 'Error'
      }));
      setIsStartingRecording(false);
    }
  };

  // Removed: AssemblyAI callbacks; only Deepgram live path is supported here

  // Helper: wait for an in-flight gated fill to complete (max waitMs)
  const waitForGatingIdle = useCallback(async (maxWaitMs: number = 30000) => {
    const start = Date.now();
    while (gatingInFlightRef.current && Date.now() - start < maxWaitMs) {
      await new Promise((r) => setTimeout(r, 200));
    }
  }, []);
  // Stop conversation (updated to stop streaming and orchestrate parallel tasks)
  const stopConversation = useCallback(async () => {
    if (!isSessionOwner) {
      console.warn('View-only mode: cannot stop recording');
      return;
    }
    // Reset pause state immediately to ensure final chunks are processed if stopping from paused state
    pausedRef.current = false;

    // *** IMMEDIATELY show loader so the user sees activity - FIRST! ***
    if (BATCH_MODE_ENABLED) {
      setPipelineStage('uploading');
      if (!ENABLE_DEEPGRAM_PROGRESS) setTranscribingProgress(null);
    }
    setAnalyzeSubstage('questions');
    setDismissedCallout(false);
    setAnalysisEnded(false);
    setIsAnalyzingTranscript(true);
    setAnalysisComplete(false);

    // Update session status to Processing (non-blocking, happens in background)
    if (sessionId && appUser?.id) {
      // Update UI immediately (optimistic update)
      if (sessionData) {
        setSessionData({
          ...sessionData,
          status: TranscriptionSessionStatus.Processing
        });
      }

      // Do this in background without blocking UI
      updateTranscriptionSession(
        sessionId,
        {
          id: sessionId,
          status: TranscriptionSessionStatus.Processing
        },
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      )
        .then((updatedSession) => {
          if (updatedSession) {
            setSessionData(updatedSession);
          }
        })
        .catch((error) => {
          console.error(
            'Failed to update session status to Processing:',
            error
          );
          // Keep optimistic update even on error
        });
    }

    // Create a promise for MediaRecorder stop BEFORE stopping it
    const mediaRecorderStopPromise = new Promise<void>((resolve) => {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        const recorder = mediaRecorderRef.current;
        const handleStop = () => {
          recorder.removeEventListener('stop', handleStop);
          resolve();
        };
        recorder.addEventListener('stop', handleStop);
        recorder.stop(); // Stop it here, after listener is attached
      } else {
        resolve(); // Already stopped
      }
    });

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (deepgramLiveRef.current) {
      deepgramLiveRef.current.finish();
      deepgramLiveRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    // No batching needed – periodic analysis on full transcript

    setState((prev) => ({
      ...prev,
      isRecording: false,
      isPaused: false,
      connectionStatus: 'Disconnected',
      audioLevel: 0
    }));
    broadcastRecordingState({
      isRecording: false,
      isPaused: false,
      accumulatedMilliseconds: recordingDuration,
      recordingEndedAtUtc: new Date().toISOString(),
      audioLevel: 0
    });
    pausedRef.current = false;
    setIsStartingRecording(false);
    // Reset recording UI flags so FAB returns to start state for next recording
    setHasStartedRecording(false);
    setSessionStartTime(null);
    setHasAnalyzed(false);

    if (!appUser?.id) return;

    const fileName =
      recordingFileNameRef.current ||
      `session-${(sessionId ?? '').slice(0, 8)}-rec-${(
        recordingIdRef.current || '0'
      ).slice(0, 8)}-${Date.now()}.webm`;

    // Use actual recording duration (excludes pauses) - recordingDuration state is in milliseconds
    const estimatedDurationSeconds = Math.max(0, recordingDuration / 1000);

    // Start three flows concurrently WITHOUT BLOCKING (UI already shown!)
    // All waiting happens inside these flows

    // 1) Final gated question filling using same flow as periodic (reuse gated thread)
    const startQf = (async () => {
      if (gatingInFlightRef.current) {
        await waitForGatingIdle(30000);
      }
      await runGatedQfTick();
      setAnalysisComplete(true);
      console.log('[EndMeeting] Question filling completed, popup should show');
    })();

    // 2) Summaries generation (ephemeral thread, no persistence) - background only
    if (sessionId && appUser?.id) {
      setIsSummariesLoading(true);
      setIsSummariesGenerating(true);
    }
    const startSummaries = performSummariesOnly();

    // 3) Finalize chunked upload (waits for MediaRecorder stop and chunks internally)
    const startUpload = (async () => {
      try {
        if (!recordingIdRef.current || !sessionId) {
          throw new Error('Missing recording context for chunk finalization');
        }

        // Wait for MediaRecorder stop event (ensures all ondataavailable fired)
        await mediaRecorderStopPromise;

        // Wait for ALL chunk uploads to complete (no arbitrary delays!)
        const maxWaitTime = 30000;
        const startWait = Date.now();

        while (
          (pendingUploadsRef.current.size > 0 ||
            uploadedBlocksRef.current.length < chunkIndexRef.current) &&
          Date.now() - startWait < maxWaitTime
        ) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Verify all chunks uploaded successfully
        if (uploadedBlocksRef.current.length < chunkIndexRef.current) {
          throw new Error(
            `Only ${uploadedBlocksRef.current.length} of ${chunkIndexRef.current} chunks uploaded`
          );
        }

        if (pendingUploadsRef.current.size > 0) {
          throw new Error(
            `${pendingUploadsRef.current.size} chunks still pending after timeout`
          );
        }

        const sortedBlocks = [...uploadedBlocksRef.current].sort(
          (a, b) => a.index - b.index
        );

        // Check for errors
        if (sortedBlocks.length === 0) {
          throw new Error('No uploaded audio chunks available to finalize.');
        }
        if (uploadErrorRef.current) {
          throw new Error(
            `Chunk upload failed earlier: ${uploadErrorRef.current}`
          );
        }

        // Calculate accurate duration from the actual audio blob
        let accurateDurationSeconds = estimatedDurationSeconds;
        try {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: 'audio/webm'
          });
          const blobUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(blobUrl);

          // Wait for metadata to load to get accurate duration
          await new Promise<void>((resolve, reject) => {
            audio.onloadedmetadata = () => {
              if (audio.duration && isFinite(audio.duration)) {
                accurateDurationSeconds = audio.duration;
                console.log(
                  `✓ Accurate duration from audio: ${accurateDurationSeconds.toFixed(
                    2
                  )}s (timer: ${estimatedDurationSeconds.toFixed(2)}s)`
                );
              }
              URL.revokeObjectURL(blobUrl);
              resolve();
            };
            audio.onerror = () => {
              URL.revokeObjectURL(blobUrl);
              resolve(); // Don't fail, just use timer duration
            };
            // Timeout after 2 seconds
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
              resolve();
            }, 2000);
          });
        } catch (e) {
          console.warn(
            'Could not get accurate duration from audio, using timer:',
            e
          );
        }

        const iso8601Duration =
          accurateDurationSeconds > 0
            ? `PT${Math.floor(accurateDurationSeconds)}S`
            : undefined;

        const finalizeResult = await finalizeAudioChunks(
          sessionId,
          recordingIdRef.current,
          sortedBlocks.map((block) => block.blockId),
          fileName,
          iso8601Duration,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          true // skipAnalysis = true to prevent auto-triggering AI pipeline
        );

        if (!finalizeResult.success) {
          throw new Error(
            finalizeResult.error || 'Failed to finalize chunked audio upload'
          );
        }

        console.log(`✓ Audio uploaded: ${sortedBlocks.length} chunks`);

        // On conversation end, trigger one final AssemblyAI batch transcription
        // using the full finalized audio URL, and request backend alignment.
        const finalAudioUrl: string | undefined =
          finalizeResult.document?.documentUrl;

        // Reset chunk tracking for next recording
        uploadedBlocksRef.current = [];
        chunkIndexRef.current = 0;
        audioChunksRef.current = [];
        uploadErrorRef.current = null;

        // Refresh session and jobs (non-blocking) to pick up the new transcribe job
        try {
          if (sessionId && appUser?.id) {
            getSessionJobs(
              sessionId,
              appUser.id,
              getAccessTokenSilently,
              getAccessTokenWithPopup
            )
              .then((list) => setSessionJobs(list))
              .catch(() => {});
          }
          const refreshed = await getTranscriptionSessionById(
            sessionId || '',
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
          if (refreshed) {
            setSessionData(refreshed);
            const combinedRows: TranscriptRow[] = [];
            const timeline: Array<{
              t: number;
              kind: 'segment' | 'divider';
              row: any;
            }> = [];
            if (refreshed.speakerSegments?.length) {
              for (const seg of refreshed.speakerSegments) {
                timeline.push({
                  t: new Date(seg.timestamp).getTime(),
                  kind: 'segment',
                  row: {
                    id: seg.id,
                    speaker: seg.speakerIndex,
                    text: seg.textContent,
                    timestamp: seg.timestamp,
                    confidence: seg.confidenceScore || 1.0,
                    duration: seg.durationSeconds || 0
                  }
                });
              }
            }
            if (refreshed.recordings?.length) {
              for (const rec of refreshed.recordings) {
                timeline.push({
                  t: new Date(rec.recordingStartTimeUtc).getTime(),
                  kind: 'divider',
                  row: {
                    id: `rec-divider-${rec.id}`,
                    kind: 'recording-divider',
                    startTimeUtc: rec.recordingStartTimeUtc
                  }
                });
              }
            }
            if (timeline.length) {
              timeline.sort((a, b) =>
                a.t !== b.t
                  ? a.t - b.t
                  : a.kind === 'divider' && b.kind === 'segment'
                  ? -1
                  : 1
              );
              for (const item of timeline) combinedRows.push(item.row);
              setSpeakers(combinedRows);
            } else {
              setSpeakers([]);
            }
          }
        } catch (e) {
          console.error('Failed to refresh session after upload:', e);
        }
      } catch (error) {
        console.error('Error finalizing chunked audio:', error);
        if (BATCH_MODE_ENABLED) setPipelineStage('error');
        throw error;
      }
    })();

    // Stop server-side recording if one is active (do this early, in parallel)
    if (sessionId && recordingIdRef.current && appUser?.id) {
      // If stopped while paused, record the pending pause segment before stopping server recording
      const stopRecordingAsync = async () => {
        try {
          const started = pauseStartRef.current;
          if (started) {
            const durationSeconds = Math.max(
              0,
              (Date.now() - started.getTime()) / 1000
            );

            // Create pause segment for local state
            const pauseSegment: SpeakerSegment = {
              id: `pause-final-${Date.now()}`,
              speaker: 0,
              text: '',
              timestamp: started.toISOString(),
              confidence: 1.0,
              duration: durationSeconds,
              isPauseSegment: true
            };

            // Add to local state
            setSpeakers((prev) => [...prev, pauseSegment]);

            await postSpeakerSegment(
              sessionId,
              appUser.id,
              getAccessTokenSilently,
              {
                textContent: '',
                timestamp: started.toISOString(),
                durationSeconds,
                isPauseSegment: true,
                transcriptionRecordingId: recordingIdRef.current!
              }
            );
            pauseStartRef.current = null;
          }
        } catch (e) {
          console.error('Failed to post final pause segment', e);
        }

        try {
          await apiStopRecording(
            sessionId,
            recordingIdRef.current!,
            appUser.id,
            getAccessTokenSilently
          );
          startTimeUtcRef.current = null;
        } catch (e) {
          console.error('Failed to stop server recording', e);
        }
      };

      // Fire and forget (non-blocking)
      stopRecordingAsync();
    }

    // Wait for all concurrent flows to complete
    try {
      const [qfResult, summariesResult] = await Promise.allSettled([
        startQf,
        startSummaries,
        startUpload
      ]);

      console.log('End-of-meeting flows completed:', {
        qf: qfResult.status,
        summaries: summariesResult.status
      });

      // Reload summaries after generation completes
      if (summariesResult.status === 'fulfilled') {
        console.log(
          '[EndMeeting] Summary generation completed, reloading summaries'
        );
        await loadSessionSummaries();

        // Optimistically reflect final status in UI (backend updates handled by LangGraph)
        const finalStatus = requiresApproval
          ? TranscriptionSessionStatus.PendingApproval
          : TranscriptionSessionStatus.Approved;

        setSessionData((prev) => {
          if (!prev || prev.status === finalStatus) {
            return prev;
          }

          console.log(
            `[EndMeeting] Optimistically updating UI status to ${finalStatus}`
          );

          return {
            ...prev,
            status: finalStatus
          };
        });
        setIsSummariesGenerating(false);
      } else {
        setIsSummariesLoading(false);
        setIsSummariesGenerating(false);
      }

      // Final UI state updates
      if (BATCH_MODE_ENABLED) {
        setPipelineStage('complete');
      }
      setIsAnalyzingTranscript(false);
      setAnalysisEnded(true);
    } catch (error) {
      console.error('Error in end-of-meeting flows:', error);
      setIsSummariesLoading(false);
      setIsSummariesGenerating(false);
      if (BATCH_MODE_ENABLED) {
        setPipelineStage('error');
      }
      setIsAnalyzingTranscript(false);
      setAnalysisEnded(true);
    }
  }, [
    isSessionOwner,
    sessionId,
    appUser,
    sessionData,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    recordingDuration,
    broadcastRecordingState,
    waitForGatingIdle,
    runGatedQfTick,
    performSummariesOnly,
    loadSessionSummaries,
    hydrateTranscriptFromSession,
    requiresApproval
  ]);

  // Pause/Resume controls
  const pauseRecording = () => {
    if (!isSessionOwner || !state.isRecording || state.isPaused) return;
    setState((prev) => ({ ...prev, isPaused: true }));
    pausedRef.current = true;
    // Mark pause start time for later duration calculation
    pauseStartRef.current = new Date();
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording' &&
      mediaRecorderRef.current.pause
    ) {
      try {
        mediaRecorderRef.current.pause();
      } catch (e) {
        // Some browsers may not support pause on MediaRecorder
      }
    }
    broadcastRecordingState({
      isPaused: true,
      accumulatedMilliseconds: accumulatedTimeRef.current,
      audioLevel: 0
    });
  };

  const resumeRecording = () => {
    if (!isSessionOwner || !state.isRecording || !state.isPaused) return;
    setState((prev) => ({ ...prev, isPaused: false }));
    pausedRef.current = false;
    // If we had an active pause, record a pause segment for that duration
    try {
      const started = pauseStartRef.current;
      if (started && sessionId && appUser?.id && recordingIdRef.current) {
        const durationMs = Math.max(0, Date.now() - started.getTime());
        const durationSeconds = durationMs / 1000;

        // Accumulate the pause duration for timestamp calculations
        accumulatedPauseDurationRef.current += durationMs;
        console.log(
          '[Pause] Accumulated pause duration:',
          accumulatedPauseDurationRef.current / 1000,
          'seconds'
        );

        // Create pause segment for local state (optimistic update)
        const pauseSegment: SpeakerSegment = {
          id: `pause-${Date.now()}`,
          speaker: 0, // Not relevant for pause segments
          text: '',
          timestamp: started.toISOString(),
          confidence: 1.0,
          duration: durationSeconds,
          isPauseSegment: true
        };

        // Add pause segment to local state immediately
        setSpeakers((prev) => [...prev, pauseSegment]);

        // Fire-and-forget; do not block UI resume
        postSpeakerSegment(sessionId, appUser.id, getAccessTokenSilently, {
          textContent: '',
          timestamp: started.toISOString(),
          durationSeconds,
          isPauseSegment: true,
          transcriptionRecordingId: recordingIdRef.current
        }).catch((e) => console.error('Failed to post pause segment', e));
      }
    } finally {
      pauseStartRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'paused' &&
      mediaRecorderRef.current.resume
    ) {
      try {
        mediaRecorderRef.current.resume();
      } catch (e) {
        // Ignore resume errors silently
      }
    }
    broadcastRecordingState({
      isPaused: false,
      accumulatedMilliseconds: accumulatedTimeRef.current
    });
  };

  // Download transcript handler
  const handleDownloadTranscript = useCallback(async () => {
    if (!sessionId || !appUser?.id) return;

    setIsDownloading(true);
    try {
      const result = await generateAndDownloadFormattedTranscript(
        sessionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      if (result.success) {
        console.log('Transcript downloaded successfully');
        // You could show a success message here if needed
      } else {
        console.error('Failed to download transcript:', result.message);
        // You could show an error message here if needed
      }
    } catch (error) {
      console.error('Error downloading transcript:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [sessionId, appUser?.id]);

  useEffect(() => {
    loadSessionSummaries();
  }, [loadSessionSummaries]);

  useEffect(() => {
    if (analysisComplete) {
      loadSessionSummaries();
    }
  }, [analysisComplete, loadSessionSummaries]);

  // Set up mobile recording callbacks
  useEffect(() => {
    setRecordingCallbacks({
      onPause: () => {
        if (state.isRecording && !state.isPaused) {
          pauseRecording();
        }
      },
      onResume: () => {
        if (state.isRecording && state.isPaused) {
          resumeRecording();
        }
      },
      onGenerateSummary: async () => {
        await stopConversation();
        setMobileRecordingMode(false); // Return to normal nav
      }
    });

    // Sync pause state with mobile context
    setPaused(state.isPaused);
  }, [state.isRecording, state.isPaused]);

  // Session title saving (isolated so typing doesn't re-render the whole page)
  const saveSessionTitle = useCallback(
    async (newTitle: string) => {
      if (!sessionId || !appUser?.id || !newTitle.trim()) return;
      try {
        const updateInput: UpdateTranscriptionSessionClientInput = {
          id: sessionId,
          title: newTitle.trim()
        };
        const updatedSession = await updateTranscriptionSession(
          sessionId,
          updateInput,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        if (updatedSession) {
          setSessionData(updatedSession);
        }
      } catch (error) {
        console.error('Failed to update session title:', error);
      }
    },
    [sessionId, appUser?.id, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  // Template picker functions
  const loadAvailableTemplates = useCallback(async () => {
    if (!appUser?.id) return;

    try {
      const templates = await getAllTemplates(
        { userId: appUser.id },
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setAvailableTemplates(templates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  }, [appUser?.id]);

  const handleSelectTemplate = useCallback(
    async (template: Template) => {
      if (!sessionId || !appUser?.id) return;

      try {
        // Close picker and show loading state immediately
        setShowTemplatePicker(false);
        setIsLoadingQuestions(true);
        setTemplateName(template.name);

        // Import dynamically to avoid circular deps if any, though service is safe
        const { changeSessionTemplate } = await import(
          '@/services/transcriptionSessionService'
        );

        // Call API to swap template, reset questions, and trigger analysis
        const updatedSession = await changeSessionTemplate(
          sessionId,
          template.id,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        if (updatedSession) {
          setSessionData(updatedSession);

          // Update pipeline state to show "Analyzing" status immediately
          // The backend triggers the job, so we should visually reflect that processing has started
          setPipelineStage('analyzing');
          setAnalyzeSubstage('questions');
          setIsAnalyzingTranscript(true);
          setAnalysisComplete(false);
          setAnalysisEnded(false);
          setDismissedCallout(false);
          // Clear persisted dismissal state when restarting analysis
          if (sessionId) {
            localStorage.removeItem(`intakeai_callout_dismissed_${sessionId}`);
          }
          setAllQuestionsAnswered(false);
          setUnansweredQuestions([]); // Will be repopulated when questions load
        }
      } catch (error) {
        console.error('Error changing template:', error);
        setIsLoadingQuestions(false); // Only unset if error; otherwise useEffect handles it
      }
    },
    [sessionId, appUser?.id, getAccessTokenSilently, getAccessTokenWithPopup]
  );
  // Header setup with breadcrumb-style title
  useEffect(() => {
    if (isLoadingQuestions) {
      setHeaderText('Loading Session...');
    } else {
      // Create breadcrumb-style title with session info
      const sessionTitle = sessionData?.title || 'Untitled Session';
      // Use file extension if available, otherwise fall back to Audio/Document
      const fileTypeLabel =
        fileTypeExtension || (isAudioSession ? 'Audio' : 'Document');

      setHeaderText(
        isMobile ? (
          // Mobile: Editable title only (collection tagger moved to menu)
          <EditableSessionTitle
            title={sessionTitle}
            onSave={saveSessionTitle}
            isMobile
          />
        ) : (
          // Desktop: Breadcrumb layout with file type chip (Attio style)
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'nowrap',
              minWidth: 0
            }}
          >
            <Box
              onClick={() => navigate('/records')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: 'pointer',
                transition: 'color 0.15s ease',
                flexShrink: 0,
                '&:hover': { 
                  color: theme.palette.text.primary 
                }
              }}
            >
              <FileText size={16} weight="regular" />
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'inherit'
                }}
              >
                Files
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '14px',
                color: theme.palette.text.disabled,
                flexShrink: 0,
                userSelect: 'none',
                mx: -0.25
              }}
            >
              /
            </Typography>
            {/* Show folder name in breadcrumb only if file is in exactly ONE collection */}
            {sessionCollections.length === 1 && (
              <>
                <Box
                  onClick={() => navigate(`/collections/${sessionCollections[0].id}`)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    transition: 'color 0.15s ease',
                    flexShrink: 0,
                    maxWidth: '200px',
                    overflow: 'hidden',
                    '&:hover': { 
                      color: theme.palette.text.primary 
                    }
                  }}
                >
                  <Folders size={16} weight="regular" style={{ flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'inherit',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {sessionCollections[0].title}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: theme.palette.text.disabled,
                    flexShrink: 0,
                    userSelect: 'none',
                    mx: -0.25
                  }}
                >
                  /
                </Typography>
              </>
            )}
            <Box
              sx={{
                minWidth: 0,
                flex: '1 1 auto',
                overflow: isMobile ? 'hidden' : 'visible',
                mr: 0.5,
                position: 'relative',
                zIndex: 1
              }}
            >
              <EditableSessionTitle
                title={sessionTitle}
                onSave={saveSessionTitle}
                isMobile={false}
              />
            </Box>
            <Chip
              label={fileTypeLabel}
              size="small"
              sx={{
                height: '22px',
                px: 0.75,
                borderRadius: '4px',
                fontSize: '11px',
                flexShrink: 0,
                ml: 0.5,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)',
                color: theme.palette.text.secondary,
                border: 'none',
                cursor: 'default',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  px: 0.75,
                  fontSize: '11px',
                  fontWeight: 500
                }
              }}
            />
            {effectiveSessionId && (
              <Box sx={{ flexShrink: 0, ml: 0.5 }}>
                <SessionCollectionTagger
                  sessionId={effectiveSessionId}
                  currentCollections={sessionCollections}
                  onUpdate={reloadSessionCollections}
                />
              </Box>
            )}
          </Box>
        )
      );
    }
    setMenuActions(
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {/* Show presence indicator when there's a session */}
        {effectiveSessionId && appUser?.id && (
          <PresenceIndicator
            sessionId={effectiveSessionId}
            currentUserId={appUser.id}
          />
        )}
        {!isSessionOwner && (
          <Chip
            size="small"
            icon={<ViewOnlyIcon fontSize="small" />}
            label="Viewing"
            sx={{
              height: '24px',
              px: 1,
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(59, 130, 246, 0.15)'
                  : '#eff6ff',
              color: theme.palette.mode === 'dark' ? '#93c5fd' : '#2563eb',
              border: 'none',
              cursor: 'default',
              '& .MuiChip-label': {
                px: 0.75,
                fontSize: '12px'
              },
              '& .MuiChip-icon': {
                color: theme.palette.mode === 'dark' ? '#93c5fd' : '#2563eb',
                fontSize: '14px',
                ml: 0.5
              }
            }}
          />
        )}

        {isMobile ? (
          // Mobile: Only show three dots menu
          <IconButton
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            disableRipple
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : '#f3f4f6'
              },
              '& .MuiSvgIcon-root': {
                fontSize: '20px'
              }
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ) : (
          // Desktop: Show all buttons
          <>
            <IconButton
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              disableRipple
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : '#f3f4f6'
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '20px'
                }
              }}
            >
              <MoreVertIcon />
            </IconButton>

            {/* Share button - Hidden for now */}
            {/* {sessionSummaries.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={async (e) => {
                  setShareAnchorEl(e.currentTarget);
                  if (availableDistributionGroups.length === 0 && templateId) {
                    setIsLoadingDistributionGroups(true);
                    try {
                      const groups = await getAvailableDistributionGroups(
                        parseInt(templateId),
                        selectedUserRole!.companyId,
                        getAccessTokenSilently,
                        getAccessTokenWithPopup
                      );
                      setAvailableDistributionGroups(groups);
                    } catch (error) {
                      console.error(
                        'Error loading distribution groups:',
                        error
                      );
                    } finally {
                      setIsLoadingDistributionGroups(false);
                    }
                  }
                }}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#f9fafb',
                    borderColor: theme.palette.divider
                  }
                }}
              >
                Share
              </Button>
            )} */}

            {/* End & Generate Summary button - Show as soon as recording starts */}
            {(state.isRecording || hasStartedRecording) && (
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={async () => {
                  await stopConversation();
                }}
                disabled={!canControlRecording}
                sx={{
                  borderRadius: '6px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#1d4ed8',
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)'
                  }
                }}
              >
                End & Generate Summary
              </Button>
            )}
          </>
        )}

        {/* Removed duplicate/hidden tooltip/button */}
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMobile,
    isLoadingQuestions,
    templateName,
    sessionData?.title,
    sessionData?.status,
    saveSessionTitle,
    state.isRecording,
    hasStartedRecording,
    effectiveSessionId,
    appUser?.id,
    isAudioSession,
    fileTypeExtension,
    sessionCollections.length,
    canControlRecording,
    isSessionOwner,
    theme.palette.mode
  ]);

  // Effect to handle cleanup on unmount ONLY
  useEffect(() => {
    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [setHeaderText, setMenuActions]);

  // Cleanup on unmount (updated to stop streaming)
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (deepgramLiveRef.current) {
        deepgramLiveRef.current.finish();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      // Cancel any in-progress chunked upload
      if (recordingIdRef.current && sessionId && appUser?.id) {
        cancelAudioChunks(
          sessionId,
          recordingIdRef.current,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        ).catch((error) => {
          console.warn('Failed to cancel chunks on unmount:', error);
        });
      }
    };
  }, [sessionId, appUser?.id]);

  // UPDATED: useEffect to change active tab when content space changes
  useEffect(() => {
    if (shouldUseTabs) {
      // Always prioritize summaries tab for mobile/tablet mode
      if (activeTab !== 'summaries') {
        setActiveTab('summaries');
      }
    } else {
      // For side-by-side mode, keep summaries as priority, fallback to template if needed
      if (activeTab === 'transcript') {
        setActiveTab('summaries');
      }
    }
  }, [shouldUseTabs]); // activeTab is intentionally not a dependency to avoid loops, we are setting it.

  // Define Tab Configurations
  const TABS_CONFIG: Record<
    ActiveTabType,
    { key: ActiveTabType; label: string; icon?: React.ReactNode }
  > = {
    transcript: {
      key: 'transcript',
      label: 'Transcript',
      icon: <Waveform size={16} weight="regular" />
    },
    summaries: {
      key: 'summaries',
      label: 'Summary',
      icon: <Article size={16} weight="regular" />
    },
    template: { 
      key: 'template', 
      label: 'Template',
      icon: <ClipboardText size={16} weight="regular" />
    },
    documents: {
      key: 'documents',
      label: 'Documents',
      icon: <Folders size={16} weight="regular" />
    },
    speakers: {
      key: 'speakers',
      label: 'Speakers',
      icon: <Users size={16} weight="regular" />
    },
    notes: {
      key: 'notes',
      label: 'Notes',
      icon: <NotePencil size={16} weight="regular" />
    },
    chat: {
      key: 'chat',
      label: 'Ask',
      icon: <Lightning size={16} weight="regular" />
    },
    critical: {
      key: 'critical',
      label: 'Critical Items',
      icon: <WarningCircle size={16} weight="regular" />
    }
  };

  // Show tabs based on layout: Notes now in main tabs, Chat in right sidebar
  const displayedTabs = shouldStackVertically
    ? [
        // Mobile only - all tabs including notes and critical items (when content is very narrow)
        TABS_CONFIG.summaries,
        ...(isAudioSession ? [TABS_CONFIG.transcript] : []), // Only show transcript for audio sessions
        TABS_CONFIG.template,
        TABS_CONFIG.documents,
        TABS_CONFIG.speakers,
        TABS_CONFIG.notes,
        TABS_CONFIG.critical
      ]
    : [
        // Desktop & tablet - notes in main tabs, chat in right sidebar, critical items hidden for now
        TABS_CONFIG.summaries,
        ...(isAudioSession ? [TABS_CONFIG.transcript] : []), // Only show transcript for audio sessions
        TABS_CONFIG.template,
        TABS_CONFIG.documents,
        TABS_CONFIG.speakers,
        TABS_CONFIG.notes
      ];

  // Get content space info (removed debug logging to prevent render loop)
  const { contentWidth, isCompact, isMedium, isWide } = useContentSpace();

  // Calculate max visible tabs for desktop view to accommodate action buttons.
  // Tabs with header actions need an extra slot reserved so the overflow trigger
  // stays visible instead of sliding underneath the action area.
  const hasHeaderActions =
    activeTab === 'documents' ||
    activeTab === 'template' ||
    activeTab === 'transcript' ||
    activeTab === 'speakers';

  const baseVisibleTabs = isWide ? 4 : isMedium ? 2 : 1;
  const desktopMaxVisibleTabs = hasHeaderActions
    ? Math.max(1, baseVisibleTabs - 1)
    : baseVisibleTabs;

  // Calculate max visible tabs for mobile/tablet view (when stacked vertically)
  // Tablet (SM-MD): 3 tabs
  // Phone (XS): 2 tabs
  const baseMobileTabs = isMobile ? 2 : 3;
  const mobileMaxVisibleTabs = hasHeaderActions
    ? Math.max(1, baseMobileTabs - 1)
    : baseMobileTabs;

  // Ensure activeTab is valid for current displayedTabs (with ref to prevent loops)
  const displayedTabsRef = useRef(displayedTabs);
  displayedTabsRef.current = displayedTabs;

  useEffect(() => {
    const isActiveTabValid = displayedTabsRef.current.some(
      (tab) => tab.key === activeTab
    );
    if (!isActiveTabValid && activeTab !== 'summaries') {
      setActiveTab('summaries');
    }
  }, [activeTab]); // Only depend on activeTab, not displayedTabs

  const sendSegmentToServer = useCallback(
    async (
      finalText: string,
      speakerIndex: number,
      timestampIso: string,
      durationSeconds?: number,
      confidenceScore?: number
    ) => {
      try {
        if (!sessionId || !appUser?.id || !recordingIdRef.current) return;
        await postSpeakerSegment(
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          {
            textContent: finalText,
            timestamp: timestampIso,
            speakerIndex,
            confidenceScore,
            durationSeconds,
            transcriptionRecordingId: recordingIdRef.current
          }
        );
      } catch (e) {
        console.error('Failed to send segment', e);
      }
    },
    [sessionId, appUser?.id]
  );

  const handleUploadAudioBlob = async (blob: Blob, name: string) => {
    if (!sessionId || !appUser?.id) return;
    await uploadAudioDocument(
      sessionId,
      blob,
      name,
      appUser.id,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      undefined,
      { recordingId: recordingIdRef.current || undefined }
    );
  };
  const processingCalloutCopy = useMemo(() => {
    // ALWAYS check analysisComplete first - this takes precedence over all pipeline stages
    if (analysisComplete) {
      // Only show question-related messages if there are actually questions in the template
      if (templateQuestions.length > 0) {
        return allQuestionsAnswered
          ? 'All template questions have been answered!'
          : `${unansweredQuestions.length} question${
              unansweredQuestions.length === 1 ? '' : 's'
            } need attention`;
      }
      // If no questions, don't show a message (callout will be hidden)
      return null;
    }

    const formatWithProgress = (
      label: string,
      progressValue: number | null
    ) => {
      if (progressValue == null) return label;
      return `${label} ${progressValue}%`;
    };

    switch (pipelineStage) {
      case 'uploading':
        return formatWithProgress('Uploading audio…', uploadProgress);
      case 'transcribing':
        return formatWithProgress(
          transcribingStatusMessage || 'Transcribing audio…',
          transcribingProgress
        );
      case 'analyzing':
        if (analyzeSubstage === 'questions') {
          return 'Filling template questions…';
        }
        if (analyzeSubstage === 'summaries') {
          return 'Generating AI summaries…';
        }
        if (analyzeSubstage === 'quality') {
          return 'Reviewing transcript quality…';
        }
        return 'Running AI analysis…';
      default:
        if (
          sessionData?.status === TranscriptionSessionStatus.Processing ||
          isAnalyzingTranscript
        ) {
          return 'Preparing transcript…';
        }
        return 'Processing conversation...';
    }
  }, [
    analysisComplete,
    allQuestionsAnswered,
    unansweredQuestions.length,
    templateQuestions.length,
    pipelineStage,
    uploadProgress,
    transcribingProgress,
    transcribingStatusMessage,
    analyzeSubstage,
    sessionData?.status,
    isAnalyzingTranscript
  ]);

  // Show skeleton loader only on initial load (not on refreshes)
  if (!sessionData && !hasLoadedSessionDataRef.current) {
    return (
      <Box
        sx={{
          px: { xs: 0.5, sm: 1, md: 2 },
          py: 0.5,
          height: isMobile ? 'calc(100vh - 128px)' : 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: 'background.default'
        }}
      >
        {/* Title skeleton */}
        <Box sx={{ px: { xs: 1, md: 2 }, py: 1.5 }}>
          <Skeleton 
            width="40%" 
            height={32}
            sx={{ 
              mb: 0.5,
              bgcolor: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.05)' 
                : 'rgba(0,0,0,0.06)'
            }} 
          />
          <Skeleton 
            width="25%" 
            height={20}
            sx={{ 
              bgcolor: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.05)' 
                : 'rgba(0,0,0,0.06)'
            }} 
          />
        </Box>

        {/* Tabs skeleton */}
        <Box sx={{ px: { xs: 1, md: 2 }, mb: 2 }}>
          <Stack direction="row" spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton 
                key={i}
                width={80} 
                height={36}
                sx={{ 
                  borderRadius: '8px',
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.06)'
                }} 
              />
            ))}
          </Stack>
        </Box>

        {/* Main content skeleton */}
        <Box sx={{ px: { xs: 1, md: 2 }, flex: 1 }}>
          <Stack spacing={2}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i}>
                <Skeleton 
                  width="15%" 
                  height={20}
                  sx={{ 
                    mb: 1,
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }} 
                />
                <Skeleton 
                  width="100%" 
                  height={60}
                  sx={{ 
                    borderRadius: '8px',
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }} 
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 0.5, sm: 1, md: 2 }, // Changed from 1.5 to 2 (16px instead of 12px on desktop)
        py: 0.5, // Reduced vertical padding
        height: isMobile ? 'calc(100vh - 128px)' : 'calc(100vh - 80px)', // Extra space on mobile for bottom nav (only when truly mobile)
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // Prevent scrolling on main container
        backgroundColor: 'background.default',
        position: 'relative', // Ensure proper stacking context
        zIndex: 1, // Lower than left menu (1200)
        pointerEvents: 'auto', // Ensure this doesn't block clicks
        marginLeft: 0 // Ensure no negative margins
      }}
    >
      {/* Full-height layout for desktop with chat extending up */}
      {!shouldUseMobileLayout ? (
        // Desktop Layout - Side-by-side with extended chat
        <>
          {/* Processing/Completion Callout - Notion Style */}
          {(() => {
            // Don't show if we're in a final state (complete, idle, or error)
            if (pipelineStage === 'complete' || pipelineStage === 'idle' || pipelineStage === 'error') {
              // Exception: show completion message if analysis is done and there are questions
              const shouldShowCompletion =
                analysisComplete && templateQuestions.length > 0;
              
              if (!shouldShowCompletion || dismissedCallout || !sessionData) {
                return false;
              }
            }
            
            // Show if actually processing
            const isActuallyProcessing =
              pipelineStage === 'uploading' ||
              pipelineStage === 'transcribing' ||
              pipelineStage === 'analyzing';
            
            // Also check if analyzing via other means (but not if pipeline is complete)
            const isAnalyzingViaOtherMeans =
              (isAnalyzingTranscript ||
                sessionData?.status === TranscriptionSessionStatus.Processing) &&
              pipelineStage !== 'complete';
            
            // Only show completion message if analysis is done and there are questions
            const shouldShowCompletion =
              analysisComplete && templateQuestions.length > 0;

            const shouldShow =
              !dismissedCallout &&
              sessionData && // Only show if session data is loaded
              (isActuallyProcessing || isAnalyzingViaOtherMeans || shouldShowCompletion);

            console.log('[Callout] rendering check', {
              shouldShow,
              pipelineStage,
              dismissedCallout,
              analysisComplete,
              hasQuestions: templateQuestions.length > 0,
              isActuallyProcessing,
              isAnalyzingViaOtherMeans,
              shouldShowCompletion
            });

            return shouldShow && processingCalloutCopy; // Also check that we have a message to show
          })() && (
            <Box
              sx={{
                // Remove horizontal margin since container now has padding
                mx: 0,
                mt: 1,
                py: 0.25, // reduced vertical padding to make callout shorter
                px: 1,
                minHeight: 32, // further reduced for more compact callout
                position: 'relative',
                backgroundColor: analysisComplete
                  ? allQuestionsAnswered
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(34, 197, 94, 0.1)'
                      : '#f0fdf4'
                    : theme.palette.mode === 'dark'
                    ? 'rgba(245, 158, 11, 0.1)'
                    : '#fffbeb'
                  : theme.palette.mode === 'dark'
                  ? 'rgba(96, 165, 250, 0.1)'
                  : '#f0f9ff',
                border: analysisComplete
                  ? allQuestionsAnswered
                    ? theme.palette.mode === 'dark'
                      ? '1px solid rgba(34, 197, 94, 0.4)'
                      : '1px solid #22c55e'
                    : theme.palette.mode === 'dark'
                    ? '1px solid rgba(245, 158, 11, 0.4)'
                    : '1px solid #f59e0b'
                  : theme.palette.mode === 'dark'
                  ? '1px solid rgba(96, 165, 250, 0.4)'
                  : '1px solid #60a5fa',
                borderRadius: '8px',
                overflow: 'visible',
                flexShrink: 0
                // Removed transition and animation to prevent re-rendering
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 1, // Reduced vertical padding to make callout more compact
                  px: 2
                }}
              >
                {analysisComplete ? (
                  allQuestionsAnswered ? (
                    <CheckCircleIcon
                      sx={{
                        color:
                          theme.palette.mode === 'dark' ? '#86efac' : '#22c55e',
                        fontSize: 20,
                        flexShrink: 0
                      }}
                    />
                  ) : (
                    <WarningIcon
                      sx={{
                        color:
                          theme.palette.mode === 'dark' ? '#fbbf24' : '#f59e0b',
                        fontSize: 20,
                        flexShrink: 0
                      }}
                    />
                  )
                ) : (
                  <AutoAwesomeIcon
                    sx={{
                      color:
                        theme.palette.mode === 'dark' ? '#93c5fd' : '#60a5fa',
                      fontSize: 20,
                      flexShrink: 0
                    }}
                  />
                )}
                <Typography
                  variant="body1"
                  sx={{
                    color: analysisComplete
                      ? allQuestionsAnswered
                        ? theme.palette.mode === 'dark'
                          ? '#86efac'
                          : '#15803d'
                        : theme.palette.mode === 'dark'
                        ? '#fbbf24'
                        : '#d97706' // Green or yellow text
                      : theme.palette.mode === 'dark'
                      ? '#93c5fd'
                      : '#2563eb', // Blue text for processing
                    fontSize: '14px',
                    fontWeight: 500,
                    flex: 1,
                    textAlign: 'left',
                    // Add fade animation when transcribing
                    ...(pipelineStage === 'transcribing' && {
                      animation: 'fadeInOut 3s ease-in-out infinite',
                      '@keyframes fadeInOut': {
                        '0%': { opacity: 0.7 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.7 }
                      }
                    })
                  }}
                >
                  {processingCalloutCopy}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Dismiss Button for all callouts */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDismissedCallout(true);
                      setShowJustInTimeUI(false);
                      setShowNextActionsUI(false);
                      // Persist dismissal state
                      if (effectiveSessionId) {
                        localStorage.setItem(
                          `intakeai_callout_dismissed_${effectiveSessionId}`,
                          'true'
                        );
                      }
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      zIndex: 1000,
                      position: 'relative',
                      color: analysisComplete
                        ? allQuestionsAnswered
                          ? theme.palette.mode === 'dark'
                            ? '#86efac'
                            : '#15803d'
                          : theme.palette.mode === 'dark'
                          ? '#fbbf24'
                          : '#d97706'
                        : theme.palette.mode === 'dark'
                        ? '#93c5fd'
                        : '#2563eb',
                      '&:hover': {
                        backgroundColor: analysisComplete
                          ? allQuestionsAnswered
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(34, 197, 94, 0.2)'
                              : '#dcfce7'
                            : theme.palette.mode === 'dark'
                            ? 'rgba(245, 158, 11, 0.2)'
                            : '#fef3c7'
                          : theme.palette.mode === 'dark'
                          ? 'rgba(96, 165, 250, 0.2)'
                          : '#dbeafe'
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}

          {/* Recording Controls Section - Hidden when FAB controls are active */}
          {showRecordingControls && !canShowFabControls() && (
            <Grid
              container
              spacing={{ xs: 0, md: 1 }}
              sx={{ flexShrink: 0, mb: 1, mt: 1 }}
            >
              {/* Recording Controls - Full Width */}
              <Grid
                size={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box
                  ref={recordingInterfaceRef}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 1.5, // Reduced from 2 (4px less on top and bottom)
                    mx: 0,
                    backgroundColor: !state.isRecording
                      ? analysisComplete
                        ? 'rgba(59, 130, 246, 0.02)' // Very light blue for analyzed
                        : hasStartedRecording
                        ? 'rgba(245, 158, 11, 0.02)' // Very light orange for has content
                        : 'rgba(34, 197, 94, 0.02)' // Very light green for ready
                      : state.isPaused
                      ? 'rgba(245, 158, 11, 0.02)' // Very light orange for paused
                      : 'rgba(220, 38, 38, 0.02)', // Very light red for recording
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    position: 'relative',
                    flexShrink: 0
                  }}
                >
                  {!state.isRecording ? (
                    // Ready to Record State - Same layout as recording state to prevent jumping
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          justifyContent: 'center'
                        }}
                      >
                        {/* Record Button */}
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Tooltip
                            title={
                              !canControlRecording
                                ? 'Viewing only'
                                : isStartingRecording
                                ? 'Preparing…'
                                : 'Click to Start'
                            }
                            enterDelay={500}
                            arrow
                          >
                            <Box
                              sx={{
                                width: { xs: 80, sm: 100 },
                                height: { xs: 80, sm: 100 },
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}
                            >
                              <IconButton
                                onClick={async () => {
                                  await startConversation();
                                }}
                                disabled={
                                  !appUser?.id ||
                                  isStartingRecording ||
                                  !canControlRecording
                                }
                                sx={{
                                  width: { xs: 60, sm: 80 },
                                  height: { xs: 60, sm: 80 },
                                  borderRadius: '50%',
                                  bgcolor: '#dc2626',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: '#b91c1c'
                                  },
                                  '&:disabled': {
                                    bgcolor: theme.palette.text.secondary
                                  },
                                  transition: 'background-color 0.2s ease'
                                }}
                              >
                                <Box
                                  sx={{
                                    width: { xs: 20, sm: 28 },
                                    height: { xs: 20, sm: 28 },
                                    borderRadius: '50%',
                                    bgcolor: 'currentColor'
                                  }}
                                />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </Box>

                        {/* Status Text */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#16a34a', // Green for ready
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: { xs: '12px', sm: '15px' },
                            alignSelf: 'center',
                            minWidth: '120px', // Reduced to fit "READY TO BEGIN"
                            textAlign: 'center'
                          }}
                        >
                          {isSessionOwner ? 'READY TO BEGIN' : 'VIEWING MODE'}
                        </Typography>

                        {/* Timer - Always shown to prevent layout shift */}
                        <Typography
                          variant="h4"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: { xs: '1.5rem', sm: '2.5rem' },
                            mx: 0,
                            alignSelf: 'center',
                            minWidth: '140px',
                            textAlign: 'center' // Center the timer text
                          }}
                        >
                          {formatTime(recordingDuration)}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    // Recording Active State
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          justifyContent: 'center'
                        }}
                      >
                        {/* Recording/Pause Button with Animation Ring */}
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Tooltip
                            title={
                              state.isPaused
                                ? 'Click to Continue'
                                : 'Click to Pause'
                            }
                            enterDelay={500}
                            arrow
                          >
                            <Box
                              sx={{
                                width: { xs: 80, sm: 100 },
                                height: { xs: 80, sm: 100 },
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                '&::before': state.isPaused
                                  ? {}
                                  : {
                                      content: '""',
                                      position: 'absolute',
                                      top: -4,
                                      left: -4,
                                      right: -4,
                                      bottom: -4,
                                      borderRadius: '50%',
                                      background:
                                        'radial-gradient(circle, rgba(220, 38, 38, 0.6) 0%, rgba(220, 38, 38, 0.3) 70%, rgba(220, 38, 38, 0.1) 90%, rgba(220, 38, 38, 0) 100%)',
                                      animation:
                                        state.audioLevel > 15
                                          ? 'wavyPulse 0.8s ease-in-out infinite'
                                          : 'gradientPulse 2s ease-in-out infinite'
                                    },
                                '@keyframes gradientPulse': {
                                  '0%': {
                                    transform: 'scale(0.8)',
                                    opacity: 0.8
                                  },
                                  '50%': {
                                    transform: 'scale(1.05)',
                                    opacity: 0.4
                                  },
                                  '100%': {
                                    transform: 'scale(0.8)',
                                    opacity: 0.8
                                  }
                                },
                                '@keyframes wavyPulse': {
                                  '0%': {
                                    transform: 'scale(0.9)',
                                    opacity: 0.9,
                                    borderRadius: '50%'
                                  },
                                  '25%': {
                                    transform: 'scale(1.0)',
                                    opacity: 0.6,
                                    borderRadius: '45% 55% 50% 50%'
                                  },
                                  '50%': {
                                    transform: 'scale(1.1)',
                                    opacity: 0.3,
                                    borderRadius: '50% 50% 45% 55%'
                                  },
                                  '75%': {
                                    transform: 'scale(1.0)',
                                    opacity: 0.6,
                                    borderRadius: '55% 45% 50% 50%'
                                  },
                                  '100%': {
                                    transform: 'scale(0.9)',
                                    opacity: 0.9,
                                    borderRadius: '50%'
                                  }
                                }
                              }}
                            >
                              <IconButton
                                onClick={() => {
                                  if (!canControlRecording) return;
                                  if (state.isPaused) {
                                    resumeRecording();
                                  } else {
                                    pauseRecording();
                                  }
                                }}
                                disabled={!canControlRecording}
                                sx={{
                                  width: { xs: 60, sm: 80 },
                                  height: { xs: 60, sm: 80 },
                                  borderRadius: '50%',
                                  bgcolor: '#dc2626', // Always red
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: '#b91c1c' // Always darker red
                                  },
                                  '&:disabled': {
                                    bgcolor:
                                      theme.palette.action.disabledBackground,
                                    color: theme.palette.action.disabled
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    width: { xs: 24, sm: 32 },
                                    height: { xs: 24, sm: 32 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {state.isPaused ? (
                                    <Box
                                      sx={{
                                        width: { xs: 16, sm: 22 },
                                        height: { xs: 16, sm: 22 },
                                        borderRadius: '50%', // Circle instead of square
                                        bgcolor: 'white'
                                      }}
                                    />
                                  ) : (
                                    <PauseIcon
                                      sx={{ fontSize: { xs: 24, sm: 32 } }}
                                    />
                                  )}
                                </Box>
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </Box>

                        {/* Status Text */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#dc2626', // Always red
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: { xs: '12px', sm: '15px' },
                            alignSelf: 'center',
                            minWidth: '120px', // Match ready state width
                            textAlign: 'center'
                          }}
                        >
                          {state.isPaused ? 'PAUSED' : 'RECORDING'}
                        </Typography>

                        {/* Timer */}
                        <Typography
                          variant="h4"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: { xs: '1.5rem', sm: '2.5rem' },
                            mx: 0,
                            alignSelf: 'center',
                            minWidth: '140px',
                            textAlign: 'center', // Center the timer text
                            mt: -0.5 // Move timer up slightly away from pulse effect
                          }}
                        >
                          {formatTime(recordingDuration)}
                        </Typography>
                      </Box>

                      {/* Keyboard Shortcuts and Hide Controls */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                          mt: 0.5
                        }}
                      >
                        <Box
                          sx={{
                            display: { xs: 'none', lg: 'flex' },
                            gap: 1,
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#6b7280',
                              alignSelf: 'center',
                              fontSize: '9px',
                              fontWeight: 500,
                              mr: 1
                            }}
                          >
                            Keyboard shortcuts:
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                              px: 0.75,
                              py: 0.25,
                              backgroundColor: '#f3f4f6',
                              borderRadius: '4px',
                              border: '1px solid #e5e7eb'
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: '#6b7280',
                                fontSize: '9px'
                              }}
                            >
                              Space
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              color: '#9ca3af',
                              alignSelf: 'center',
                              fontSize: '9px'
                            }}
                          >
                            Play/Pause
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                              px: 0.75,
                              py: 0.25,
                              backgroundColor: '#f3f4f6',
                              borderRadius: '4px',
                              border: '1px solid #e5e7eb'
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: '#6b7280',
                                fontSize: '9px'
                              }}
                            >
                              Esc
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              color: '#9ca3af',
                              alignSelf: 'center',
                              fontSize: '9px'
                            }}
                          >
                            End & Generate Summary
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Connection status - minimal indicator */}
                  {state.connectionStatus === 'Error' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                        boxShadow: '0 0 8px rgba(255, 0, 0, 0.5)'
                      }}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Add spacing when recording controls are hidden */}
          {!showRecordingControls && <Box sx={{ height: '8px' }} />}

          <Grid
            container
            spacing={{ xs: 0, md: 1 }}
            sx={{ flex: 1, minHeight: 0 }}
          >
            {/* Left Column - Main Content (55% desktop, 100% mobile) */}
            <Grid
              size={{ xs: 12, md: 6.6 }} // Full width on mobile, 55% on desktop
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%', // Take full height of parent
                overflow: 'visible', // Allow content to determine overflow
                pl: 0 // No left padding for full width popup
              }}
            >
              {/* Main tabbed content */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  bgcolor: 'background.paper',
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minHeight: 0
                  }}
                >
                  {/* Tab Headers using UnifiedTabs */}
                  <UnifiedTabsContainer>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: '48px'
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          '& > .MuiBox-root': { borderBottom: 'none' }
                        }}
                      >
                        <UnifiedTabs
                          tabs={displayedTabs
                            .filter(
                              (t) =>
                                t.key !== 'chat' &&
                                t.key !== 'critical'
                            )
                            .map((t) => ({
                              key: t.key,
                              label: t.label,
                              icon: t.icon,
                              count: 
                                t.key === 'summaries' ? sessionSummaries.length :
                                t.key === 'documents' ? (sessionData?.companyDocuments?.length || 0) :
                                t.key === 'speakers' ? new Set(speakers.filter((s: any) => !(s as any).kind).map((s: any) => (s as SpeakerSegment).speaker)).size :
                                undefined
                            }))}
                          activeTab={activeTab}
                          onTabChange={(key) =>
                            setActiveTab(key as ActiveTabType)
                          }
                          maxVisibleTabs={desktopMaxVisibleTabs}
                          fitToContainer
                          minVisibleTabs={1}
                        />
                      </Box>

                      {/* Tab-specific Action Buttons */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          minHeight: '32px',
                          px: 1
                        }}
                      >
                        {activeTab === 'template' && (
                          <>
                            {templateQuestions.length > 0 &&
                              unansweredQuestions.length > 0 &&
                              analysisComplete && (
                                <Button
                                  variant="text"
                                  size="small"
                                  startIcon={<SortIcon />}
                                  onClick={() =>
                                    setSortUnansweredFirst(!sortUnansweredFirst)
                                  }
                                  sx={{
                                    fontSize: '11px',
                                    color: sortUnansweredFirst
                                      ? '#3b82f6'
                                      : '#6b7280',
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    px: 1,
                                    '&:hover': {
                                      backgroundColor: sortUnansweredFirst
                                        ? '#eff6ff'
                                        : '#f3f4f6'
                                    }
                                  }}
                                >
                                  {sortUnansweredFirst ? 'Default' : 'Sort'}
                                </Button>
                              )}
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<FileText size={16} />}
                              disabled={state.isRecording}
                              onClick={() => {
                                // Check if changing template would cause data loss
                                if (hasProgressToLose()) {
                                  setShowTemplateChangeWarning(true);
                                } else {
                                  loadAvailableTemplates();
                                  setShowTemplatePicker(true);
                                }
                              }}
                              sx={{
                                borderRadius: '6px',
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary,
                                fontSize: '11px',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : '#f9fafb',
                                  borderColor: theme.palette.divider
                                },
                                '&:disabled': {
                                  borderColor: theme.palette.divider,
                                  color: theme.palette.text.secondary
                                }
                              }}
                            >
                              Select Template
                            </Button>
                          </>
                        )}
                        {activeTab === 'transcript' && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={handleDownloadTranscript}
                            disabled={isDownloading}
                            sx={{
                              borderRadius: '6px',
                              borderColor: theme.palette.divider,
                              color: theme.palette.text.secondary,
                              fontSize: '11px',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : '#f9fafb',
                                borderColor: theme.palette.divider
                              },
                              '&:disabled': {
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary
                              }
                            }}
                          >
                            {isDownloading ? 'Exporting...' : 'Export'}
                          </Button>
                        )}
                        {activeTab === 'speakers' && (
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              borderRadius: '6px',
                              borderColor: theme.palette.divider,
                              color: theme.palette.text.secondary,
                              fontSize: '11px',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : '#f9fafb',
                                borderColor: theme.palette.divider
                              }
                            }}
                          >
                            Auto-assign
                          </Button>
                        )}
                        {activeTab === 'documents' && (
                          <>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CameraIcon />}
                              onClick={handleCameraCapture}
                              disabled={!!uploadProgress}
                              sx={{
                                borderRadius: '6px',
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary,
                                fontSize: '11px',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : '#f9fafb',
                                  borderColor: theme.palette.divider
                                },
                                '&:disabled': {
                                  borderColor: theme.palette.divider,
                                  color: theme.palette.text.secondary
                                }
                              }}
                            >
                              Camera
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AttachFileIcon />}
                              onClick={handleFileAttachment}
                              disabled={!!uploadProgress}
                              sx={{
                                borderRadius: '6px',
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary,
                                fontSize: '11px',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : '#f9fafb',
                                  borderColor: theme.palette.divider
                                },
                                '&:disabled': {
                                  borderColor: theme.palette.divider,
                                  color: theme.palette.text.secondary
                                }
                              }}
                            >
                              Files
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  </UnifiedTabsContainer>

                  {/* Tab Content */}
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      p: 0,
                      minHeight: 0
                    }}
                  >
                    {/* Transcript Tab - Now always available */}
                    {activeTab === 'transcript' && (
                      <Stack
                        spacing={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        {/* Transcript content - header removed, Export action moved to tab line */}

                        <Box
                          ref={transcriptScrollRef}
                          onScroll={handleTranscriptScroll}
                          sx={{ p: 0, overflow: 'auto', height: '100%' }}
                        >
                          <MinimalTranscriptView
                            speakers={speakers}
                            isRecording={state.isRecording}
                            interimTranscript={state.interimTranscript}
                            speakerMapping={speakerMapping}
                            onFindingClick={(_finding) => {
                              setActiveTab('summaries');
                            }}
                          />
                        </Box>

                        {/* New segments indicator */}
                        {hasNewSegments && (
                          <Box
                            onClick={() => scrollToBottom(true)}
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              zIndex: 10,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 2,
                              py: 1,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(37, 99, 235, 0.95)'
                                  : '#2563eb',
                              color: 'white',
                              borderRadius: '24px',
                              cursor: 'pointer',
                              boxShadow:
                                '0 4px 12px rgba(37, 99, 235, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1)',
                              backdropFilter: 'blur(8px)',
                              border:
                                theme.palette.mode === 'dark'
                                  ? '1px solid rgba(255, 255, 255, 0.1)'
                                  : 'none',
                              transition: 'all 0.2s ease',
                              animation: 'slideUp 0.3s ease-out',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(29, 78, 216, 0.95)'
                                    : '#1d4ed8',
                                transform: 'translateX(-50%) translateY(-2px)',
                                boxShadow:
                                  '0 6px 16px rgba(37, 99, 235, 0.5), 0 2px 4px rgba(0, 0, 0, 0.1)'
                              },
                              '&:active': {
                                transform: 'translateX(-50%) translateY(0px)'
                              },
                              '@keyframes slideUp': {
                                '0%': {
                                  opacity: 0,
                                  transform: 'translateX(-50%) translateY(10px)'
                                },
                                '100%': {
                                  opacity: 1,
                                  transform: 'translateX(-50%) translateY(0)'
                                }
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '13px',
                                fontWeight: 500,
                                lineHeight: 1,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              New messages
                            </Typography>
                            <ExpandMoreIcon sx={{ fontSize: 18 }} />
                          </Box>
                        )}
                      </Stack>
                    )}

                    {activeTab === 'summaries' && (
                      <SummariesTab
                        sessionId={sessionId || undefined}
                        summaries={sessionSummaries}
                        isLoading={isSummariesLoading || isSummariesGenerating}
                      />
                    )}

                    {activeTab === 'template' && (
                      <Stack
                        spacing={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0
                        }}
                      >
                        {/* Template content - header removed, actions moved to tab line */}

                        <Box
                          sx={{
                            p: 0,
                            flex: 1,
                            overflow: 'auto',
                            minHeight: 0,
                            maxHeight: '100%',
                            pb: isMobile && canShowFabControls() ? '100px' : 3,
                            // Dark mode scrollbar styling
                            scrollbarWidth: 'thin',
                            scrollbarColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(80, 80, 80, 0.6) transparent'
                                : 'rgba(155, 155, 155, 0.5) transparent',
                            '&::-webkit-scrollbar': {
                              width: '10px',
                              height: '10px'
                            },
                            '&::-webkit-scrollbar-track': {
                              background: 'transparent'
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(80, 80, 80, 0.6)'
                                  : 'rgba(155, 155, 155, 0.5)',
                              borderRadius: '5px',
                              border: '2px solid transparent',
                              backgroundClip: 'padding-box'
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(100, 100, 100, 0.8)'
                                  : 'rgba(155, 155, 155, 0.7)'
                            }
                          }}
                          data-section="questions"
                        >
                          {isLoadingQuestions ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: 4,
                                gap: 2
                              }}
                            >
                              <CircularProgress size={24} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Loading template questions...
                              </Typography>
                            </Box>
                          ) : templateQuestions.length > 0 ? (
                            <>
                              {templateQuestions
                                .slice()
                                .sort((a, b) => {
                                  if (!sortUnansweredFirst) return 0;
                                  // Don't move questions around while user is actively typing in them
                                  if (
                                    typingQuestionId === a.id ||
                                    typingQuestionId === b.id
                                  )
                                    return 0;
                                  const aUnanswered =
                                    !a.isComplete ||
                                    (a.answer || '').trim().length === 0;
                                  const bUnanswered =
                                    !b.isComplete ||
                                    (b.answer || '').trim().length === 0;
                                  if (aUnanswered && !bUnanswered) return -1;
                                  if (!aUnanswered && bUnanswered) return 1;
                                  return 0;
                                })
                                .map((question, questionIndex) => (
                                  <Accordion
                                    key={`${question.id}-${questionIndex}`}
                                    elevation={0}
                                    sx={{
                                      '&:before': { display: 'none' },
                                      mb: 0.25, // Further reduced spacing between accordions
                                      // Remove default expansion margin
                                      '&.Mui-expanded': {
                                        margin: 0,
                                        mb: 0.25
                                      },
                                      // Highlight unanswered questions with subtle yellow background (only after analysis)
                                      ...(analysisComplete &&
                                      (!question.isComplete ||
                                        (question.answer || '').trim()
                                          .length === 0)
                                        ? {
                                            backgroundColor:
                                              theme.palette.mode === 'dark'
                                                ? 'rgba(245, 158, 11, 0.1)'
                                                : '#fffbeb'
                                          }
                                        : {}),
                                      '& .MuiAccordionSummary-root': {
                                        minHeight: 'auto',
                                        py: 0, // Removed vertical padding to show divider line
                                        '&.Mui-expanded': {
                                          minHeight: 'auto'
                                        }
                                      },
                                      '& .MuiAccordionDetails-root': {
                                        padding: '0px 16px 0px 16px', // Removed top and bottom padding to show divider line
                                        backgroundColor: 'inherit'
                                      }
                                    }}
                                  >
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          width: '100%'
                                        }}
                                      >
                                        {question.isComplete ? (
                                          <Box
                                            sx={{
                                              position: 'relative',
                                              width: 16,
                                              height: 16,
                                              borderRadius: '50%',
                                              border: '1px solid',
                                              borderColor:
                                                question.confidence >= 0.8
                                                  ? '#16a34a' // Darker green border for >80%
                                                  : question.confidence >= 0.5
                                                  ? '#d97706' // Darker orange border for 50-80%
                                                  : '#dc2626', // Darker red border for <50%
                                              backgroundColor:
                                                question.confidence >= 0.8
                                                  ? '#dcfce7' // Light green background for >80%
                                                  : question.confidence >= 0.5
                                                  ? '#fef3c7' // Light orange background for 50-80%
                                                  : '#fee2e2', // Light red background for <50%
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                            }}
                                          >
                                            <CheckIcon
                                              sx={{
                                                fontSize: 10,
                                                color:
                                                  question.confidence >= 0.8
                                                    ? '#16a34a' // Darker green check for >80%
                                                    : question.confidence >= 0.5
                                                    ? '#d97706' // Darker orange check for 50-80%
                                                    : '#dc2626' // Darker red check for <50%
                                              }}
                                            />
                                          </Box>
                                        ) : (
                                          <RadioButtonUncheckedIcon
                                            color="disabled"
                                            fontSize="small"
                                          />
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{ flex: 1 }}
                                        >
                                          {question.question}
                                          {question.required && (
                                            <Typography
                                              component="span"
                                              sx={{
                                                color: 'error.main',
                                                ml: 0.5
                                              }}
                                            >
                                              *
                                            </Typography>
                                          )}
                                        </Typography>
                                        {question.isComplete &&
                                          question.confidence < 0.8 && (
                                            <Tooltip
                                              title={
                                                question.confidence >= 0.5
                                                  ? `AI is ${Math.round(
                                                      question.confidence * 100
                                                    )}% confident in this answer. This indicates a reliable response.`
                                                  : `AI is only ${Math.round(
                                                      question.confidence * 100
                                                    )}% confident in this answer. You may want to review and verify this response.`
                                              }
                                              arrow
                                              enterDelay={500}
                                              leaveDelay={200}
                                              placement="top"
                                            >
                                              <Chip
                                                label={`${Math.round(
                                                  question.confidence * 100
                                                )}%`}
                                                size="small"
                                                sx={{
                                                  mr: 1, // Move 8 pixels to the left
                                                  borderRadius: '16px', // Smaller rounded pill design
                                                  height: '20px', // Smaller height
                                                  fontSize: '10px', // Smaller font
                                                  fontWeight: 400, // Thinner text weight
                                                  border: '1px solid', // Thinner border
                                                  backgroundColor:
                                                    question.confidence >= 0.5
                                                      ? '#fef3c7' // Light yellow/orange background for 50-80%
                                                      : '#fee2e2', // Light red background for <50%
                                                  borderColor:
                                                    question.confidence >= 0.5
                                                      ? '#f59e0b' // Orange border for 50-80%
                                                      : '#ef4444', // Red border for <50%
                                                  color:
                                                    question.confidence >= 0.5
                                                      ? '#92400e' // Dark orange text for 50-80%
                                                      : '#dc2626', // Dark red text for <50%
                                                  cursor: 'default', // Default cursor, no question mark
                                                  '&:hover': {
                                                    backgroundColor:
                                                      question.confidence >= 0.5
                                                        ? '#fde68a' // Slightly darker yellow on hover
                                                        : '#fecaca' // Slightly darker red on hover
                                                  },
                                                  '& .MuiChip-label': {
                                                    px: 1,
                                                    py: 0.25
                                                  }
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <TextField
                                        fullWidth
                                        multiline
                                        minRows={1}
                                        maxRows={6}
                                        value={question.answer}
                                        placeholder="Enter your answer or let AI fill this..."
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          // Positioning handled by AccordionDetails padding
                                          '& .MuiOutlinedInput-root': {
                                            backgroundColor:
                                              theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.06)'
                                                : '#fff',
                                            fontSize: '14px' // Set text to 14pt
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '14px' // Ensure input text is 14pt
                                          },
                                          '& .MuiInputBase-input::placeholder':
                                            {
                                              fontSize: '14px',
                                              color: 'text.disabled'
                                            }
                                        }}
                                        onChange={(e) => {
                                          // Mark this question as being actively typed in
                                          setTypingQuestionId(question.id);

                                          // Update the question answer immediately
                                          const updatedQuestions =
                                            templateQuestions.map((q) =>
                                              q.id === question.id
                                                ? {
                                                    ...q,
                                                    answer: e.target.value,
                                                    isComplete:
                                                      e.target.value.trim()
                                                        .length > 0
                                                  }
                                                : q
                                            );
                                          applyTemplateQuestions(
                                            updatedQuestions
                                          );

                                          // Clear typing state after user stops typing
                                          setTimeout(() => {
                                            setTypingQuestionId(null);
                                            console.log(
                                              'Auto-saving template question:',
                                              question.id,
                                              e.target.value
                                            );
                                            // TODO: Implement actual save to backend
                                          }, 1000);
                                        }}
                                      />
                                    </AccordionDetails>
                                  </Accordion>
                                ))}
                            </>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '300px'
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  textAlign: 'center',
                                  fontSize: '14px',
                                  maxWidth: '300px'
                                }}
                              >
                                No template selected yet
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    )}

                    {activeTab === 'speakers' && (
                      <Stack
                        spacing={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {/* Speakers content - header removed, Auto-assign action moved to tab line */}

                        <Box sx={{ p: 3 }}>
                          {speakers.length > 0 ? (
                            <Stack spacing={1}>
                              {/* Identified Speakers */}
                              {Array.from(
                                new Set(
                                  speakers
                                    .filter((s: any) => !(s as any).kind)
                                    .map(
                                      (s: any) => (s as SpeakerSegment).speaker
                                    )
                                )
                              )
                                .filter((speakerIndex) => {
                                  const assignment =
                                    speakerMapping?.speakerAssignments.find(
                                      (a) => a.speakerIndex === speakerIndex
                                    );
                                  return assignment && assignment.personId;
                                })
                                .map((speakerIndex) => {
                                  const assignment =
                                    speakerMapping?.speakerAssignments.find(
                                      (a) => a.speakerIndex === speakerIndex
                                    );
                                  const person = availablePeople.find(
                                    (p) => p.id === assignment?.personId
                                  );
                                  const speakerSegments = speakers
                                    .filter((s: any) => !(s as any).kind)
                                    .filter(
                                      (s: any) =>
                                        (s as SpeakerSegment).speaker ===
                                        speakerIndex
                                    ) as SpeakerSegment[];
                                  const totalDuration = speakerSegments.reduce(
                                    (sum, s) => sum + (s.duration || 0),
                                    0
                                  );

                                  return (
                                    <Box
                                      key={speakerIndex}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        py: 1,
                                        px: 0
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: '6px',
                                          backgroundColor:
                                            theme.palette.mode === 'dark'
                                              ? 'rgba(255, 255, 255, 0.08)'
                                              : '#f3f4f6',
                                          border: `1px solid ${theme.palette.divider}`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: theme.palette.text.secondary,
                                          fontSize: '13px',
                                          fontWeight: 600,
                                          flexShrink: 0
                                        }}
                                      >
                                        {person?.firstName?.charAt(0) || 'S'}
                                        {person?.lastName?.charAt(0) ||
                                          speakerIndex}
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: 'text.primary',
                                            textAlign: 'left',
                                            lineHeight: 1.4
                                          }}
                                        >
                                          {person
                                            ? `${person.firstName} ${person.lastName}`
                                            : `Speaker ${speakerIndex}`}
                                        </Typography>
                                      </Box>
                                      <Typography
                                        sx={{
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          color: 'text.secondary',
                                          flexShrink: 0
                                        }}
                                      >
                                        {Math.floor(totalDuration / 60)}:
                                        {String(
                                          Math.floor(totalDuration % 60)
                                        ).padStart(2, '0')}
                                      </Typography>
                                    </Box>
                                  );
                                })}

                              {/* Unidentified Speakers */}
                              {Array.from(
                                new Set(
                                  speakers
                                    .filter((s: any) => !(s as any).kind)
                                    .map(
                                      (s: any) => (s as SpeakerSegment).speaker
                                    )
                                )
                              ).filter((speakerIndex) => {
                                const assignment =
                                  speakerMapping?.speakerAssignments.find(
                                    (a) => a.speakerIndex === speakerIndex
                                  );
                                return !assignment || !assignment.personId;
                              }).length > 0 && (
                                <Box
                                  sx={{ pt: 2, borderTop: '1px solid #f3f4f6' }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '14px',
                                      color: 'text.secondary',
                                      mb: 2
                                    }}
                                  >
                                    {
                                      Array.from(
                                        new Set(
                                          speakers
                                            .filter(
                                              (s: any) => !(s as any).kind
                                            )
                                            .map(
                                              (s: any) =>
                                                (s as SpeakerSegment).speaker
                                            )
                                        )
                                      ).filter((speakerIndex) => {
                                        const assignment =
                                          speakerMapping?.speakerAssignments.find(
                                            (a) =>
                                              a.speakerIndex === speakerIndex
                                          );
                                        return (
                                          !assignment || !assignment.personId
                                        );
                                      }).length
                                    }{' '}
                                    unidentified speakers →
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '300px'
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  textAlign: 'center',
                                  fontSize: '14px',
                                  maxWidth: '300px'
                                }}
                              >
                                No speakers recorded yet
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    )}

                    {activeTab === 'documents' && sessionId && (
                      <TranscriptionDocumentTab
                        sessionId={sessionId}
                        uploadTrigger={documentUploadTrigger}
                        isUploading={isUploadingDocument}
                      />
                    )}

                    {/* Notes tab - now renders on both desktop and mobile */}
                    {activeTab === 'notes' && (
                      <Stack
                        spacing={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0,
                          py: 1,
                          px: 2.5
                        }}
                      >
                        <NotesInterface session={sessionData} />
                      </Stack>
                    )}

                    {activeTab === 'critical' && shouldStackVertically && (
                      <Box
                        sx={{
                          p: { xs: 2, sm: 3 }, // Less padding on mobile
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'auto' // Make scrollable
                        }}
                      >
                        <CriticalItemsPanel
                          items={criticalItems}
                          onDelete={deleteCriticalItem}
                          title="Critical Items"
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
            {/* Right Column - Notes Sidebar (45% desktop, hidden on mobile) */}
            <Grid
              size={{ xs: 12, md: 5.4 }}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                pr: 0
              }}
            >
              {/* Right Sidebar - Chat */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  maxHeight: '100%',
                  bgcolor: 'background.paper',
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                {/* Right Sidebar Header - Chat Tab */}
                <Box
                  sx={{
                    display: 'flex',
                    px: 4,
                    pt: 0,
                    pb: 0,
                    height: '48px',
                    alignItems: 'center',
                    background: theme.palette.background.default,
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.2)'
                        : theme.palette.divider,
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                      color: theme.palette.primary.main,
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      position: 'relative',
                      mb: '-1px',
                      // Underline accent on the divider line
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-9px',
                        left: 0,
                        right: 0,
                        height: '2px',
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '2px 2px 0 0'
                      }
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        opacity: 0.7
                      }}
                    >
                      <Lightning size={16} weight="regular" />
                    </Box>
                    Ask
                  </Box>
                </Box>

                {/* Chat Content */}
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {effectiveSessionId ? (
                    <SessionChatInterface
                      sessionId={effectiveSessionId}
                      initialThreadId={sessionData?.userThreadId ?? null}
                      compact={true}
                      onTranscriptUpdated={async () => {
                        // Reload the transcript data when a discrepancy is applied
                        if (sessionId && appUser?.id) {
                          try {
                            const sessionData = await getTranscriptionSessionById(
                              sessionId,
                              appUser.id,
                              getAccessTokenSilently,
                              getAccessTokenWithPopup
                            );

                            if (sessionData?.speakerSegments) {
                              // Convert backend segments to frontend format
                              const frontendSegments = sessionData.speakerSegments.map(
                                (segment: any) => ({
                                  id: segment.id,
                                  speaker: segment.speakerIndex || 0,
                                  text: segment.text,
                                  timestamp: segment.startTime || '0:00',
                                  confidence: segment.confidence || 0,
                                  duration:
                                    segment.endTime && segment.startTime
                                      ? parseFloat(segment.endTime) -
                                        parseFloat(segment.startTime)
                                      : 0
                                })
                              );
                              setSpeakers(frontendSegments);
                              console.log(
                                'Transcript reloaded after discrepancy correction'
                              );
                            }
                          } catch (error) {
                            console.error('Failed to reload transcript:', error);
                          }
                        }
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Loading chat...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
          {/* End-of-session checklist dialog */}
          <EndSessionChecklistDialog
            open={checklistOpen}
            onClose={() => setChecklistOpen(false)}
            state={checklistState}
            setState={setChecklistState}
            availableCollections={allAvailableCollections.map((col) => ({
              id: col.id,
              title: col.title,
              referenceNumber: col.referenceNumber
            }))}
            currentCollections={sessionCollections.map((col) => ({
              id: col.id,
              title: col.title,
              referenceNumber: col.referenceNumber
            }))}
            isLoadingCollections={isLoadingAllCollections}
            onGenerate={async (state) => {
              // Add to collection if selected
              if (state.selectedCollectionId && effectiveSessionId && appUser) {
                try {
                  await addSessionToCollection(
                    state.selectedCollectionId,
                    effectiveSessionId,
                    appUser.id,
                    getAccessTokenSilently,
                    getAccessTokenWithPopup
                  );
                  // Reload session collections
                  await reloadSessionCollections();
                } catch (error) {
                  console.error('Error adding session to collection:', error);
                  // Continue anyway - don't block summary generation
                }
              }

              setChecklistOpen(false);
              await stopConversation();
            }}
          />

          {/* Critical item toast */}
          <Snackbar
            open={criticalToastOpen}
            autoHideDuration={4000}
            onClose={() => setCriticalToastOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            message={criticalToastMessage || ''}
            sx={{
              '& .MuiSnackbarContent-root': {
                backgroundColor: '#111827',
                color: 'white',
                borderRadius: '8px'
              }
            }}
          />
        </>
      ) : (
        // Mobile Layout - Clean and minimal
        <>
          {/* Connection status - minimal indicator */}
          {state.connectionStatus === 'Error' && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'error.main',
                boxShadow: '0 0 6px rgba(255, 0, 0, 0.5)'
              }}
            />
          )}

          {/* Processing/Completion Callout - Notion Style (mobile) */}
          {(() => {
            // Don't show if we're in a final state (complete, idle, or error)
            if (pipelineStage === 'complete' || pipelineStage === 'idle' || pipelineStage === 'error') {
              // Exception: show completion message if analysis is done and there are questions
              const shouldShowCompletion =
                analysisComplete && templateQuestions.length > 0;
              
              if (!shouldShowCompletion || dismissedCallout || !sessionData || !processingCalloutCopy) {
                return false;
              }
            }
            
            // Show if actually processing
            const isActuallyProcessing =
              pipelineStage === 'uploading' ||
              pipelineStage === 'transcribing' ||
              pipelineStage === 'analyzing';
            
            // Also check if analyzing via other means (but not if pipeline is complete)
            const isAnalyzingViaOtherMeans =
              (isAnalyzingTranscript ||
                sessionData?.status === TranscriptionSessionStatus.Processing) &&
              pipelineStage !== 'complete';
            
            // Only show completion message if analysis is done and there are questions
            const shouldShowCompletion =
              analysisComplete && templateQuestions.length > 0;

            const shouldShow =
              !dismissedCallout &&
              sessionData && // Only show if session data is loaded
              (isActuallyProcessing || isAnalyzingViaOtherMeans || shouldShowCompletion) &&
              processingCalloutCopy;

            return shouldShow;
          })() && (
              <Box
                sx={{
                  mx: 1, // Align with main content container (tabs area)
                  mt: 1, // 8px top padding
                  mb: 1, // Ensure bottom border is visible and not visually cut off
                  minHeight: 32, // Compact height to match desktop callout
                  position: 'relative',
                  backgroundColor: analysisComplete
                    ? allQuestionsAnswered
                      ? theme.palette.mode === 'dark'
                        ? 'rgba(34, 197, 94, 0.1)'
                        : '#f0fdf4'
                      : theme.palette.mode === 'dark'
                      ? 'rgba(245, 158, 11, 0.1)'
                      : '#fffbeb' // Green or yellow
                    : theme.palette.mode === 'dark'
                    ? 'rgba(96, 165, 250, 0.1)'
                    : '#f0f9ff', // Blue for processing
                  border: analysisComplete
                    ? allQuestionsAnswered
                      ? theme.palette.mode === 'dark'
                        ? '1px solid rgba(34, 197, 94, 0.4)'
                        : '1px solid #22c55e'
                      : theme.palette.mode === 'dark'
                      ? '1px solid rgba(245, 158, 11, 0.4)'
                      : '1px solid #f59e0b' // Green or yellow border
                    : theme.palette.mode === 'dark'
                    ? '1px solid rgba(96, 165, 250, 0.4)'
                    : '1px solid #60a5fa', // Blue border for processing
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flexShrink: 0
                  // Removed transition and animation to prevent re-rendering
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 0.75,
                    px: 2
                  }}
                >
                  {analysisComplete ? (
                    allQuestionsAnswered ? (
                      <CheckCircleIcon
                        sx={{
                          color:
                            theme.palette.mode === 'dark'
                              ? '#86efac'
                              : '#22c55e',
                          fontSize: 20,
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <WarningIcon
                        sx={{
                          color:
                            theme.palette.mode === 'dark'
                              ? '#fbbf24'
                              : '#f59e0b',
                          fontSize: 20,
                          flexShrink: 0
                        }}
                      />
                    )
                  ) : (
                    <AutoAwesomeIcon
                      sx={{
                        color:
                          theme.palette.mode === 'dark' ? '#93c5fd' : '#60a5fa',
                        fontSize: 20,
                        flexShrink: 0
                      }}
                    />
                  )}
                  <Typography
                    variant="body1"
                    sx={{
                      color: analysisComplete
                        ? allQuestionsAnswered
                          ? theme.palette.mode === 'dark'
                            ? '#86efac'
                            : '#15803d'
                          : theme.palette.mode === 'dark'
                          ? '#fbbf24'
                          : '#d97706' // Green or yellow text
                        : theme.palette.mode === 'dark'
                        ? '#93c5fd'
                        : '#2563eb', // Blue text for processing
                      fontSize: '14px',
                      fontWeight: 500,
                      flex: 1,
                      textAlign: 'left',
                      // Add fade animation when transcribing
                      ...(pipelineStage === 'transcribing' && {
                        animation: 'fadeInOut 3s ease-in-out infinite',
                        '@keyframes fadeInOut': {
                          '0%': { opacity: 0.7 },
                          '50%': { opacity: 1 },
                          '100%': { opacity: 0.7 }
                        }
                      })
                    }}
                  >
                    {processingCalloutCopy}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* Dismiss Button for all callouts */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Closing callout (mobile)');
                        setDismissedCallout(true);
                        // Persist dismissal state
                        if (effectiveSessionId) {
                          localStorage.setItem(
                            `intakeai_callout_dismissed_${effectiveSessionId}`,
                            'true'
                          );
                        }
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        color: analysisComplete
                          ? allQuestionsAnswered
                            ? theme.palette.mode === 'dark'
                              ? '#86efac'
                              : '#15803d'
                            : theme.palette.mode === 'dark'
                            ? '#fbbf24'
                            : '#d97706'
                          : theme.palette.mode === 'dark'
                          ? '#93c5fd'
                          : '#2563eb',
                        '&:hover': {
                          backgroundColor: analysisComplete
                            ? allQuestionsAnswered
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(34, 197, 94, 0.2)'
                                : '#dcfce7'
                              : theme.palette.mode === 'dark'
                              ? 'rgba(245, 158, 11, 0.2)'
                              : '#fef3c7'
                            : theme.palette.mode === 'dark'
                            ? 'rgba(96, 165, 250, 0.2)'
                            : '#dbeafe'
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}

          {/* Next Actions UI for Success Callout - Unified with callout (mobile) */}
          {/* Next actions for mobile removed as requested */}

          {/* Mobile Main Content - Single column with tabs only */}
          <Grid
            container
            spacing={2}
            sx={{ flex: 1, minHeight: 0, maxHeight: '100%', px: 1 }}
          >
            <Grid
              size={12}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%'
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  maxHeight: '100%',
                  bgcolor: 'background.paper',
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}
                >
                  {/* Consolidated Tab Headers with UnifiedTabs */}
                  <UnifiedTabsContainer>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: '48px'
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          '& > .MuiBox-root': { borderBottom: 'none' }
                        }}
                      >
                        <UnifiedTabs
                          tabs={displayedTabs.map((t) => ({
                            key: t.key,
                            label: t.label,
                            icon: t.icon,
                            count: 
                              t.key === 'summaries' ? sessionSummaries.length :
                              t.key === 'documents' ? (sessionData?.companyDocuments?.length || 0) :
                              t.key === 'speakers' ? new Set(speakers.filter((s: any) => !(s as any).kind).map((s: any) => (s as SpeakerSegment).speaker)).size :
                              undefined
                          }))}
                          activeTab={activeTab}
                          onTabChange={(key) =>
                            setActiveTab(key as ActiveTabType)
                          }
                          maxVisibleTabs={mobileMaxVisibleTabs}
                          fitToContainer
                          minVisibleTabs={1}
                        />
                      </Box>

                      {/* Tab-specific Action Buttons - Shown on all sizes */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexShrink: 0,
                          gap: 1,
                          alignItems: 'center',
                          minHeight: '32px',
                          px: 1
                        }}
                      >
                        {activeTab === 'template' && (
                          <>
                            {templateQuestions.length > 0 &&
                              unansweredQuestions.length > 0 &&
                              analysisComplete && (
                                <Button
                                  variant="text"
                                  size="small"
                                  startIcon={<SortIcon />}
                                  onClick={() =>
                                    setSortUnansweredFirst(!sortUnansweredFirst)
                                  }
                                  sx={{
                                    fontSize: '11px',
                                    color: sortUnansweredFirst
                                      ? '#3b82f6'
                                      : '#6b7280',
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    px: 1,
                                    '&:hover': {
                                      backgroundColor: sortUnansweredFirst
                                        ? '#eff6ff'
                                        : '#f3f4f6'
                                    }
                                  }}
                                >
                                  {sortUnansweredFirst ? 'Default' : 'Sort'}
                                </Button>
                              )}
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<FileText size={16} />}
                              disabled={state.isRecording}
                              onClick={() => {
                                // Check if changing template would cause data loss
                                if (hasProgressToLose()) {
                                  setShowTemplateChangeWarning(true);
                                } else {
                                  loadAvailableTemplates();
                                  setShowTemplatePicker(true);
                                }
                              }}
                              sx={{
                                borderRadius: '6px',
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary,
                                fontSize: '11px',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : '#f9fafb',
                                  borderColor: theme.palette.divider
                                },
                                '&:disabled': {
                                  borderColor: theme.palette.divider,
                                  color: theme.palette.text.secondary
                                }
                              }}
                            >
                              Select Template
                            </Button>
                          </>
                        )}
                        {activeTab === 'transcript' && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={handleDownloadTranscript}
                            disabled={isDownloading}
                            sx={{
                              borderRadius: '6px',
                              borderColor: theme.palette.divider,
                              color: theme.palette.text.secondary,
                              fontSize: '11px',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : '#f9fafb',
                                borderColor: theme.palette.divider
                              },
                              '&:disabled': {
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary
                              }
                            }}
                          >
                            {isDownloading ? 'Exporting...' : 'Export'}
                          </Button>
                        )}
                        {activeTab === 'speakers' && (
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              borderRadius: '6px',
                              borderColor: theme.palette.divider,
                              color: theme.palette.text.secondary,
                              fontSize: '11px',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : '#f9fafb',
                                borderColor: theme.palette.divider
                              }
                            }}
                          >
                            Auto-assign
                          </Button>
                        )}
                        {activeTab === 'documents' && (
                          <>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CameraIcon />}
                              onClick={handleCameraCapture}
                              disabled={!!uploadProgress}
                              sx={{
                                borderRadius: '6px',
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary,
                                fontSize: '11px',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : '#f9fafb',
                                  borderColor: theme.palette.divider
                                },
                                '&:disabled': {
                                  borderColor: theme.palette.divider,
                                  color: theme.palette.text.secondary
                                }
                              }}
                            >
                              Camera
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AttachFileIcon />}
                              onClick={handleFileAttachment}
                              disabled={!!uploadProgress}
                              sx={{
                                borderRadius: '6px',
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary,
                                fontSize: '11px',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : '#f9fafb',
                                  borderColor: theme.palette.divider
                                },
                                '&:disabled': {
                                  borderColor: theme.palette.divider,
                                  color: theme.palette.text.secondary
                                }
                              }}
                            >
                              Files
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  </UnifiedTabsContainer>

                  {/* Tab Content */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'auto',
                      p: 1,
                      minHeight: 0
                    }}
                  >
                    {/* All tab content for mobile */}
                    {activeTab === 'transcript' && (
                      <Stack
                        spacing={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        {/* Transcript content - header removed, Export action moved to tab line */}

                        <Box
                          ref={transcriptScrollRef}
                          onScroll={handleTranscriptScroll}
                          sx={{ p: 0, overflow: 'auto', height: '100%' }}
                        >
                          <MinimalTranscriptView
                            speakers={speakers}
                            isRecording={state.isRecording}
                            interimTranscript={state.interimTranscript}
                            speakerMapping={speakerMapping}
                            onFindingClick={(_finding) => {
                              setActiveTab('summaries');
                            }}
                          />
                        </Box>

                        {/* New segments indicator */}
                        {hasNewSegments && (
                          <Box
                            onClick={() => scrollToBottom(true)}
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              zIndex: 10,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 2,
                              py: 1,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(37, 99, 235, 0.95)'
                                  : '#2563eb',
                              color: 'white',
                              borderRadius: '24px',
                              cursor: 'pointer',
                              boxShadow:
                                '0 4px 12px rgba(37, 99, 235, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1)',
                              backdropFilter: 'blur(8px)',
                              border:
                                theme.palette.mode === 'dark'
                                  ? '1px solid rgba(255, 255, 255, 0.1)'
                                  : 'none',
                              transition: 'all 0.2s ease',
                              animation: 'slideUp 0.3s ease-out',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(29, 78, 216, 0.95)'
                                    : '#1d4ed8',
                                transform: 'translateX(-50%) translateY(-2px)',
                                boxShadow:
                                  '0 6px 16px rgba(37, 99, 235, 0.5), 0 2px 4px rgba(0, 0, 0, 0.1)'
                              },
                              '&:active': {
                                transform: 'translateX(-50%) translateY(0px)'
                              },
                              '@keyframes slideUp': {
                                '0%': {
                                  opacity: 0,
                                  transform: 'translateX(-50%) translateY(10px)'
                                },
                                '100%': {
                                  opacity: 1,
                                  transform: 'translateX(-50%) translateY(0)'
                                }
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '13px',
                                fontWeight: 500,
                                lineHeight: 1,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              New messages
                            </Typography>
                            <ExpandMoreIcon sx={{ fontSize: 18 }} />
                          </Box>
                        )}
                      </Stack>
                    )}

                    {activeTab === 'summaries' && (
                      <SummariesTab
                        sessionId={sessionId || undefined}
                        summaries={sessionSummaries}
                        isLoading={isSummariesLoading || isSummariesGenerating}
                      />
                    )}

                    {activeTab === 'template' && (
                      <Stack
                        spacing={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0
                        }}
                      >
                        {/* Template content - header removed, actions moved to tab line */}

                        <Box
                          sx={{
                            p: 0,
                            flex: 1,
                            overflow: 'auto',
                            minHeight: 0,
                            maxHeight: '100%',
                            pb: isMobile && canShowFabControls() ? '100px' : 3,
                            // Dark mode scrollbar styling
                            scrollbarWidth: 'thin',
                            scrollbarColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(80, 80, 80, 0.6) transparent'
                                : 'rgba(155, 155, 155, 0.5) transparent',
                            '&::-webkit-scrollbar': {
                              width: '10px',
                              height: '10px'
                            },
                            '&::-webkit-scrollbar-track': {
                              background: 'transparent'
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(80, 80, 80, 0.6)'
                                  : 'rgba(155, 155, 155, 0.5)',
                              borderRadius: '5px',
                              border: '2px solid transparent',
                              backgroundClip: 'padding-box'
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(100, 100, 100, 0.8)'
                                  : 'rgba(155, 155, 155, 0.7)'
                            }
                          }}
                          data-section="questions"
                        >
                          {isLoadingQuestions ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: 4,
                                gap: 2
                              }}
                            >
                              <CircularProgress size={24} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Loading template questions...
                              </Typography>
                            </Box>
                          ) : templateQuestions.length > 0 ? (
                            <>
                              {templateQuestions
                                .slice()
                                .sort((a, b) => {
                                  if (!sortUnansweredFirst) return 0;
                                  // Don't move questions around while user is actively typing in them
                                  if (
                                    typingQuestionId === a.id ||
                                    typingQuestionId === b.id
                                  )
                                    return 0;
                                  const aUnanswered =
                                    !a.isComplete ||
                                    (a.answer || '').trim().length === 0;
                                  const bUnanswered =
                                    !b.isComplete ||
                                    (b.answer || '').trim().length === 0;
                                  if (aUnanswered && !bUnanswered) return -1;
                                  if (!aUnanswered && bUnanswered) return 1;
                                  return 0;
                                })
                                .map((question, questionIndex) => (
                                  <Accordion
                                    key={`${question.id}-${questionIndex}`}
                                    elevation={0}
                                    sx={{
                                      '&:before': { display: 'none' },
                                      mb: 0.25, // Further reduced spacing between accordions
                                      // Remove default expansion margin
                                      '&.Mui-expanded': {
                                        margin: 0,
                                        mb: 0.25
                                      },
                                      // Highlight unanswered questions with subtle yellow background (only after analysis)
                                      ...(analysisComplete &&
                                      (!question.isComplete ||
                                        (question.answer || '').trim()
                                          .length === 0)
                                        ? {
                                            backgroundColor:
                                              theme.palette.mode === 'dark'
                                                ? 'rgba(245, 158, 11, 0.1)'
                                                : '#fffbeb'
                                          }
                                        : {}),
                                      '& .MuiAccordionSummary-root': {
                                        minHeight: 'auto',
                                        py: 0, // Removed vertical padding to show divider line
                                        '&.Mui-expanded': {
                                          minHeight: 'auto'
                                        }
                                      },
                                      '& .MuiAccordionDetails-root': {
                                        padding: '0px 16px 0px 16px', // Removed top and bottom padding to show divider line
                                        backgroundColor: 'inherit'
                                      }
                                    }}
                                  >
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          width: '100%'
                                        }}
                                      >
                                        {question.isComplete ? (
                                          <Box
                                            sx={{
                                              position: 'relative',
                                              width: 16,
                                              height: 16,
                                              borderRadius: '50%',
                                              border: '1px solid',
                                              borderColor:
                                                question.confidence >= 0.8
                                                  ? '#16a34a' // Darker green border for >80%
                                                  : question.confidence >= 0.5
                                                  ? '#d97706' // Darker orange border for 50-80%
                                                  : '#dc2626', // Darker red border for <50%
                                              backgroundColor:
                                                question.confidence >= 0.8
                                                  ? '#dcfce7' // Light green background for >80%
                                                  : question.confidence >= 0.5
                                                  ? '#fef3c7' // Light orange background for 50-80%
                                                  : '#fee2e2', // Light red background for <50%
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                            }}
                                          >
                                            <CheckIcon
                                              sx={{
                                                fontSize: 10,
                                                color:
                                                  question.confidence >= 0.8
                                                    ? '#16a34a' // Darker green check for >80%
                                                    : question.confidence >= 0.5
                                                    ? '#d97706' // Darker orange check for 50-80%
                                                    : '#dc2626' // Darker red check for <50%
                                              }}
                                            />
                                          </Box>
                                        ) : (
                                          <RadioButtonUncheckedIcon
                                            color="disabled"
                                            fontSize="small"
                                          />
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{ flex: 1 }}
                                        >
                                          {question.question}
                                          {question.required && (
                                            <Typography
                                              component="span"
                                              sx={{
                                                color: 'error.main',
                                                ml: 0.5
                                              }}
                                            >
                                              *
                                            </Typography>
                                          )}
                                        </Typography>
                                        {question.isComplete &&
                                          question.confidence < 0.8 && (
                                            <Tooltip
                                              title={
                                                question.confidence >= 0.5
                                                  ? `AI is ${Math.round(
                                                      question.confidence * 100
                                                    )}% confident in this answer. This indicates a reliable response.`
                                                  : `AI is only ${Math.round(
                                                      question.confidence * 100
                                                    )}% confident in this answer. You may want to review and verify this response.`
                                              }
                                              arrow
                                              enterDelay={500}
                                              leaveDelay={200}
                                              placement="top"
                                            >
                                              <Chip
                                                label={`${Math.round(
                                                  question.confidence * 100
                                                )}%`}
                                                size="small"
                                                sx={{
                                                  mr: 1, // Move 8 pixels to the left
                                                  borderRadius: '16px', // Smaller rounded pill design
                                                  height: '20px', // Smaller height
                                                  fontSize: '10px', // Smaller font
                                                  fontWeight: 400, // Thinner text weight
                                                  border: '1px solid', // Thinner border
                                                  backgroundColor:
                                                    question.confidence >= 0.5
                                                      ? '#fef3c7' // Light yellow/orange background for 50-80%
                                                      : '#fee2e2', // Light red background for <50%
                                                  borderColor:
                                                    question.confidence >= 0.5
                                                      ? '#f59e0b' // Orange border for 50-80%
                                                      : '#ef4444', // Red border for <50%
                                                  color:
                                                    question.confidence >= 0.5
                                                      ? '#92400e' // Dark orange text for 50-80%
                                                      : '#dc2626', // Dark red text for <50%
                                                  cursor: 'default', // Default cursor, no question mark
                                                  '&:hover': {
                                                    backgroundColor:
                                                      question.confidence >= 0.5
                                                        ? '#fde68a' // Slightly darker yellow on hover
                                                        : '#fecaca' // Slightly darker red on hover
                                                  },
                                                  '& .MuiChip-label': {
                                                    px: 1,
                                                    py: 0.25
                                                  }
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <TextField
                                        fullWidth
                                        multiline
                                        minRows={1}
                                        maxRows={6}
                                        value={question.answer}
                                        placeholder="Enter your answer or let AI fill this..."
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          // Positioning handled by AccordionDetails padding
                                          '& .MuiOutlinedInput-root': {
                                            backgroundColor:
                                              theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.06)'
                                                : '#fff',
                                            fontSize: '14px' // Set text to 14pt
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '14px' // Ensure input text is 14pt
                                          },
                                          '& .MuiInputBase-input::placeholder':
                                            {
                                              fontSize: '14px',
                                              color: 'text.disabled'
                                            }
                                        }}
                                        onChange={(e) => {
                                          // Mark this question as being actively typed in
                                          setTypingQuestionId(question.id);

                                          // Update the question answer immediately
                                          const updatedQuestions =
                                            templateQuestions.map((q) =>
                                              q.id === question.id
                                                ? {
                                                    ...q,
                                                    answer: e.target.value,
                                                    isComplete:
                                                      e.target.value.trim()
                                                        .length > 0
                                                  }
                                                : q
                                            );
                                          applyTemplateQuestions(
                                            updatedQuestions
                                          );

                                          // Clear typing state after user stops typing
                                          setTimeout(() => {
                                            setTypingQuestionId(null);
                                            console.log(
                                              'Auto-saving template question:',
                                              question.id,
                                              e.target.value
                                            );
                                            // TODO: Implement actual save to backend
                                          }, 1000);
                                        }}
                                      />
                                    </AccordionDetails>
                                  </Accordion>
                                ))}
                            </>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '300px'
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  textAlign: 'center',
                                  fontSize: '14px',
                                  maxWidth: '300px'
                                }}
                              >
                                No template selected yet
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    )}

                    {activeTab === 'speakers' && (
                      <Stack
                        spacing={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {/* Speakers content - header removed, Auto-assign action moved to tab line */}

                        <Box sx={{ p: 3 }}>
                          {speakers.length > 0 ? (
                            <Stack spacing={1}>
                              {/* Identified Speakers */}
                              {Array.from(
                                new Set(
                                  speakers
                                    .filter((s: any) => !(s as any).kind)
                                    .map(
                                      (s: any) => (s as SpeakerSegment).speaker
                                    )
                                )
                              )
                                .filter((speakerIndex) => {
                                  const assignment =
                                    speakerMapping?.speakerAssignments.find(
                                      (a) => a.speakerIndex === speakerIndex
                                    );
                                  return assignment && assignment.personId;
                                })
                                .map((speakerIndex) => {
                                  const assignment =
                                    speakerMapping?.speakerAssignments.find(
                                      (a) => a.speakerIndex === speakerIndex
                                    );
                                  const person = availablePeople.find(
                                    (p) => p.id === assignment?.personId
                                  );
                                  const speakerSegments = speakers
                                    .filter((s: any) => !(s as any).kind)
                                    .filter(
                                      (s: any) =>
                                        (s as SpeakerSegment).speaker ===
                                        speakerIndex
                                    ) as SpeakerSegment[];
                                  const totalDuration = speakerSegments.reduce(
                                    (sum, s) => sum + (s.duration || 0),
                                    0
                                  );

                                  return (
                                    <Box
                                      key={speakerIndex}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        py: 1,
                                        px: 0
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: '6px',
                                          backgroundColor:
                                            theme.palette.mode === 'dark'
                                              ? 'rgba(255, 255, 255, 0.08)'
                                              : '#f3f4f6',
                                          border: `1px solid ${theme.palette.divider}`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: theme.palette.text.secondary,
                                          fontSize: '13px',
                                          fontWeight: 600,
                                          flexShrink: 0
                                        }}
                                      >
                                        {person?.firstName?.charAt(0) || 'S'}
                                        {person?.lastName?.charAt(0) ||
                                          speakerIndex}
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: 'text.primary',
                                            textAlign: 'left',
                                            lineHeight: 1.4
                                          }}
                                        >
                                          {person
                                            ? `${person.firstName} ${person.lastName}`
                                            : `Speaker ${speakerIndex}`}
                                        </Typography>
                                      </Box>
                                      <Typography
                                        sx={{
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          color: 'text.secondary',
                                          flexShrink: 0
                                        }}
                                      >
                                        {Math.floor(totalDuration / 60)}:
                                        {String(
                                          Math.floor(totalDuration % 60)
                                        ).padStart(2, '0')}
                                      </Typography>
                                    </Box>
                                  );
                                })}

                              {/* Unidentified Speakers */}
                              {Array.from(
                                new Set(
                                  speakers
                                    .filter((s: any) => !(s as any).kind)
                                    .map(
                                      (s: any) => (s as SpeakerSegment).speaker
                                    )
                                )
                              ).filter((speakerIndex) => {
                                const assignment =
                                  speakerMapping?.speakerAssignments.find(
                                    (a) => a.speakerIndex === speakerIndex
                                  );
                                return !assignment || !assignment.personId;
                              }).length > 0 && (
                                <Box
                                  sx={{ pt: 2, borderTop: '1px solid #f3f4f6' }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '14px',
                                      color: 'text.secondary',
                                      mb: 2
                                    }}
                                  >
                                    {
                                      Array.from(
                                        new Set(
                                          speakers
                                            .filter(
                                              (s: any) => !(s as any).kind
                                            )
                                            .map(
                                              (s: any) =>
                                                (s as SpeakerSegment).speaker
                                            )
                                        )
                                      ).filter((speakerIndex) => {
                                        const assignment =
                                          speakerMapping?.speakerAssignments.find(
                                            (a) =>
                                              a.speakerIndex === speakerIndex
                                          );
                                        return (
                                          !assignment || !assignment.personId
                                        );
                                      }).length
                                    }{' '}
                                    unidentified speakers →
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '300px'
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  textAlign: 'center',
                                  fontSize: '14px',
                                  maxWidth: '300px'
                                }}
                              >
                                No speakers recorded yet
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    )}

                    {activeTab === 'documents' && sessionId && (
                      <TranscriptionDocumentTab
                        sessionId={sessionId}
                        uploadTrigger={documentUploadTrigger}
                        isUploading={isUploadingDocument}
                      />
                    )}

                    {activeTab === 'notes' && (
                      <Stack
                        spacing={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0,
                          py: 1,
                          px: 2.5
                        }}
                      >
                        <NotesInterface session={sessionData} />
                      </Stack>
                    )}

                    {activeTab === 'critical' && (
                      <Box
                        sx={{
                          p: { xs: 2, sm: 3 },
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'auto' // Make scrollable
                        }}
                      >
                        <CriticalItemsPanel
                          items={criticalItems}
                          onDelete={deleteCriticalItem}
                          compact={true}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )}


      {/* Error Display */}
      {state.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {' '}
          <AlertTitle>Transcription Error</AlertTitle> {state.error}{' '}
        </Alert>
      )}

      {/* Removed duplicate boxes - using proper inline callout above tabs */}

      {/* Template Change Warning Dialog */}
      <Dialog
        open={showTemplateChangeWarning}
        onClose={() => setShowTemplateChangeWarning(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Change template?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will replace your current questions. Any progress will be lost:
          </Typography>

          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 0.5 } }}>
            {speakers.length > 0 && (
              <Typography component="li" variant="body2" color="text.secondary">
                Transcript content ({speakers.length} entries)
              </Typography>
            )}
            {templateQuestions.some(
              (q) => q.isComplete || (q.answer && q.answer.trim().length > 0)
            ) && (
              <Typography component="li" variant="body2" color="text.secondary">
                Answered questions (
                {
                  templateQuestions.filter(
                    (q) =>
                      q.isComplete || (q.answer && q.answer.trim().length > 0)
                  ).length
                }{' '}
                responses)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowTemplateChangeWarning(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowTemplateChangeWarning(false);
              loadAvailableTemplates();
              setShowTemplatePicker(true);
            }}
            variant="contained"
          >
            Change template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Picker Dialog */}
      <Dialog
        open={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a template to use for this session
          </Typography>
          <TextField
            placeholder="Search templates..."
            fullWidth
            size="small"
            value={templateSearchQuery}
            onChange={(e) => setTemplateSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <Box
                  sx={{
                    mr: 1,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </Box>
              )
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              maxHeight: '400px',
              overflow: 'auto'
            }}
          >
            {availableTemplates
              .filter((t) =>
                t.name.toLowerCase().includes(templateSearchQuery.toLowerCase())
              )
              .map((template) => (
                <Button
                  key={template.id}
                  variant="outlined"
                  onClick={() => handleSelectTemplate(template)}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1.5,
                    px: 2,
                    borderRadius: '6px',
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    backgroundColor:
                      template.name === templateName
                        ? theme.palette.action.selected
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : '#f9fafb',
                      borderColor: theme.palette.divider
                    }
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {template.name}
                    </Typography>
                    {template.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {template.description}
                      </Typography>
                    )}
                  </Box>
                </Button>
              ))}
            {availableTemplates.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 4 }}
              >
                No templates available
              </Typography>
            )}
            {availableTemplates.length > 0 &&
              availableTemplates.filter((t) =>
                t.name.toLowerCase().includes(templateSearchQuery.toLowerCase())
              ).length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 4 }}
                >
                  No templates match your search
                </Typography>
              )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Speaker Identity Dialog */}
      <SpeakerIdentityDialog
        open={showSpeakerDialog}
        onClose={() => setShowSpeakerDialog(false)}
        onConfirm={async (mappings) => {
          try {
            // Apply speaker mappings using the speaker service
            for (const mapping of mappings) {
              await speakerService.assignPersonToSpeaker(sessionId!, {
                speakerIndex: mapping.speakerIndex,
                personId: mapping.personId,
                isManuallyAssigned: true,
                assignmentMethod: 'dialog_confirmation'
              });
            }

            // Refresh speaker mapping after assignments
            if (sessionId) {
              const updatedMapping = await speakerService.getSessionSpeakers(
                sessionId
              );
              setSpeakerMapping(updatedMapping);
            }
          } catch (error) {
            console.error('Failed to confirm speaker mappings:', error);
          }
        }}
        currentSegments={currentSegments}
        availablePeople={availablePeople}
        previousAssignments={previousAssignments}
      />

      {/* Success notification for status updates */}
      <Snackbar
        open={statusUpdateSuccess}
        autoHideDuration={3000}
        onClose={() => setStatusUpdateSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setStatusUpdateSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Session status updated successfully!
        </Alert>
      </Snackbar>

      {/* Header Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        PaperProps={{
          sx: {
            minWidth: 220,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '6px'
          }
        }}
        MenuListProps={{
          sx: {
            py: 1
          }
        }}
      >
        {/* Actions Section (moved to top) */}
        <MenuItem
          disabled
          sx={{
            opacity: 0.7,
            fontWeight: 600,
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'text.secondary',
            py: 0.5,
            px: 2,
            minHeight: 'auto'
          }}
        >
          Actions
        </MenuItem>

        {/* Tab-specific actions - Mobile only (always visible regardless of active tab).
            Note: MUI Menu does not support Fragment children, so each item is conditionally
            rendered without wrapping in a Fragment to avoid console warnings. */}
        {isMobile && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              if (hasProgressToLose()) {
                setShowTemplateChangeWarning(true);
              } else {
                loadAvailableTemplates();
                setShowTemplatePicker(true);
              }
            }}
            disabled={state.isRecording}
            sx={{
              py: 1,
              px: 2,
              minHeight: 'auto',
              fontSize: '13px'
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <FileText size={18} weight="light" />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
            >
              Select Template
            </ListItemText>
          </MenuItem>
        )}

        {isMobile && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              handleDownloadTranscript();
            }}
            disabled={isDownloading}
            sx={{
              py: 1,
              px: 2,
              minHeight: 'auto',
              fontSize: '13px'
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
            >
              {isDownloading ? 'Exporting...' : 'Export Transcript'}
            </ListItemText>
          </MenuItem>
        )}

        {isMobile && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              handleCameraCapture();
            }}
            disabled={!!uploadProgress}
            sx={{
              py: 1,
              px: 2,
              minHeight: 'auto',
              fontSize: '13px'
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CameraIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
            >
              Take Photo
            </ListItemText>
          </MenuItem>
        )}

        {isMobile && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              handleFileAttachment();
            }}
            disabled={!!uploadProgress}
            sx={{
              py: 1,
              px: 2,
              minHeight: 'auto',
              fontSize: '13px'
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AttachFileIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
            >
              Upload Files
            </ListItemText>
          </MenuItem>
        )}

        {isMobile && <Divider sx={{ my: 1 }} />}

        {/* Collections - Show on mobile, hidden on desktop (desktop has inline chip) */}
        {isMobile && effectiveSessionId && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              setMobileCollectionDrawerOpen(true);
            }}
            sx={{
              py: 1,
              px: 2,
              minHeight: 'auto',
              fontSize: '13px'
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Folders size={18} weight="light" />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
            >
              Add to Folder
              {sessionCollections.length > 0 && (
                <Chip
                  label={sessionCollections.length}
                  size="small"
                  sx={{ ml: 1, height: 20, fontSize: '11px' }}
                />
              )}
            </ListItemText>
          </MenuItem>
        )}

        {/* Hide/Show Audio Controls - Desktop/Tablet inline controls; FAB toggle on mobile/medium */}
        <MenuItem
          onClick={() => {
            if (!shouldUseFabToggle) {
              // Desktop/Tablet (wide content): Show/hide top recording controls
              setShowRecordingControls((prev) => {
                const next = !prev;
                setRecordingControlsOverride(next ? 'show' : 'hide');
                return next;
              });

              // Auto-scroll to recording interface when showing controls
              if (!showRecordingControls && recordingInterfaceRef.current) {
                recordingInterfaceRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            } else {
              // Mobile/Medium: Toggle FAB visibility
              setShowFabControls((prev) => {
                const next = !prev;
                setFabVisibilityOverride(next ? 'show' : 'hide');
                return next;
              });
            }
            setMenuAnchorEl(null);
          }}
          sx={{
            py: 1,
            px: 2,
            minHeight: 'auto',
            fontSize: '13px'
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            {shouldUseFabToggle ? (
              <MicOutlinedIcon sx={{ fontSize: 18 }} />
            ) : showRecordingControls ? (
              <CloseIcon sx={{ fontSize: 18 }} />
            ) : (
              <AddOutlinedIcon sx={{ fontSize: 18 }} />
            )}
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
          >
            {shouldUseFabToggle
              ? showFabControls
                ? 'Hide Recording FAB'
                : 'Show Recording FAB'
              : showRecordingControls
              ? 'Hide Audio'
              : 'Add Audio'}
          </ListItemText>
        </MenuItem>

        {/* Share Session - Hidden for now */}
        {/* {(sessionSummaries.length === 0 || isMobile) && [
          <MenuItem
            key="share"
            onClick={async (e) => {
              setShareAnchorEl(e.currentTarget);
              setMenuAnchorEl(null);
              // Load distribution groups if needed
              if (availableDistributionGroups.length === 0 && templateId) {
                setIsLoadingDistributionGroups(true);
                try {
                  const groups = await getAvailableDistributionGroups(
                    parseInt(templateId),
                    selectedUserRole!.companyId,
                    getAccessTokenSilently,
                    getAccessTokenWithPopup
                  );
                  setAvailableDistributionGroups(groups);
                } catch (error) {
                  console.error('Error loading distribution groups:', error);
                } finally {
                  setIsLoadingDistributionGroups(false);
                }
              }
            }}
            sx={{
              py: 1,
              px: 2,
              minHeight: 'auto',
              fontSize: '13px'
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ShareOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
            >
              Share Session
            </ListItemText>
          </MenuItem>
        ]} */}

        {/* Download Section */}
        <MenuItem
          disabled
          sx={{
            opacity: 0.7,
            fontWeight: 600,
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'text.secondary',
            py: 0.5,
            px: 2,
            minHeight: 'auto'
          }}
        >
          Download
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleDownloadTranscript();
            setMenuAnchorEl(null);
          }}
          disabled={isDownloading}
          sx={{
            py: 1,
            px: 2,
            minHeight: 'auto',
            fontSize: '13px'
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <FilePdf size={18} weight="light" />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
          >
            {isDownloading ? 'Downloading...' : 'Transcript'}
          </ListItemText>
        </MenuItem>

        {/* Settings Section (moved to bottom) */}
        <MenuItem
          disabled
          sx={{
            opacity: 0.7,
            fontWeight: 600,
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'text.secondary',
            py: 0.5,
            px: 2,
            minHeight: 'auto'
          }}
        >
          Settings
        </MenuItem>

        {/* Rename Menu Item */}
        <MenuItem
          onClick={handleOpenRenameDialog}
          sx={{
            py: 1,
            px: 2,
            minHeight: 'auto',
            fontSize: '13px'
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
          >
            Rename
          </ListItemText>
        </MenuItem>

        {/* Move to Folder Menu Item - Show on desktop, mobile has its own drawer */}
        {!isMobile && (
          <MenuItem
            onClick={handleOpenMoveToCollectionDialog}
            sx={{
              py: 1,
              px: 2,
              minHeight: 'auto',
              fontSize: '13px'
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <MoveOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
            >
              Move to Folder
            </ListItemText>
          </MenuItem>
        )}

        {/* Requires Approval Toggle - Temporarily hidden until feature is re-enabled */}
        {/* <MenuItem
          onClick={(e) => {
            e.preventDefault();
            handleToggleRequiresApproval();
          }}
          sx={{
            py: 1,
            px: 2,
            minHeight: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'text.primary'
            }}
          >
            Requires Approval
          </Typography>
          <Switch
            checked={requiresApproval}
            onChange={handleToggleRequiresApproval}
            size="small"
            onClick={(e) => e.stopPropagation()}
          />
        </MenuItem> */}

        <Divider sx={{ my: 1 }} />

        {/* Delete Session */}
        <MenuItem
          onClick={handleOpenDeleteDialog}
          sx={{
            py: 1,
            px: 2,
            minHeight: 'auto',
            fontSize: '13px',
            color: theme.palette.error.main
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <DeleteOutlinedIcon
              sx={{ fontSize: 18, color: theme.palette.error.main }}
            />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{
              fontSize: '13px',
              fontWeight: 500,
              color: theme.palette.error.main
            }}
          >
            Delete Session
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Share Menu */}
      <Menu
        anchorEl={shareAnchorEl}
        open={Boolean(shareAnchorEl)}
        onClose={() => setShareAnchorEl(null)}
        PaperProps={{ sx: { minWidth: 280 } }}
      >
        <MenuItem disabled>
          <ListItemText>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Share with Distribution Groups
            </Typography>
          </ListItemText>
        </MenuItem>

        {isLoadingDistributionGroups ? (
          <MenuItem disabled>
            <ListItemIcon>
              <CircularProgress size={16} />
            </ListItemIcon>
            <ListItemText>Loading distribution groups...</ListItemText>
          </MenuItem>
        ) : availableDistributionGroups.length === 0 ? (
          [
            <MenuItem disabled key="no-groups">
              <ListItemText>
                <Typography variant="body2" color="text.secondary">
                  No distribution groups configured
                </Typography>
              </ListItemText>
            </MenuItem>,
            <MenuItem disabled key="configure-info">
              <ListItemText>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  Configure distribution groups in Organization settings
                </Typography>
              </ListItemText>
            </MenuItem>
          ]
        ) : (
          availableDistributionGroups.map((group) => (
            <MenuItem
              key={group.id}
              onClick={async () => {
                if (!sessionId) return;

                setIsSharing(true);
                try {
                  const result = await shareSummaries(
                    {
                      sessionId,
                      summaryIds: [], // TODO: Get actual generated summary IDs
                      distributionGroupIds: [group.id],
                      message: `Summaries from session ${sessionId}`
                    },
                    getAccessTokenSilently,
                    getAccessTokenWithPopup
                  );

                  // Show success/error message
                  if (result.success) {
                    // TODO: Show success toast/snackbar
                    console.log('Share successful:', result.message);
                  } else {
                    // TODO: Show error toast/snackbar
                    console.error('Share failed:', result.message);
                  }
                } catch (error) {
                  console.error('Error sharing summaries:', error);
                  // TODO: Show error toast/snackbar
                } finally {
                  setIsSharing(false);
                }
                setShareAnchorEl(null);
              }}
              disabled={isSharing}
            >
              <ListItemIcon>
                {isSharing ? (
                  <CircularProgress size={16} />
                ) : (
                  <PeopleIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2">{group.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.recipientCount} recipients •{' '}
                  {group.associatedSummaries.join(', ')}
                </Typography>
              </ListItemText>
            </MenuItem>
          ))
        )}
      </Menu>

      {/* View Summary Dialog - Perfectly Centered */}
      <Dialog
        open={summaryPopoverOpen}
        onClose={handleSummaryPopoverClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '80vw',
            maxWidth: 900,
            height: '70vh',
            maxHeight: 600,
            overflow: 'hidden',
            borderRadius: '12px',
            boxShadow:
              '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            m: 0
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              Session Summaries
            </Typography>
            <IconButton onClick={handleSummaryPopoverClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Tab Header */}
          {sessionData?.aiInsights && sessionData.aiInsights.length > 0 ? (
            <>
              <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', px: 3 }}>
                  {sessionData.aiInsights.map((insight, index) => (
                    <Button
                      key={insight.id || index}
                      onClick={() => setSelectedSummaryTab(index)}
                      sx={{
                        minHeight: 48,
                        px: 2,
                        py: 1.5,
                        borderRadius: 0,
                        borderBottom:
                          selectedSummaryTab === index
                            ? '2px solid #2563eb'
                            : '2px solid transparent',
                        backgroundColor: 'transparent',
                        color:
                          selectedSummaryTab === index ? '#2563eb' : '#6b7280',
                        fontWeight: selectedSummaryTab === index ? 600 : 500,
                        textTransform: 'none',
                        fontSize: '14px',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color:
                            selectedSummaryTab === index ? '#2563eb' : '#374151'
                        }
                      }}
                    >
                      {insight.type || 'General Summary'}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Content Area */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {sessionData.aiInsights[selectedSummaryTab] && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        mb: 2.5,
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px'
                      }}
                    >
                      {sessionData.aiInsights[selectedSummaryTab].type ||
                        'General Summary'}
                    </Typography>

                    <Box
                      sx={{
                        p: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : '#fafafa',
                        maxHeight: '500px',
                        overflow: 'auto',
                        fontSize: '13px',
                        color: theme.palette.text.primary,
                        lineHeight: 1.6,
                        textAlign: 'left',
                        '& p': {
                          margin: '0 0 1.25em 0',
                          '&:last-child': {
                            marginBottom: 0
                          }
                        },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          marginTop: '2em',
                          marginBottom: '0.75em',
                          fontWeight: 600,
                          lineHeight: 1.4,
                          color: theme.palette.text.primary,
                          '&:first-of-type': {
                            marginTop: 0
                          }
                        },
                        '& h1': {
                          fontSize: '15px',
                          fontWeight: 600
                        },
                        '& h2': {
                          fontSize: '14px',
                          fontWeight: 600
                        },
                        '& h3': {
                          fontSize: '13px',
                          fontWeight: 600
                        },
                        '& ul, & ol': {
                          margin: '0.75em 0',
                          paddingLeft: '1.5em'
                        },
                        '& li': {
                          margin: '0.5em 0',
                          lineHeight: 1.6
                        },
                        '& strong': {
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        },
                        '& code': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)',
                          padding: '0.15em 0.4em',
                          borderRadius: '4px',
                          fontSize: '0.92em',
                          fontFamily: 'ui-monospace, monospace'
                        },
                        '& pre': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                          padding: '1em',
                          borderRadius: '8px',
                          overflow: 'auto',
                          margin: '1.25em 0',
                          border: `1px solid ${theme.palette.divider}`
                        },
                        '& pre code': {
                          backgroundColor: 'transparent',
                          padding: 0,
                          fontSize: '12px'
                        },
                        '& blockquote': {
                          borderLeft: `2px solid ${theme.palette.divider}`,
                          paddingLeft: '1em',
                          margin: '1.25em 0',
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic'
                        },
                        '& table': {
                          borderCollapse: 'collapse',
                          width: '100%',
                          margin: '1.25em 0',
                          fontSize: '13px'
                        },
                        '& th, & td': {
                          border: `1px solid ${theme.palette.divider}`,
                          padding: '0.5em 0.75em',
                          textAlign: 'left'
                        },
                        '& th': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.03)'
                              : 'rgba(0, 0, 0, 0.02)',
                          fontWeight: 600,
                          fontSize: '12px'
                        },
                        '& a': {
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {sessionData.aiInsights[selectedSummaryTab]
                          .description ||
                          'No content available for this summary.'}
                      </ReactMarkdown>

                      {sessionData.aiInsights[selectedSummaryTab].createdAt && (
                        <Typography
                          sx={{
                            fontSize: '11px',
                            color: theme.palette.text.secondary,
                            mt: 2.5,
                            display: 'block',
                            borderTop: `1px solid ${theme.palette.divider}`,
                            pt: 2,
                            fontStyle: 'italic'
                          }}
                        >
                          Generated:{' '}
                          {new Date(
                            sessionData.aiInsights[selectedSummaryTab].createdAt
                          ).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    textAlign: 'center',
                    fontSize: '14px',
                    maxWidth: '300px',
                    mx: 'auto'
                  }}
                >
                  Complete your recording and analysis to generate summaries
                  that will appear here
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Recording FAB - Show on mobile, tablet portrait, and stacked content layouts */}
      {canShowFabControls() && isSessionOwner && (
        <RecordingFAB
          isRecording={state.isRecording}
          isPaused={state.isPaused}
          duration={recordingDuration}
          hasStartedRecording={hasStartedRecording}
          isStartingRecording={isStartingRecording}
          onStartRecording={startConversation}
          onPause={pauseRecording}
          onResume={resumeRecording}
          onGenerateSummary={async () => {
            await stopConversation();
          }}
        />
      )}

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            px: 3,
            pt: 3,
            pb: 0
          }}
        >
          Rename Session
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}
          >
            <TextField
              autoFocus
              label="Session Name"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                }
              }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
            gap: 1.5
          }}
        >
          <Button
            onClick={() => setRenameDialogOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            variant="contained"
            disabled={!renameValue.trim()}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#ffffff' : '#1A1A1A',
              color: theme.palette.mode === 'dark' ? '#1A1A1A' : '#ffffff',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#e5e5e5' : '#000000'
              },
              '&:disabled': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.12)',
                color:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.26)'
                    : 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            px: 3,
            pt: 3,
            pb: 0
          }}
        >
          Delete Session
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Typography>
            Are you sure you want to delete this session? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
            gap: 1.5
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSession}
            variant="contained"
            disabled={isDeletingSession}
            startIcon={
              isDeletingSession ? (
                <CircularProgress size={16} sx={{ color: '#ffffff' }} />
              ) : null
            }
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              backgroundColor: theme.palette.error.main,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: theme.palette.error.dark
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.error.main,
                color: '#ffffff',
                opacity: 0.7
              }
            }}
          >
            {isDeletingSession ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move to Folder Dialog */}
      <Dialog
        open={moveToCollectionDialogOpen}
        onClose={() => setMoveToCollectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            px: 3,
            pt: 3,
            pb: 0
          }}
        >
          Move to Folder
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Box sx={{ pt: 1 }}>
            <Select
              fullWidth
              value={selectedCollectionId}
              onChange={(e) => setSelectedCollectionId(e.target.value)}
              displayEmpty
              sx={{
                mt: 1,
                '& .MuiSelect-select': {
                  py: 1.5
                }
              }}
            >
              <MenuItem value="">
                <em>No Folder</em>
              </MenuItem>
              {availableCollections.map((collection) => (
                <MenuItem key={collection.id} value={collection.id}>
                  {collection.title}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
            gap: 1.5
          }}
        >
          <Button
            onClick={() => setMoveToCollectionDialogOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMoveToCollection}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#ffffff' : '#1A1A1A',
              color: theme.palette.mode === 'dark' ? '#1A1A1A' : '#ffffff',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#e5e5e5' : '#000000'
              }
            }}
          >
            Move
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Collection Drawer */}
      {isMobile && effectiveSessionId && (
        <MobileCollectionDrawer
          open={mobileCollectionDrawerOpen}
          onClose={() => setMobileCollectionDrawerOpen(false)}
          sessionId={effectiveSessionId}
          currentCollections={sessionCollections}
          onUpdate={reloadSessionCollections}
        />
      )}

      {/* Keyframe animations */}
      <style>{` 
        @keyframes pulse { 
          0% { transform: scale(1); opacity: 1; } 
          50% { transform: scale(1.1); opacity: 0.7; } 
          100% { transform: scale(1); opacity: 1; } 
        } 
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {/* AssemblyAITranscribe not rendered; Deepgram is the only live provider */}
    </Box>
  );
};

export default NewTranscriptPage;
